import { ChangeEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDays, differenceInCalendarDays, format, parseISO } from 'date-fns';
import { AlertTriangle, Copy, Download, ExternalLink, Plus, ReceiptText, ShieldAlert, WalletCards } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useApp } from '@/contexts/AppContext';
import AppDateField from '@/components/AppDateField';
import SectionAccordionCard from '@/components/SectionAccordionCard';
import { supabase } from '@/integrations/supabase/client';
import { applyBenefitsLifeEvent, calculateBenefits, type BenefitsInput, type BenefitsLifeEvent, type EmploymentStatus, type ExtendedBenefitResult } from '@/lib/benefits-calculator';
import { getHouseholdSharePeerName } from '@/lib/household-share';
import { completeBankConnection, fetchStoredBankTransactions, isOpenBankingEnabled } from '@/lib/open-banking';
import BenefitsForm from '@/components/benefits/BenefitsForm';
import BenefitCard from '@/components/benefits/BenefitCard';
import LifeEventsChips from '@/components/benefits/LifeEventsChips';
import MoneyJourneyCard from '@/components/money/MoneyJourneyCard';
import SectionShortcutsLauncher from '@/components/SectionShortcutsLauncher';
import SectionNudge from '@/components/SectionNudge';
import SectionGuideRail from '@/components/playbooks/SectionGuideRail';
import {
  loadIntegrationSettings,
  saveIntegrationSettings,
  type IntegrationSettingsRecord,
} from '@/lib/integration-settings';
import type { BudgetCategory, Expense, ExpenseRecurringPeriod, UserProfile } from '@/types';
import { assessBudgetPressure, assessDebtPressure, assessSurvivalActivation, buildCategorisedStatementCsv, calculateBalanceTransferEstimate, calculateDebtModel, compareDebtStrategies, getDebtRateComparison, getDefaultRecurringReminderDays, getNextRecurringExpenseDate, getRecurringPaymentStatus, parseStatementCsv, parseStatementPdfText, summariseStatementTransactions, type RecurringPaymentStatus, type StatementImportSummary, type StatementTransaction, type SurvivalActivationAssessment } from '@/lib/money-tools';
import { trackProductEvent } from '@/lib/product-analytics';
import {
  BANK_BREAKDOWN_CATEGORY_OPTIONS,
  buildFinanceLedgerBankConnectionSummary,
  buildFinanceLedgerStatementImportReviewMessage,
  buildBankFeedAnalysis,
  buildBankFinanceAnalysisViews,
  buildBankFeedMerchantReviewCandidates,
  buildBankFeedBudgetSignals,
  buildBankCreditorSummaryText,
  buildFinanceReviewInboxSummary,
  buildBankMortgagePrepSummaryText,
  buildMoneyBudgetBenchmarkRows,
  buildBudgetCategoryCollections,
  buildBudgetCategoryOptions,
  DEFAULT_BUDGET_CATEGORIES,
  describeBankBudgetReviewReason,
  describeBankFeedMerchantReviewReason,
  formatBankBudgetCategoryLabel,
  mapBudgetCategoryToBankBreakdownCategoryId,
  mapBreakdownCategoryIdToLegacyCategory,
  mergeSuggestedBudgetLimits,
  normaliseMoneyMerchant,
  normaliseBudgetCategoryLabel,
  resolveSavedStatementImportLedgerSource,
  shouldAutoRefreshConnectedBankBudget,
  getFinanceBankAccountRoleLabel,
  isFinanceBankAccountBusiness,
  shouldIncludeFinanceBankAccountInMoneyView,
  type BankBudgetCategoryId,
  type BankBudgetReviewItem,
  type BankFeedRecurringCommitment,
  type BankFeedCategoryOverride,
  type BankNormalisedTransaction,
  type BankCategoryRule,
  type BankCategoryRuleScope,
  type FinanceBankAccountRole,
} from '../../ui-foundation/features/money';
import {
  AMAZON_CATEGORY_TARGETS,
  buildAmazonOrderRecord,
  buildAmazonOrderSummary,
  mergeBankFeedInputsWithAmazonOrders,
  parseAmazonOrderText,
  reconcileAmazonOrdersToBankTransactions,
  toAmazonCategoryRuleInput,
  type AmazonCategoryRuleScope,
  type AmazonCategoryRule,
  type AmazonOrderRecord,
} from '../../ui-foundation/features/money';
import {
  applySharedHomeBillInferences,
  AppButton,
  AppButtonRow,
  AppCard,
  AppDisclosureGlyph,
  AppEmptyState,
  AppInlineAlert,
  AppPage,
  AppSectionHeader,
  AppTabBar,
  AppTabButton,
  AppText,
  buildSharedHomeBillDefaults,
  buildSharedHomeBillInferences,
  buildSharedHomeBillTransferCandidates,
  buildMoneyBenefitsScripts,
  buildMoneyDebtScripts,
  buildMoneySurvivalPacks,
  formatSharedHomeBillAmount,
  formatSharedHomeBillSchedule,
  getSharedHomeBillLabel,
  getSharedHomeBillMonthlyAmount,
  mergeSharedHomeBillRecords,
  normalizeVehicleRegistration,
  type SharedHomeVehicleRecord,
  type SharedVehicleInsuranceRenewal,
  type SharedHomeBillKey,
  type SharedHomeBillRecord,
} from '../../ui-foundation';
import {
  buildPetSpendSummary,
  filterPetRecurringOutgoings,
  hasTrackedPets,
} from '../../ui-foundation/features/pets';
import { buildHouseholdSharingSummary } from '../../ui-foundation/features/family';
import {
  AppDialog as Dialog,
  AppDialogContent as DialogContent,
  AppDialogDescription as DialogDescription,
  AppDialogHeader as DialogHeader,
  AppDialogTitle as DialogTitle,
  AppDialogTrigger as DialogTrigger,
  AppProgress as Progress,
  AppSelect as Select,
  AppSelectContent as SelectContent,
  AppSelectItem as SelectItem,
  AppSelectTrigger as SelectTrigger,
  AppSelectValue as SelectValue,
  AppWebFileInput as FileInput,
  AppWebButton as Button,
  AppWebInput as Input,
  AppWebTextarea as Textarea,
} from '../../ui-foundation/adapters/web';
import { repairStoredHomeVehicle } from '@/lib/homeVehicles';

const TABS = [
  { key: 'budget', label: 'Budget' },
  { key: 'debts', label: 'Debts' },
  { key: 'benefits', label: 'Benefits' },
  { key: 'survival', label: 'Survival' },
] as const;

const RECURRING_EXPENSE_OPTIONS: Array<{ value: ExpenseRecurringPeriod; label: string }> = [
  { value: 'none', label: 'One-off' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

type TabKey = typeof TABS[number]['key'];

type StatementImportRow = {
  id: string;
  file_name: string;
  period_label: string | null;
  transactions: StatementTransaction[];
  summary: StatementImportSummary;
  expenses_synced_at: string | null;
  created_at: string;
};

type BankAccountRow = {
  id: string;
  institution_name: string;
  name: string | null;
  account_type: string | null;
  account_role: string | null;
  iban: string | null;
  currency: string | null;
  balance: string | null;
  last_synced: string;
};

type BankTransactionRow = {
  id: string;
  account_id: string;
  booking_date: string;
  amount: number;
  currency: string;
  description: string | null;
  creditor_name: string | null;
  debtor_name: string | null;
};

type HomeProfileMoneyContext = {
  address: string | null;
  tenure: string | null;
  monthly_rent: number | null;
  landlord_name: string | null;
  council_tax_band: string | null;
  mortgage_provider: string | null;
  ground_rent_amount: number | null;
  service_charge_amount: number | null;
  has_vehicle: boolean | null;
  vehicle_registration_number: string | null;
  vehicle_tax_due_date: string | null;
  vehicle_mot_due_date: string | null;
  utility_gas_provider: string | null;
  utility_electric_provider: string | null;
  utility_broadband_provider: string | null;
  utility_water_provider: string | null;
  utility_phone_provider: string | null;
};

type HomeVehicleRow = {
  id: string;
  registration_number: string;
  make: string | null;
  mot_due_date: string | null;
  mot_status: string | null;
  tax_due_date: string | null;
  tax_status: string | null;
  tax_annual_cost: number | null;
  tax_six_month_cost: number | null;
  tax_monthly_direct_debit_total: number | null;
  tax_monthly_direct_debit_installment: number | null;
  mot_estimated_cost: number | null;
  service_last_date: string | null;
  service_due_date: string | null;
  dvla_synced_at: string | null;
};

type VehicleInsuranceRenewalRow = {
  type: string;
  renewal_date: string;
  registration_number: string | null;
  vehicle_label: string | null;
  provider: string | null;
};

const GBP_FORMATTER = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function formatCurrency(value: number | null | undefined) {
  return value == null ? null : GBP_FORMATTER.format(value);
}

type HomeBillRow = {
  bill_key: SharedHomeBillKey;
  enabled: boolean;
  frequency: SharedHomeBillRecord['frequency'];
  amount: number | null;
  due_day_of_week: SharedHomeBillRecord['dueDayOfWeek'];
  due_day_of_month: number | null;
  annual_due_date: string | null;
  provider_name: string | null;
  provider_account: string | null;
};

type BankCategoryOverrideRow = {
  merchant_key: string;
  merchant_label: string | null;
  category: BankFeedCategoryOverride['category'];
};

type BankCategoryRuleRow = {
  id: string;
  scope: BankCategoryRuleScope;
  transaction_id: string | null;
  merchant_key: string | null;
  merchant_label: string | null;
  pattern_key: string | null;
  category_id: BankBudgetCategoryId;
};

type AmazonOrderRow = {
  id: string;
  order_number: string;
  order_date: string;
  order_total: number;
  currency: string;
  source: string;
  source_subject: string | null;
  source_sender: string | null;
  source_excerpt: string | null;
  subscribe_and_save: boolean;
  matched_bank_transaction_id: string | null;
  match_confidence: 'high' | 'medium' | 'low';
  match_reason: string | null;
};

type AmazonOrderItemRow = {
  id: string;
  amazon_order_id: string;
  item_title: string;
  item_brand_key: string | null;
  quantity: number;
  line_total: number;
  budget_category: string | null;
  category_confidence: 'high' | 'medium' | 'low';
  needs_review: boolean;
  review_reason: string | null;
  is_subscribe_and_save: boolean;
};

type AmazonReviewItem = {
  orderNumber: string;
  orderId: string | null;
  itemId: string | null;
  title: string;
  brandKey: string | null;
  lineTotal: number;
  reviewReason: string | null;
  isSubscribeAndSave: boolean;
  currentCategory: BudgetCategory | null;
};

type AmazonCategoryRuleRow = {
  id: string;
  scope: 'item' | 'brand' | 'keyword';
  item_title: string | null;
  brand_key: string | null;
  keyword: string | null;
  budget_category: string;
};

type BankViewMode = 'mine' | 'household';

type HouseholdBankAccountRow = {
  account_id: string;
  owner_user_id: string;
  owner_label: string;
  institution_name: string | null;
  account_name: string | null;
  account_type: string | null;
  account_role: string | null;
  iban: string | null;
  currency: string | null;
  balance: string | null;
  last_synced: string | null;
};

function buildMoneyHomeBillDefaults(profile: HomeProfileMoneyContext | null) {
  return buildSharedHomeBillDefaults({
    tenure: profile?.tenure || null,
    monthlyHousingCost: profile?.monthly_rent ?? null,
    mortgageProvider: profile?.mortgage_provider || null,
    landlordName: profile?.landlord_name || null,
    councilTaxBand: profile?.council_tax_band || null,
    groundRentAmount: profile?.ground_rent_amount ?? null,
    serviceChargeAmount: profile?.service_charge_amount ?? null,
    hasVehicle: profile?.has_vehicle ?? null,
    vehicleRegistrationNumber: profile?.vehicle_registration_number || null,
    vehicleTaxDueDate: profile?.vehicle_tax_due_date || null,
    vehicleMotDueDate: profile?.vehicle_mot_due_date || null,
    utilityGasProvider: profile?.utility_gas_provider || null,
    utilityElectricProvider: profile?.utility_electric_provider || null,
    utilityBroadbandProvider: profile?.utility_broadband_provider || null,
    utilityWaterProvider: profile?.utility_water_provider || null,
    utilityPhoneProvider: profile?.utility_phone_provider || null,
  });
}

function describeNormalisationKind(kind: BankNormalisedTransaction['normalisationKind']) {
  if (kind === 'salary') return 'Treated as salary income.';
  if (kind === 'benefits') return 'Treated as benefits income.';
  if (kind === 'maintenance') return 'Treated as maintenance income.';
  if (kind === 'income') return 'Treated as other incoming money, but kept ready for review.';
  if (kind === 'incoming-transfer') return 'Treated as a regular incoming transfer.';
  if (kind === 'internal-transfer') return 'Treated as money moving between your own accounts.';
  if (kind === 'savings-transfer') return 'Treated as a savings transfer.';
  if (kind === 'credit-card-repayment') return 'Treated as a credit-card repayment rather than ordinary spend.';
  if (kind === 'refund') return 'Treated as a refund or reversal.';
  if (kind === 'cash-withdrawal') return 'Treated as a cash withdrawal and kept ready for review.';
  if (kind === 'loan-drawdown') return 'Treated as borrowed money arriving, not household income.';
  if (kind === 'bank-fee') return 'Treated as a bank fee.';
  if (kind === 'bank-interest') return 'Treated as bank interest.';
  return 'Treated as ordinary spend.';
}

type BankCorrectionTarget = {
  key: string;
  transactionId: string | null;
  merchantKey: string;
  merchantLabel: string;
  patternKey: string;
  rawLabel: string;
  bookingDate: string;
  amount: number;
  currency: string;
  suggestedCategoryId: BankBudgetCategoryId | null;
};

type HouseholdBankTransactionRow = {
  transaction_id: string;
  owner_user_id: string;
  owner_label: string;
  account_id: string;
  booking_date: string;
  amount: number;
  currency: string | null;
  description: string | null;
  creditor_name: string | null;
  debtor_name: string | null;
};

type BankAccountSummary = {
  id: string;
  owner_label: string;
  institution_name: string | null;
  name: string | null;
  account_role: FinanceBankAccountRole | null;
  iban: string | null;
  currency: string | null;
  balance: number | null;
  last_synced: string | null;
};

type BankTransactionSummary = {
  id: string;
  owner_label: string;
  account_id: string;
  booking_date: string;
  amount: number;
  currency: string | null;
  description: string | null;
  creditor_name: string | null;
  debtor_name: string | null;
};

type DebtRow = {
  id: string;
  creditor_name: string;
  debt_type: string;
  balance: number;
  apr: number | null;
  minimum_payment: number | null;
  payment_status: 'current' | 'struggling' | 'missed';
  notes: string | null;
};

type LocalSupportLookupResult = {
  postcode: string;
  authorityName: string;
  councilUrl: string;
  authorityTier: string | null;
  authoritySlug: string | null;
  councilSearchUrl: string;
  foodBankUrl: string;
};

const DEFAULT_TAB: TabKey = 'budget';
const MOBILE_BANK_CALLBACK_STATE_PREFIX = 'mobile-';

function resolveBudgetCategory(primary: string, custom: string) {
  return normaliseBudgetCategoryLabel(custom) || normaliseBudgetCategoryLabel(primary);
}
const MOBILE_BANK_CALLBACK_BASE_URL = 'adulting:///money/open-banking-callback';

function getStatementInsights(summary: StatementImportSummary, transactions: StatementTransaction[]) {
  const fallback = summariseStatementTransactions(transactions);
  return {
    confidenceCounts: summary.confidenceCounts || fallback.confidenceCounts,
    recurringOutgoings: summary.recurringOutgoings?.length ? summary.recurringOutgoings : fallback.recurringOutgoings,
    suggestedBudget: summary.suggestedBudget || fallback.suggestedBudget,
  };
}

function downloadCsvFile(fileName: string, csvText: string) {
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
  const downloadUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(downloadUrl);
}

type PdfTextItem = {
  str?: string;
  transform?: number[];
};

function buildPdfPageLines(items: PdfTextItem[]) {
  const rows = new Map<number, PdfTextItem[]>();

  for (const item of items) {
    const text = item.str?.trim();
    const transform = item.transform;
    if (!text || !transform || transform.length < 6) continue;

    const yKey = Math.round(transform[5]);
    const current = rows.get(yKey) || [];
    current.push(item);
    rows.set(yKey, current);
  }

  return [...rows.entries()]
    .sort((left, right) => right[0] - left[0])
    .map(([, rowItems]) => rowItems
      .sort((left, right) => (left.transform?.[4] || 0) - (right.transform?.[4] || 0))
      .map((item) => item.str?.trim() || '')
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim())
    .filter(Boolean);
}

async function extractTextFromPdfFile(file: File) {
  const [{ getDocument, GlobalWorkerOptions }, workerModule] = await Promise.all([
    import('pdfjs-dist/legacy/build/pdf.mjs'),
    import('pdfjs-dist/legacy/build/pdf.worker.mjs?url'),
  ]);

  GlobalWorkerOptions.workerSrc = workerModule.default;
  const data = new Uint8Array(await file.arrayBuffer());
  const pdfDocument = await getDocument({ data }).promise;
  const pageTexts: string[] = [];

  for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
    const page = await pdfDocument.getPage(pageNumber);
    const textContent = await page.getTextContent();
    pageTexts.push(buildPdfPageLines(textContent.items as PdfTextItem[]).join('\n'));
  }

  return pageTexts.join('\n');
}

function notifyDebtDataChanged() {
  window.dispatchEvent(new CustomEvent('adulting:debts-changed'));
}

function resolveMoneyTab(requestedTab: string | null): TabKey {
  if (requestedTab === 'bank' || requestedTab === 'statements') return 'budget';
  const matchingTab = TABS.find((tab) => tab.key === requestedTab);
  return matchingTab?.key || DEFAULT_TAB;
}

function BudgetAccordionSection({
  emoji,
  title,
  subtitle,
  open,
  onToggle,
  children,
}: {
  emoji: string;
  title: string;
  subtitle: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <SectionAccordionCard emoji={emoji} title={title} subtitle={subtitle} open={open} onToggle={onToggle}>
      <div className="space-y-6">{children}</div>
    </SectionAccordionCard>
  );
}

function formatSterlingAmount(value: number, currency = 'GBP') {
  if (!Number.isFinite(value)) {
    return 'Balance unavailable';
  }

  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value);
}

function recurringPeriodLabel(period: ExpenseRecurringPeriod) {
  if (period === 'monthly') return 'Monthly';
  if (period === 'yearly') return 'Yearly';
  return 'One-off';
}

function getRecurringDueLabel(nextDue: string | null) {
  if (!nextDue) return 'Next due date unavailable';

  const nextDueDate = parseISO(nextDue);
  const daysUntilDue = differenceInCalendarDays(nextDueDate, new Date());
  if (daysUntilDue < 0) {
    return `${Math.abs(daysUntilDue)} day${Math.abs(daysUntilDue) === 1 ? '' : 's'} overdue`;
  }

  if (daysUntilDue === 0) {
    return 'Due today';
  }

  if (daysUntilDue === 1) {
    return 'Due tomorrow';
  }

  if (daysUntilDue <= 14) {
    return `Due in ${daysUntilDue} days`;
  }

  return `Next due ${format(nextDueDate, 'd MMM')}`;
}

function getRecurringRenewalLabel(nextDue: string | null) {
  if (!nextDue) return 'Renewal date unavailable';
  return `Renews ${format(parseISO(nextDue), 'd MMM yyyy')}`;
}

function getRecurringCadenceLabel(cadence: BankFeedRecurringCommitment['cadence']) {
  if (cadence === 'four-weekly') return 'Four-weekly';
  if (cadence === 'annual') return 'Annual';
  return cadence.charAt(0).toUpperCase() + cadence.slice(1);
}

function getBankCommitmentRecurringPeriod(cadence: BankFeedRecurringCommitment['cadence']): ExpenseRecurringPeriod {
  return cadence === 'annual' ? 'yearly' : 'monthly';
}

function getBankCommitmentNextDueDate(latestBookingDate: string, cadence: BankFeedRecurringCommitment['cadence']) {
  const latestDate = parseISO(latestBookingDate);
  if (Number.isNaN(latestDate.getTime())) {
    return format(new Date(), 'yyyy-MM-dd');
  }

  const cadenceDays =
    cadence === 'weekly'
      ? 7
      : cadence === 'four-weekly'
        ? 28
        : cadence === 'monthly'
          ? 30
          : cadence === 'quarterly'
            ? 91
            : 365;

  return format(addDays(latestDate, cadenceDays), 'yyyy-MM-dd');
}

function buildMobileBankCallbackUrl(searchParams: URLSearchParams) {
  const callbackParams = new URLSearchParams();

  for (const key of ['code', 'state', 'error', 'error_description', 'scope']) {
    const value = searchParams.get(key);
    if (value) {
      callbackParams.set(key, value);
    }
  }

  const query = callbackParams.toString();
  return query ? `${MOBILE_BANK_CALLBACK_BASE_URL}?${query}` : MOBILE_BANK_CALLBACK_BASE_URL;
}

function getRecurringStatusMeta(status: RecurringPaymentStatus) {
  if (status === 'missed') {
    return {
      label: 'Missed',
      classes: 'bg-red-100 text-red-800',
    };
  }

  if (status === 'due-soon') {
    return {
      label: 'Due soon',
      classes: 'bg-amber-100 text-amber-800',
    };
  }

  if (status === 'upcoming') {
    return {
      label: 'Upcoming',
      classes: 'bg-green-100 text-green-800',
    };
  }

  return {
    label: 'Check due date',
    classes: 'bg-muted text-muted-foreground',
  };
}

function getDebtRateStatusMeta(status: 'below-average' | 'around-average' | 'above-average' | 'well-above-average') {
  if (status === 'well-above-average') {
    return {
      label: 'Well above average',
      classes: 'bg-red-100 text-red-800',
    };
  }

  if (status === 'above-average') {
    return {
      label: 'Above average',
      classes: 'bg-amber-100 text-amber-800',
    };
  }

  if (status === 'below-average') {
    return {
      label: 'Below average',
      classes: 'bg-green-100 text-green-800',
    };
  }

  return {
    label: 'Near average',
    classes: 'bg-muted text-muted-foreground',
  };
}

function getSurvivalActivationMeta(level: 'watch' | 'high' | 'critical') {
  if (level === 'critical') {
    return {
      classes: 'border-red-200 bg-red-50',
      badgeClasses: 'bg-red-100 text-red-800',
      buttonClasses: 'bg-red-600 text-white hover:bg-red-700',
      badgeLabel: 'Act now',
    };
  }

  if (level === 'high') {
    return {
      classes: 'border-amber-200 bg-amber-50',
      badgeClasses: 'bg-amber-100 text-amber-900',
      buttonClasses: 'bg-amber-600 text-white hover:bg-amber-700',
      badgeLabel: 'Use soon',
    };
  }

  return {
    classes: 'border-border bg-muted/35',
    badgeClasses: 'bg-background text-muted-foreground',
    buttonClasses: 'bg-primary text-primary-foreground hover:bg-primary/90',
    badgeLabel: 'Keep nearby',
  };
}

function getLastPaidLabel(lastPaidAt?: string, lastPaidAmount?: number) {
  if (!lastPaidAt) return 'Not marked paid yet';

  const paidDate = parseISO(lastPaidAt);
  const dateLabel = Number.isNaN(paidDate.getTime()) ? lastPaidAt : format(paidDate, 'd MMM');
  if (typeof lastPaidAmount === 'number') {
    return `Last paid ${dateLabel} · ${formatSterlingAmount(lastPaidAmount)}`;
  }

  return `Last paid ${dateLabel}`;
}

function getDefaultBenefitsEmploymentStatus(profile: UserProfile): EmploymentStatus {
  if (profile.workType === 'mix') return 'mixed';
  if (profile.workType === 'self-employed') return 'self-employed';
  if (profile.workType === 'not-working') return 'not-working';
  if (profile.employmentType === 'self-employed') return 'self-employed';
  if (profile.employmentType === 'not-working') return 'not-working';
  return 'employed';
}

export default function MoneyPage() {
  const { user, data } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<TabKey>(() => resolveMoneyTab(requestedTab));
  const [survivalDebts, setSurvivalDebts] = useState<DebtRow[]>([]);

  useEffect(() => {
    if (requestedTab === 'bank' || requestedTab === 'statements') {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('tab', 'budget');
      setSearchParams(nextParams, { replace: true });
      return;
    }

    const nextTab = resolveMoneyTab(requestedTab);
    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  }, [activeTab, requestedTab, searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;

    const fetchSurvivalDebts = async () => {
      if (!user) {
        setSurvivalDebts([]);
        return;
      }

      const { data: debtRows, error } = await supabase
        .from('debts' as any)
        .select('*')
        .eq('user_id', user.id);

      if (cancelled) return;

      if (error) {
        console.error(error);
        setSurvivalDebts([]);
        return;
      }

      setSurvivalDebts((debtRows || []) as DebtRow[]);
    };

    void fetchSurvivalDebts();

    const handleDebtDataChanged = () => {
      void fetchSurvivalDebts();
    };
    window.addEventListener('adulting:debts-changed', handleDebtDataChanged);

    return () => {
      cancelled = true;
      window.removeEventListener('adulting:debts-changed', handleDebtDataChanged);
    };
  }, [user]);

  const currentMonth = format(new Date(), 'yyyy-MM');
  const loggedExpenses = useMemo(
    () => data.expenses.filter((expense) => !expense.isRecurring),
    [data.expenses],
  );
  const monthlyExpenses = useMemo(
    () => loggedExpenses.filter((expense) => expense.date.startsWith(currentMonth)),
    [currentMonth, loggedExpenses],
  );
  const totalSpent = useMemo(
    () => monthlyExpenses.reduce((sum, expense) => sum + expense.amount, 0),
    [monthlyExpenses],
  );
  const visibleBudgetCategories = useMemo(
    () =>
      buildBudgetCategoryCollections({
        budgetLimitCategories: data.budgetLimits.map((limit) => limit.category),
        expenseCategories: data.expenses.map((expense) => expense.category),
        preferences: data.budgetCategoryPreferences,
      }).visible,
    [data.budgetCategoryPreferences, data.budgetLimits, data.expenses],
  );
  const overBudgetCategories = useMemo(
    () =>
      data.budgetLimits.filter((limit) => {
        if (!visibleBudgetCategories.includes(limit.category)) {
          return false;
        }
        const spent = monthlyExpenses
          .filter((expense) => expense.category === limit.category)
          .reduce((sum, expense) => sum + expense.amount, 0);
        return spent > limit.limit;
      }),
    [data.budgetLimits, monthlyExpenses, visibleBudgetCategories],
  );
  const recurringExpenses = useMemo(
    () =>
      data.expenses
        .filter((expense) => expense.isRecurring && expense.recurringPeriod && expense.recurringPeriod !== 'none')
        .map((expense) => {
          const period = expense.recurringPeriod || 'none';
          const reminderDaysBefore = expense.reminderDaysBefore ?? getDefaultRecurringReminderDays(period);
          const nextDue = expense.nextDueDate || getNextRecurringExpenseDate(expense.date, period);
          const paymentState = getRecurringPaymentStatus(nextDue, reminderDaysBefore);
          return {
            ...expense,
            recurringPeriod: period,
            paymentStatus: paymentState.status,
            monthlyEquivalent: period === 'yearly' ? expense.amount / 12 : expense.amount,
          };
        }),
    [data.expenses],
  );
  const recurringMonthlyCommitment = useMemo(
    () => recurringExpenses.reduce((sum, expense) => sum + expense.monthlyEquivalent, 0),
    [recurringExpenses],
  );
  const recurringDueSoonCount = useMemo(
    () => recurringExpenses.filter((expense) => expense.paymentStatus === 'due-soon').length,
    [recurringExpenses],
  );
  const recurringMissedCount = useMemo(
    () => recurringExpenses.filter((expense) => expense.paymentStatus === 'missed').length,
    [recurringExpenses],
  );

  const budgetPressure = useMemo(
    () =>
      assessBudgetPressure({
        annualIncome: data.profile.annualIncome || 0,
        totalSpent,
        recurringMonthlyCommitment,
        overBudgetCategoryCount: overBudgetCategories.length,
        missedRecurringCount: recurringMissedCount,
        dueSoonRecurringCount: recurringDueSoonCount,
      }),
    [
      data.profile.annualIncome,
      overBudgetCategories.length,
      recurringDueSoonCount,
      recurringMissedCount,
      recurringMonthlyCommitment,
      totalSpent,
    ],
  );

  const debtPressure = useMemo(
    () =>
      assessDebtPressure(
        survivalDebts.map((debt) => ({
          debtType: debt.debt_type,
          balance: debt.balance,
          apr: debt.apr,
          minimumPayment: debt.minimum_payment,
          paymentStatus: debt.payment_status,
        })),
        data.profile.annualIncome || 0,
      ),
    [data.profile.annualIncome, survivalDebts],
  );

  const survivalActivation = useMemo(
    () =>
      assessSurvivalActivation({
        budgetPressure,
        debtPressure,
      }),
    [budgetPressure, debtPressure],
  );

  const handleTabChange = (tabKey: TabKey) => {
    setActiveTab(tabKey);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', tabKey);
    setSearchParams(nextParams, { replace: true });
  };

  const openMoneyShortcut = useCallback((compose: 'expense' | 'debt') => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', compose === 'expense' ? 'budget' : 'debts');
    nextParams.set('compose', compose);
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  const clearMoneyShortcut = useCallback(() => {
    if (!searchParams.get('compose')) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('compose');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <AppPage className="animate-fade-in">
      {/* MoneyJourneyCard keeps Money on the shared AppSectionHero grammar while adding journey actions. */}
      <MoneyJourneyCard
        action={(
          <SectionShortcutsLauncher
            title="Money shortcuts"
            subtitle="Open the next useful money action without leaving a bulky panel behind."
            items={[
              {
                id: 'expense',
                label: 'Add expense',
                description: 'Log spending straight into the live budget.',
                onPress: () => openMoneyShortcut('expense'),
              },
              {
                id: 'debt',
                label: 'Add debt',
                description: 'Capture a balance, APR, and minimum payment quickly.',
                onPress: () => openMoneyShortcut('debt'),
              },
            ]}
          />
        )}
      />

      <SectionNudge section="money" />

      <AppTabBar flexWrap="wrap">
        {TABS.map((tab) => (
          <AppTabButton
            key={tab.key}
            active={activeTab === tab.key}
            onPress={() => handleTabChange(tab.key)}
          >
            {tab.label}
          </AppTabButton>
        ))}
      </AppTabBar>

      {survivalActivation && activeTab !== 'survival' && (
        <AppCard>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-medium ${getSurvivalActivationMeta(survivalActivation.level).badgeClasses}`}>
                  {getSurvivalActivationMeta(survivalActivation.level).badgeLabel}
                </span>
                <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                  {survivalActivation.trigger === 'both'
                    ? 'Budget + debt signals'
                    : survivalActivation.trigger === 'budget'
                      ? 'Budget signal'
                      : 'Debt signal'}
                </span>
              </div>
              <AppText className="mt-2" fontSize="$sm" fontWeight="700">{survivalActivation.title}</AppText>
              <AppText className="mt-1" fontSize="$sm" color="$colorMuted">{survivalActivation.summary}</AppText>
            </div>
            <AppButton
              size="sm"
              tone={survivalActivation.level === 'critical' ? 'destructive' : 'primary'}
              onPress={() => handleTabChange('survival')}
            >
              Open Survival
            </AppButton>
          </div>
          {survivalActivation.reasons.length > 0 && (
            <ul className="mt-3 list-disc pl-5 space-y-1 text-xs text-muted-foreground">
              {survivalActivation.reasons.slice(0, 3).map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          )}
        </AppCard>
      )}

      {activeTab === 'budget' && (
        <BudgetTab
          launchExpenseShortcut={searchParams.get('compose') === 'expense'}
          onLaunchHandled={clearMoneyShortcut}
        />
      )}
      {activeTab === 'debts' && (
        <DebtsTab
          launchDebtShortcut={searchParams.get('compose') === 'debt'}
          onLaunchHandled={clearMoneyShortcut}
        />
      )}
      {activeTab === 'benefits' && <BenefitsTab />}
      {activeTab === 'survival' && <SurvivalTab activation={survivalActivation} />}
      <SectionGuideRail section="money" />
    </AppPage>
  );
}

function BudgetTab({
  launchExpenseShortcut = false,
  onLaunchHandled,
}: {
  launchExpenseShortcut?: boolean;
  onLaunchHandled?: () => void;
}) {
  const { data, user, addExpense, updateRecurringExpenseFlags, recordRecurringExpensePayment, updateBudgetLimits, addBudgetCategory, renameBudgetCategory, setBudgetCategoryHidden } = useApp();
  const navigate = useNavigate();
  const [hasPetProfiles, setHasPetProfiles] = useState(false);
  const [open, setOpen] = useState(false);
  const [pendingShortcutClear, setPendingShortcutClear] = useState(false);
  const [openBudgetSection, setOpenBudgetSection] = useState<string | undefined>(undefined);
  const toggleBudgetSection = useCallback((section: string) => {
    setOpenBudgetSection((current) => (current === section ? undefined : section));
  }, []);
  const [budgetEditorOpen, setBudgetEditorOpen] = useState(false);
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const categoryCollections = useMemo(
    () =>
      buildBudgetCategoryCollections({
        budgetLimitCategories: data.budgetLimits.map((item) => item.category),
        expenseCategories: data.expenses.map((item) => item.category),
        preferences: data.budgetCategoryPreferences,
      }),
    [data.budgetCategoryPreferences, data.budgetLimits, data.expenses],
  );
  const budgetCategories = categoryCollections.visible;
  const hiddenBudgetCategories = categoryCollections.hidden;
  const [category, setCategory] = useState<BudgetCategory>(DEFAULT_BUDGET_CATEGORIES[1]);
  const [budgetCategory, setBudgetCategory] = useState<BudgetCategory>(DEFAULT_BUDGET_CATEGORIES[1]);
  const [budgetCategoryCustom, setBudgetCategoryCustom] = useState('');
  const [budgetLimit, setBudgetLimit] = useState('');
  const [categoryCustom, setCategoryCustom] = useState('');
  const [categoryManagerValue, setCategoryManagerValue] = useState('');
  const [categoryRenameSource, setCategoryRenameSource] = useState<string | null>(null);
  const [categoryRenameValue, setCategoryRenameValue] = useState('');
  const [description, setDescription] = useState('');
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [expenseVisibility, setExpenseVisibility] = useState<'personal' | 'household'>('personal');
  const [recurringPeriod, setRecurringPeriod] = useState<ExpenseRecurringPeriod>('none');
  const [trackAsSubscription, setTrackAsSubscription] = useState(false);
  const [recurringDueDate, setRecurringDueDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [recurringReminderDays, setRecurringReminderDays] = useState(() => String(getDefaultRecurringReminderDays('monthly')));
  const [selectedRecurringExpenseId, setSelectedRecurringExpenseId] = useState<string | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);

  useEffect(() => {
    if (!launchExpenseShortcut) {
      return;
    }

    setOpenBudgetSection('live-budget');
    setOpen(true);
    setPendingShortcutClear(true);
  }, [launchExpenseShortcut]);

  useEffect(() => {
    if (!pendingShortcutClear || open) {
      return;
    }

    onLaunchHandled?.();
    setPendingShortcutClear(false);
  }, [onLaunchHandled, open, pendingShortcutClear]);

  const currentMonth = format(new Date(), 'yyyy-MM');
  const loggedExpenses = useMemo(
    () => data.expenses.filter((expense) => !expense.isRecurring),
    [data.expenses],
  );
  const monthlyExpenses = loggedExpenses.filter((expense) => expense.date.startsWith(currentMonth));
  const totalSpent = monthlyExpenses.reduce((sum, expense) => sum + expense.amount, 0);
  const monthlyIncome = data.profile.annualIncome / 12;
  const remaining = monthlyIncome - totalSpent;
  const visibleBudgetLimits = useMemo(
    () => data.budgetLimits.filter((limit) => budgetCategories.includes(limit.category)),
    [budgetCategories, data.budgetLimits],
  );
  const overBudgetCategories = visibleBudgetLimits.filter((limit) => {
    const spent = monthlyExpenses
      .filter((expense) => expense.category === limit.category)
      .reduce((sum, expense) => sum + expense.amount, 0);
    return spent > limit.limit;
  });
  const recurringExpenses = useMemo(
    () =>
      data.expenses
        .filter((expense) => expense.isRecurring && expense.recurringPeriod && expense.recurringPeriod !== 'none')
        .map((expense) => {
          const period = expense.recurringPeriod || 'none';
          const reminderDaysBefore = expense.reminderDaysBefore ?? getDefaultRecurringReminderDays(period);
          const nextDue = expense.nextDueDate || getNextRecurringExpenseDate(expense.date, period);
          const paymentState = getRecurringPaymentStatus(nextDue, reminderDaysBefore);
          return {
            ...expense,
            recurringPeriod: period,
            nextDue,
            reminderDaysBefore,
            paymentStatus: paymentState.status,
            daysUntilDue: paymentState.daysUntilDue,
            monthlyEquivalent: period === 'yearly' ? expense.amount / 12 : expense.amount,
            largerThanExpected: typeof expense.lastPaidAmount === 'number' && expense.lastPaidAmount > expense.amount + 0.009,
          };
        })
        .sort((left, right) => {
          if (left.nextDue && right.nextDue) return left.nextDue.localeCompare(right.nextDue);
          if (left.nextDue) return -1;
          if (right.nextDue) return 1;
          return (left.description || left.category).localeCompare(right.description || right.category);
        }),
    [data.expenses],
  );
  const recurringMonthlyCommitment = recurringExpenses.reduce((sum, expense) => sum + expense.monthlyEquivalent, 0);
  const recurringDueSoonCount = recurringExpenses.filter(
    (expense) => expense.paymentStatus === 'due-soon',
  ).length;
  const recurringMissedCount = recurringExpenses.filter((expense) => expense.paymentStatus === 'missed').length;
  const recurringLargerThanExpectedCount = recurringExpenses.filter((expense) => expense.largerThanExpected).length;
  const subscriptionExpenses = recurringExpenses.filter((expense) => expense.isSubscription);
  const otherRecurringExpenses = recurringExpenses.filter((expense) => !expense.isSubscription);
  const selectedRecurringExpense = recurringExpenses.find((expense) => expense.id === selectedRecurringExpenseId) || null;
  const [bankRecurringTrackerItems, setBankRecurringTrackerItems] = useState<BankFeedRecurringCommitment[]>([]);
  const [bankRecurringLoading, setBankRecurringLoading] = useState(false);
  const [bankRecurringLoadError, setBankRecurringLoadError] = useState<string | null>(null);
  const recurringTrackerMerchantKeys = useMemo(
    () =>
      new Set(
        recurringExpenses.map((expense) => normaliseMoneyMerchant(expense.description || expense.category)).filter(Boolean),
      ),
    [recurringExpenses],
  );

  useEffect(() => {
    let active = true;

    const loadBankRecurringCommitments = async () => {
      if (!user || !isOpenBankingEnabled()) {
        if (active) {
          setBankRecurringTrackerItems([]);
          setBankRecurringLoadError(null);
          setBankRecurringLoading(false);
        }
        return;
      }

      setBankRecurringLoading(true);
      setBankRecurringLoadError(null);

      try {
        const oneYearAgo = new Date(Date.now() - (365 * 24 * 60 * 60 * 1000)).toISOString().slice(0, 10);
        const [accountsResult, storedTransactions, overrideResult, ruleResult] = await Promise.all([
          supabase
            .from('bank_accounts' as any)
            .select('*')
            .eq('user_id', user.id),
          fetchStoredBankTransactions(user.id, { fromDate: oneYearAgo }),
          supabase
            .from('bank_category_overrides' as any)
            .select('merchant_key, merchant_label, category')
            .eq('user_id', user.id),
          supabase
            .from('bank_category_rules' as any)
            .select('id, scope, transaction_id, merchant_key, merchant_label, pattern_key, category_id')
            .eq('user_id', user.id),
        ]);

        if (accountsResult.error) throw accountsResult.error;

        if (!active) return;

        if (overrideResult.error) {
          console.error(overrideResult.error);
        }

        if (ruleResult.error) {
          console.error(ruleResult.error);
        }

        const visibleAccountIds = new Set(
          ((accountsResult.data || []) as BankAccountRow[])
            .filter((account) => shouldIncludeFinanceBankAccountInMoneyView(account.account_role))
            .map((account) => account.id),
        );
        const categoryOverrides = overrideResult.error
          ? []
          : ((overrideResult.data || []) as BankCategoryOverrideRow[]).map((row) => ({
            merchantKey: row.merchant_key,
            merchantLabel: row.merchant_label,
            category: row.category,
          }));
        const categoryRules = ruleResult.error
          ? []
          : ((ruleResult.data || []) as BankCategoryRuleRow[]).map((row) => ({
            id: row.id,
            scope: row.scope,
            transactionId: row.transaction_id,
            merchantKey: row.merchant_key,
            merchantLabel: row.merchant_label,
            patternKey: row.pattern_key,
            categoryId: row.category_id,
          }));
        const feedInputs = ((storedTransactions || []) as BankTransactionRow[])
          .filter((entry) => visibleAccountIds.has(entry.account_id))
          .map((entry) => ({
            transactionId: entry.id,
            accountId: entry.account_id,
            bookingDate: entry.booking_date,
            amount: Number(entry.amount || 0),
            currency: entry.currency || 'GBP',
            description: entry.description,
            merchantName: entry.creditor_name || entry.debtor_name,
          }));
        const budgetBreakdown = buildBankFeedAnalysis(feedInputs, {
          categoryRules,
          categoryOverrides,
        }).budgetBreakdown;

        setBankRecurringTrackerItems(
          budgetBreakdown.recurringCommitments.filter(
            (item) => !recurringTrackerMerchantKeys.has(normaliseMoneyMerchant(item.merchant)),
          ),
        );
      } catch (error) {
        console.error(error);
        if (active) {
          setBankRecurringTrackerItems([]);
          setBankRecurringLoadError('Connected-bank recurring payments could not be checked right now.');
        }
      } finally {
        if (active) {
          setBankRecurringLoading(false);
        }
      }
    };

    void loadBankRecurringCommitments();

    return () => {
      active = false;
    };
  }, [recurringTrackerMerchantKeys, user]);

  const householdPeerName = useMemo(
    () => getHouseholdSharePeerName(data.householdShare, user?.id || null) || data.profile.partnerName.trim() || 'your other adult',
    [data.householdShare, data.profile.partnerName, user?.id],
  );
  const householdSharingSummary = useMemo(
    () =>
      buildHouseholdSharingSummary({
        share: data.householdShare,
        currentUserId: user?.id || null,
        partnerName: data.profile.partnerName,
      }),
    [data.householdShare, data.profile.partnerName, user?.id],
  );
  const householdMonthlyExpenses = useMemo(
    () => monthlyExpenses.filter((expense) => expense.visibility === 'household'),
    [monthlyExpenses],
  );
  const householdMonthlySpend = useMemo(
    () => householdMonthlyExpenses.reduce((sum, expense) => sum + expense.amount, 0),
    [householdMonthlyExpenses],
  );
  const moneyHouseholdBenchmarkInput = useMemo(
    () => ({
      partnerName: data.profile.partnerName,
      hasHouseholdShare: Boolean(data.householdShare),
      childrenCount: data.profile.children.filter((child) => child.name.trim().length > 0).length,
    }),
    [data.householdShare, data.profile.children, data.profile.partnerName],
  );

  useEffect(() => {
    let active = true;

    const loadPetVisibility = async () => {
      if (!user) {
        if (active) {
          setHasPetProfiles(false);
        }
        return;
      }

      const { data: petRows, error } = await supabase
        .from('pets')
        .select('name')
        .order('name');

      if (!active) return;
      if (error) {
        setHasPetProfiles(false);
        return;
      }

      setHasPetProfiles(hasTrackedPets((petRows || []) as Array<{ name?: string | null }>));
    };

    void loadPetVisibility();

    return () => {
      active = false;
    };
  }, [user]);

  useEffect(() => {
    if (!data.householdShare) {
      setExpenseVisibility('personal');
    }
  }, [data.householdShare]);

  useEffect(() => {
    if (!budgetCategories.includes(category)) {
      setCategory(budgetCategories[0] || DEFAULT_BUDGET_CATEGORIES[0]);
    }
    if (!budgetCategories.includes(budgetCategory)) {
      setBudgetCategory(budgetCategories[0] || DEFAULT_BUDGET_CATEGORIES[0]);
    }
  }, [budgetCategories, budgetCategory, category]);

  useEffect(() => {
    const selectedBudget = visibleBudgetLimits.find((item) => item.category === budgetCategory);
    setBudgetLimit(selectedBudget ? String(selectedBudget.limit) : '');
  }, [budgetCategory, visibleBudgetLimits]);

  const handleRecurringPeriodChange = (value: ExpenseRecurringPeriod) => {
    setRecurringPeriod(value);
    if (value === 'none') {
      setTrackAsSubscription(false);
      return;
    }
    setRecurringReminderDays(String(getDefaultRecurringReminderDays(value)));
    setRecurringDueDate(format(new Date(), 'yyyy-MM-dd'));
  };

  const handlePrefillBankRecurringCommitment = useCallback((item: BankFeedRecurringCommitment) => {
    const inferredCategory = mapBreakdownCategoryIdToLegacyCategory(item.categoryId) as BudgetCategory;
    const resolvedCategory = hiddenBudgetCategories.includes(inferredCategory)
      ? (budgetCategories[0] || DEFAULT_BUDGET_CATEGORIES[0]) as BudgetCategory
      : inferredCategory;
    const recurringPeriod = getBankCommitmentRecurringPeriod(item.cadence);

    setOpenBudgetSection('live-budget');
    setCategory(resolvedCategory);
    setCategoryCustom('');
    setAmount(item.averageAmount.toFixed(2));
    setDescription(item.merchant);
    setExpenseVisibility('personal');
    setReceiptFile(null);
    setTrackAsSubscription(false);
    handleRecurringPeriodChange(recurringPeriod);
    setRecurringDueDate(getBankCommitmentNextDueDate(item.latestBookingDate, item.cadence));
    setRecurringReminderDays(String(getDefaultRecurringReminderDays(recurringPeriod)));
    setOpen(true);
  }, [budgetCategories, hiddenBudgetCategories]);

  const openExpenseReceipt = async (expense: Expense) => {
    if (!expense.receiptStoragePath) return;

    const { data: signedUrlData, error } = await supabase.storage.from('user-documents').createSignedUrl(expense.receiptStoragePath, 60);
    if (error || !signedUrlData?.signedUrl) {
      toast.error('Could not open that receipt.');
      return;
    }

    window.open(signedUrlData.signedUrl, '_blank', 'noopener,noreferrer');
  };

  const handleAdd = async () => {
    if (!amount || Number.isNaN(Number(amount))) return;
    if (recurringPeriod !== 'none' && !recurringDueDate) return;
    const resolvedCategory = resolveBudgetCategory(category, categoryCustom);
    if (!resolvedCategory) return;
    if (hiddenBudgetCategories.includes(resolvedCategory)) {
      toast.error('Restore that category before using it again.');
      return;
    }

    const expense: Expense = {
      id: crypto.randomUUID(),
      amount: Number(amount),
      category: resolvedCategory,
      date: recurringPeriod === 'none' ? format(new Date(), 'yyyy-MM-dd') : recurringDueDate,
      description,
      visibility: data.householdShare ? expenseVisibility : 'personal',
      isRecurring: recurringPeriod !== 'none',
      recurringPeriod,
      isSubscription: recurringPeriod !== 'none' ? trackAsSubscription : false,
      cancelIntent: false,
      nextDueDate: recurringPeriod === 'none' ? undefined : recurringDueDate,
      reminderDaysBefore:
        recurringPeriod === 'none'
          ? undefined
          : Math.max(0, Number(recurringReminderDays) || getDefaultRecurringReminderDays(recurringPeriod)),
    };

    const saved = await addExpense(expense, receiptFile);
    if (!saved) {
      toast.error('Could not save that expense.');
      return;
    }

    setAmount('');
    setCategory(resolvedCategory);
    setDescription('');
    setReceiptFile(null);
    setExpenseVisibility('personal');
    setRecurringPeriod('none');
    setTrackAsSubscription(false);
    setRecurringDueDate(format(new Date(), 'yyyy-MM-dd'));
    setRecurringReminderDays(String(getDefaultRecurringReminderDays('monthly')));
    setCategoryCustom('');
    setOpen(false);
  };

  const handleSaveBudgetLimit = () => {
    const resolvedCategory = resolveBudgetCategory(budgetCategory, budgetCategoryCustom);
    if (!resolvedCategory) return;
    if (!budgetLimit || Number.isNaN(Number(budgetLimit))) return;
    if (hiddenBudgetCategories.includes(resolvedCategory)) {
      toast.error('Restore that category before using it again.');
      return;
    }

    const nextLimitValue = Number(budgetLimit);
    const remainingLimits = data.budgetLimits.filter((item) => item.category !== resolvedCategory);
    const orderedCategories = buildBudgetCategoryOptions(budgetCategories, [resolvedCategory]);
    updateBudgetLimits(
      [...remainingLimits, { category: resolvedCategory, limit: nextLimitValue }]
        .sort((left, right) => orderedCategories.indexOf(left.category) - orderedCategories.indexOf(right.category)),
    );
    setCategory(resolvedCategory);
    setBudgetCategory(resolvedCategory);
    setBudgetCategoryCustom('');
    setBudgetEditorOpen(false);
    toast.success(`${resolvedCategory} budget saved.`);
  };

  const handleAddCategory = async () => {
    const resolvedCategory = normaliseBudgetCategoryLabel(categoryManagerValue);
    if (!resolvedCategory) return;

    if (hiddenBudgetCategories.includes(resolvedCategory)) {
      const restored = await setBudgetCategoryHidden(resolvedCategory, false);
      if (restored) {
        setCategoryManagerValue('');
        toast.success(`${resolvedCategory} restored.`);
      }
      return;
    }

    if (categoryCollections.all.includes(resolvedCategory)) {
      toast.error('That category already exists.');
      return;
    }

    const saved = await addBudgetCategory(resolvedCategory);
    if (saved) {
      setCategoryManagerValue('');
      toast.success(`${resolvedCategory} added.`);
    }
  };

  const handleRenameCategory = async () => {
    if (!categoryRenameSource) return;
    const resolvedCategory = normaliseBudgetCategoryLabel(categoryRenameValue);
    if (!resolvedCategory) return;
    if (resolvedCategory !== categoryRenameSource && categoryCollections.all.includes(resolvedCategory)) {
      toast.error('That category name already exists.');
      return;
    }

    const saved = await renameBudgetCategory(categoryRenameSource, resolvedCategory);
    if (saved) {
      setCategoryRenameSource(null);
      setCategoryRenameValue('');
      setCategory(resolvedCategory);
      setBudgetCategory(resolvedCategory);
      toast.success(`${categoryRenameSource} renamed to ${resolvedCategory}.`);
    }
  };

  const handleHideCategory = async (label: string) => {
    const saved = await setBudgetCategoryHidden(label, true);
    if (saved) {
      toast.success(`${label} hidden.`);
    }
  };

  const handleShowCategory = async (label: string) => {
    const saved = await setBudgetCategoryHidden(label, false);
    if (saved) {
      toast.success(`${label} restored.`);
    }
  };

  const openPaymentDialog = (expense: typeof recurringExpenses[number]) => {
    setSelectedRecurringExpenseId(expense.id);
    setPaymentAmount(expense.amount.toFixed(2));
    setPaymentOpen(true);
  };

  const handleRecordPayment = async () => {
    if (!selectedRecurringExpense || !paymentAmount || Number.isNaN(Number(paymentAmount))) return;

    setPaymentSaving(true);
    try {
      await recordRecurringExpensePayment(selectedRecurringExpense.id, Number(paymentAmount));
      toast.success('Payment logged and tracker moved to the next due date.');
      setPaymentOpen(false);
      setSelectedRecurringExpenseId(null);
      setPaymentAmount('');
    } catch (error) {
      console.error(error);
      toast.error('Could not log that payment right now.');
    } finally {
      setPaymentSaving(false);
    }
  };

  const handleUpdateRecurringFlags = async (
    expenseId: string,
    updates: { isSubscription?: boolean; cancelIntent?: boolean },
    successMessage: string,
  ) => {
    const saved = await updateRecurringExpenseFlags(expenseId, updates);
    if (!saved) {
      toast.error('Could not update that subscription right now.');
      return;
    }
    toast.success(successMessage);
  };

  const renderRecurringExpenseCard = (
    expense: typeof recurringExpenses[number],
    options: { subscriptionView: boolean },
  ) => {
    const statusMeta = getRecurringStatusMeta(expense.paymentStatus);

    return (
      <div key={expense.id} className="bg-card rounded-lg border border-border p-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-medium text-foreground">{expense.description || expense.category}</p>
              <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                {recurringPeriodLabel(expense.recurringPeriod)}
              </span>
              {expense.isSubscription && (
                <span className="inline-flex rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-secondary-foreground">
                  Subscription
                </span>
              )}
              {expense.visibility === 'household' && (
                <span className="inline-flex rounded-full bg-teal-light px-2 py-0.5 text-[10px] font-medium text-teal">
                  Household
                </span>
              )}
              {expense.isSubscription && expense.cancelIntent && (
                <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-900">
                  Cancel intent
                </span>
              )}
              <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${statusMeta.classes}`}>
                {statusMeta.label}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {expense.category} · {options.subscriptionView ? getRecurringRenewalLabel(expense.nextDue) : getRecurringDueLabel(expense.nextDue)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Reminder window: {expense.reminderDaysBefore} day{expense.reminderDaysBefore === 1 ? '' : 's'} before
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {getLastPaidLabel(expense.lastPaidAt, expense.lastPaidAmount)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-foreground">{formatSterlingAmount(expense.amount)}</p>
            {expense.recurringPeriod === 'yearly' && (
              <p className="text-xs text-muted-foreground mt-1">
                {formatSterlingAmount(expense.monthlyEquivalent)} / month equivalent
              </p>
            )}
          </div>
        </div>
        {expense.largerThanExpected && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            <p className="text-xs font-medium text-amber-900">Last payment was larger than expected.</p>
            <p className="text-xs text-amber-800 mt-1">
              Expected {formatSterlingAmount(expense.amount)} but logged {formatSterlingAmount(expense.lastPaidAmount || expense.amount)} last time.
            </p>
          </div>
        )}
        <AppButtonRow className="mt-3">
          {expense.isSubscription ? (
            <>
              <AppButton
                size="sm"
                tone={expense.cancelIntent ? 'outline' : 'primary'}
                onPress={() => void handleUpdateRecurringFlags(
                  expense.id,
                  { cancelIntent: !expense.cancelIntent },
                  expense.cancelIntent ? 'Cancel intent cleared.' : 'Cancel intent saved.',
                )}
              >
                {expense.cancelIntent ? 'Clear cancel intent' : 'Mark cancel intent'}
              </AppButton>
              <AppButton
                size="sm"
                tone="outline"
                onPress={() => void handleUpdateRecurringFlags(
                  expense.id,
                  { isSubscription: false, cancelIntent: false },
                  'Removed from subscriptions.',
                )}
              >
                Remove from subscriptions
              </AppButton>
            </>
          ) : (
            <AppButton
              size="sm"
              tone="outline"
              onPress={() => void handleUpdateRecurringFlags(
                expense.id,
                { isSubscription: true, cancelIntent: false },
                'Moved into subscriptions.',
              )}
            >
              Track as subscription
            </AppButton>
          )}
          {(expense.paymentStatus === 'due-soon' || expense.paymentStatus === 'missed') && (
            <AppButton size="sm" onPress={() => openPaymentDialog(expense)}>
              Mark paid
            </AppButton>
          )}
        </AppButtonRow>
      </div>
    );
  };

  return (
    <>
      {overBudgetCategories.length > 0 && (
        <AppInlineAlert title="You are over budget" tone="warning">
          {`You are over budget in ${overBudgetCategories.map((entry) => entry.category).join(', ')}. Use the bank-linked budget section below if you want connected-bank history to help shape a truer starting point.`}
        </AppInlineAlert>
      )}

      {data.householdShare ? (
        <AppInlineAlert title={householdSharingSummary.statusLabel === 'Invite ready' ? 'Household invite ready' : 'Household sharing is live'} tone="info">
          {householdSharingSummary.moneySummary}
        </AppInlineAlert>
      ) : null}

      <div className="space-y-4">
        <BudgetAccordionSection
          open={openBudgetSection === 'live-budget'}
          onToggle={() => toggleBudgetSection('live-budget')}
          emoji="💷"
          title="Live budget"
          subtitle="Your current limits, categories, and spending in one calmer place."
        >
          <section className="mb-6">
            <AppSectionHeader
              title="Budget"
              subtitle="Adjust your live budget categories, limits, and spending from the same shared Money flow."
              action={(
                <div className="flex flex-wrap gap-2">
                  <Dialog open={budgetEditorOpen} onOpenChange={setBudgetEditorOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="h-auto rounded-full px-4 py-1.5 text-[11px] font-medium">
                        Edit budget
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-sm">
                      <DialogHeader>
                        <DialogTitle>Edit budget limit</DialogTitle>
                        <DialogDescription>
                          Keep the shared defaults, or add your own category when your real money life needs one.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-3">
                        <Select value={budgetCategory} onValueChange={(value) => setBudgetCategory(value as BudgetCategory)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {budgetCategories.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="New category name (optional)"
                          value={budgetCategoryCustom}
                          onChange={(event) => setBudgetCategoryCustom(event.target.value)}
                        />
                        <Input
                          placeholder="Monthly limit (£)"
                          value={budgetLimit}
                          onChange={(event) => setBudgetLimit(event.target.value)}
                          type="number"
                        />
                        <Button onClick={handleSaveBudgetLimit} className="w-full bg-primary text-primary-foreground">
                          Save budget limit
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={categoryManagerOpen} onOpenChange={setCategoryManagerOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="h-auto rounded-full px-4 py-1.5 text-[11px] font-medium">
                        Manage categories
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Manage budget categories</DialogTitle>
                        <DialogDescription>
                          Add the categories you really use, rename ones that need better labels, and hide the defaults you do not want cluttering Money.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-foreground">Add a category</label>
                          <div className="flex gap-2">
                            <Input
                              placeholder="Category name"
                              value={categoryManagerValue}
                              onChange={(event) => setCategoryManagerValue(event.target.value)}
                            />
                            <Button type="button" onClick={() => void handleAddCategory()}>
                              Add
                            </Button>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <p className="text-xs font-medium text-foreground">Visible in Money</p>
                          <div className="space-y-2">
                            {budgetCategories.map((item) => (
                              <div key={item} className="rounded-2xl border border-border bg-background px-3 py-3">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div>
                                    <AppText fontSize="$sm" fontWeight="700">{item}</AppText>
                                    <AppText fontSize="$xs" color="$colorMuted">Shows in budget limits, add expense, and live Money totals.</AppText>
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    <Button type="button" variant="outline" onClick={() => {
                                      setCategoryRenameSource(item);
                                      setCategoryRenameValue(item);
                                    }}>
                                      Rename
                                    </Button>
                                    <Button type="button" variant="outline" onClick={() => void handleHideCategory(item)}>
                                      Hide
                                    </Button>
                                  </div>
                                </div>
                                {categoryRenameSource === item ? (
                                  <div className="mt-3 flex flex-wrap gap-2">
                                    <Input
                                      value={categoryRenameValue}
                                      onChange={(event) => setCategoryRenameValue(event.target.value)}
                                      placeholder="New category name"
                                    />
                                    <Button type="button" onClick={() => void handleRenameCategory()}>
                                      Save rename
                                    </Button>
                                    <Button type="button" variant="outline" onClick={() => {
                                      setCategoryRenameSource(null);
                                      setCategoryRenameValue('');
                                    }}>
                                      Cancel
                                    </Button>
                                  </div>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        </div>

                        {hiddenBudgetCategories.length ? (
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-foreground">Hidden for now</p>
                            <div className="space-y-2">
                              {hiddenBudgetCategories.map((item) => (
                                <div key={item} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-border bg-muted/30 px-3 py-3">
                                  <div>
                                    <AppText fontSize="$sm" fontWeight="700">{item}</AppText>
                                    <AppText fontSize="$xs" color="$colorMuted">Hidden from live Money pickers and budget rows until you restore it.</AppText>
                                  </div>
                                  <Button type="button" variant="outline" onClick={() => void handleShowCategory(item)}>
                                    Show again
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                      <Button className="h-auto rounded-full px-4 py-1.5 text-[11px] font-medium">
                        <Plus size={14} /> Add expense
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-sm">
                      <DialogHeader>
                        <DialogTitle>Add expense</DialogTitle>
                        <DialogDescription>
                          Add a spending item to your budget so your monthly totals and recent expenses stay up to date.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-3">
                        <Input placeholder="Amount (£)" value={amount} onChange={(event) => setAmount(event.target.value)} type="number" />
                        <Select value={category} onValueChange={(value) => setCategory(value as BudgetCategory)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {budgetCategories.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="New category name (optional)"
                          value={categoryCustom}
                          onChange={(event) => setCategoryCustom(event.target.value)}
                        />
                        <Input placeholder="Description (optional)" value={description} onChange={(event) => setDescription(event.target.value)} />
                        {data.householdShare ? (
                          <div className="space-y-2">
                            <label className="text-xs font-medium text-foreground">Visibility</label>
                            <AppButtonRow>
                              <AppButton
                                type="button"
                                size="sm"
                                tone={expenseVisibility === 'personal' ? 'primary' : 'outline'}
                                onPress={() => setExpenseVisibility('personal')}
                              >
                                Personal
                              </AppButton>
                              <AppButton
                                type="button"
                                size="sm"
                                tone={expenseVisibility === 'household' ? 'primary' : 'outline'}
                                onPress={() => setExpenseVisibility('household')}
                              >
                                Household
                              </AppButton>
                            </AppButtonRow>
                            <p className="text-xs text-muted-foreground">
                              {expenseVisibility === 'household'
                                ? `This expense will be visible to ${householdPeerName} in Money.`
                                : 'This expense stays private to your account.'}
                            </p>
                          </div>
                        ) : null}
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-foreground">Receipt photo or file (optional)</label>
                          <FileInput
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(event) => setReceiptFile(event.target.files?.[0] || null)}
                          />
                          {receiptFile ? (
                            <div className="flex items-center justify-between gap-2 rounded-2xl border border-border bg-muted/30 px-3 py-2">
                              <AppText fontSize="$xs" color="$colorMuted">{receiptFile.name}</AppText>
                              <Button type="button" variant="outline" onClick={() => setReceiptFile(null)}>
                                Remove
                              </Button>
                            </div>
                          ) : null}
                        </div>
                        <div className="space-y-2">
                          <Select value={recurringPeriod} onValueChange={(value) => handleRecurringPeriodChange(value as ExpenseRecurringPeriod)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {RECURRING_EXPENSE_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {recurringPeriod !== 'none' && (
                            <>
                              <div className="grid gap-3 sm:grid-cols-2">
                                <AppDateField label="Next due date" value={recurringDueDate} onChange={setRecurringDueDate} />
                                <div className="space-y-1">
                                  <label className="text-xs font-medium text-foreground">Reminder window (days)</label>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={recurringReminderDays}
                                    onChange={(event) => setRecurringReminderDays(event.target.value)}
                                  />
                                </div>
                              </div>
                              <div className="space-y-2">
                                <label className="text-xs font-medium text-foreground">Recurring type</label>
                                <AppButtonRow>
                                  <AppButton
                                    type="button"
                                    size="sm"
                                    tone={trackAsSubscription ? 'outline' : 'primary'}
                                    onPress={() => setTrackAsSubscription(false)}
                                  >
                                    Regular recurring
                                  </AppButton>
                                  <AppButton
                                    type="button"
                                    size="sm"
                                    tone={trackAsSubscription ? 'primary' : 'outline'}
                                    onPress={() => setTrackAsSubscription(true)}
                                  >
                                    Subscription
                                  </AppButton>
                                </AppButtonRow>
                                <p className="text-xs text-muted-foreground">
                                  {trackAsSubscription
                                    ? 'This will show in the subscription manager with a renewal date and optional cancel-intent flag.'
                                    : 'Use Subscription for services you may want to review or cancel separately from other recurring admin.'}
                                </p>
                              </div>
                            </>
                          )}
                          <p className="text-xs text-muted-foreground">
                            Use this for subscriptions, direct debits, and annual renewals. Adulting keeps the due date visible, reminds you in the saved window, and only counts it as spend once you log it paid.
                          </p>
                        </div>
                        <Button onClick={() => void handleAdd()} className="w-full bg-primary text-primary-foreground">Save</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            />

            <div className="space-y-3">
              {visibleBudgetLimits.map((limit) => {
                const spent = monthlyExpenses
                  .filter((expense) => expense.category === limit.category)
                  .reduce((sum, expense) => sum + expense.amount, 0);
                const pct = limit.limit > 0 ? Math.min((spent / limit.limit) * 100, 100) : 0;
                const over = spent > limit.limit;

                return (
                  <div key={limit.category} className="bg-card rounded-lg border border-border p-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-foreground">{limit.category}</span>
                      <span className={over ? 'text-destructive font-semibold' : 'text-muted-foreground'}>
                        £{spent.toFixed(0)} / £{limit.limit}
                      </span>
                    </div>
                    <Progress value={pct} className="h-2" />
                  </div>
                );
              })}
            </div>
          </section>
        </BudgetAccordionSection>

        <BudgetAccordionSection
          open={openBudgetSection === 'payment-tracker'}
          onToggle={() => toggleBudgetSection('payment-tracker')}
          emoji="🧾"
          title="Payment tracker"
          subtitle="Keep recurring commitments easy to find without scanning the whole Money page."
        >
          <section className="mb-6">
            <AppSectionHeader
              title="Payment tracker"
              subtitle="Keep subscriptions, direct debits, and annual renewals visible. Mark them paid here when they actually go out so your budget history stays honest."
            />

            {bankRecurringLoading ? (
              <AppInlineAlert title="Checking connected accounts" tone="info">
                Adulting is looking for regular outgoing payments that are not already in your tracker.
              </AppInlineAlert>
            ) : null}

            {bankRecurringLoadError ? (
              <AppInlineAlert title="Connected-bank check unavailable" tone="warning">
                {bankRecurringLoadError}
              </AppInlineAlert>
            ) : null}

            {bankRecurringTrackerItems.length > 0 ? (
              <div className="rounded-2xl border border-border bg-muted/20 p-4 mb-3">
                <div className="mb-3">
                  <AppText fontSize="$sm" fontWeight="700">Detected from connected accounts</AppText>
                  <AppText fontSize="$xs" color="$colorMuted">
                    Adulting has spotted regular outgoing payments in your bank history. Add the ones you want due dates and mark-paid actions for.
                  </AppText>
                </div>
                <div className="space-y-2">
                  {bankRecurringTrackerItems.map((item) => (
                    <div key={`bank-recurring-${item.merchant}-${item.categoryId}`} className="rounded-2xl border border-border bg-card px-4 py-3">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <AppText fontSize="$sm" fontWeight="700">{item.merchant}</AppText>
                            <span className="inline-flex rounded-full bg-background px-2.5 py-1 text-[10px] font-medium text-muted-foreground">
                              {getRecurringCadenceLabel(item.cadence)}
                            </span>
                            <span className="inline-flex rounded-full bg-background px-2.5 py-1 text-[10px] font-medium text-muted-foreground">
                              Bank-detected
                            </span>
                          </div>
                          <AppText fontSize="$xs" color="$colorMuted" className="mt-1">
                            {item.categoryLabel} · average {formatSterlingAmount(item.averageAmount)} · latest {format(new Date(item.latestBookingDate), 'd MMM yyyy')}
                          </AppText>
                          <AppText fontSize="$xs" color="$colorMuted" className="mt-1">
                            Monthly equivalent {formatSterlingAmount(item.monthlyEquivalent)}
                          </AppText>
                          <AppText fontSize="$xs" color="$colorMuted" className="mt-2">
                            Adulting found this from connected-bank history. Add it to the tracker when you want reminders and mark-paid flow here.
                          </AppText>
                        </div>
                        <div className="text-right">
                          <AppText fontSize="$sm" fontWeight="700">{formatSterlingAmount(item.averageAmount)}</AppText>
                        </div>
                      </div>
                      <AppButtonRow className="mt-3">
                        <AppButton size="sm" tone="primary" onPress={() => handlePrefillBankRecurringCommitment(item)}>
                          Add to tracker
                        </AppButton>
                      </AppButtonRow>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {recurringExpenses.length === 0 ? (
              <AppEmptyState
                title={bankRecurringTrackerItems.length > 0 ? 'Nothing saved to the tracker yet' : 'No recurring payments saved yet'}
                body={bankRecurringTrackerItems.length > 0
                  ? 'Adulting has found regular bank payments above. Add the ones you want tracked here so due dates and mark-paid actions stay close.'
                  : 'When you add an expense, choose Monthly or Yearly to keep it on the tracker.'}
              />
            ) : (
              <>
                <div className="grid gap-3 sm:grid-cols-4 mb-3">
                  <div className="bg-card rounded-lg border border-border p-3">
                    <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Monthly commitment</p>
                    <p className="text-lg font-bold text-foreground mt-1">{formatSterlingAmount(recurringMonthlyCommitment)}</p>
                  </div>
                  <div className="bg-card rounded-lg border border-border p-3">
                    <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Due soon</p>
                    <p className="text-lg font-bold text-foreground mt-1">{recurringDueSoonCount}</p>
                  </div>
                  <div className="bg-card rounded-lg border border-border p-3">
                    <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Missed</p>
                    <p className="text-lg font-bold text-destructive mt-1">{recurringMissedCount}</p>
                  </div>
                  <div className="bg-card rounded-lg border border-border p-3">
                    <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Larger than expected</p>
                    <p className="text-lg font-bold text-foreground mt-1">{recurringLargerThanExpectedCount}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="rounded-2xl border border-border bg-muted/20 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <AppText fontSize="$sm" fontWeight="700">Subscription manager</AppText>
                        <AppText fontSize="$xs" color="$colorMuted">
                          Dedicated recurring subscriptions with renewal dates and a cancel-intent flag.
                        </AppText>
                      </div>
                      <span className="inline-flex rounded-full bg-background px-2.5 py-1 text-[10px] font-medium text-muted-foreground">
                        {subscriptionExpenses.length} saved
                      </span>
                    </div>
                    {subscriptionExpenses.length ? (
                      <div className="mt-3 space-y-2">
                        {subscriptionExpenses.map((expense) => renderRecurringExpenseCard(expense, { subscriptionView: true }))}
                      </div>
                    ) : (
                      <div className="mt-3">
                        <AppEmptyState
                          title="No subscriptions saved yet"
                          body="Track a recurring payment as a subscription to keep renewal dates and cancel intent visible here."
                        />
                      </div>
                    )}
                  </div>

                  {otherRecurringExpenses.length > 0 && (
                    <div className="pt-2">
                      <div className="mb-3">
                        <AppText fontSize="$sm" fontWeight="700">Other recurring payments</AppText>
                        <AppText fontSize="$xs" color="$colorMuted">
                          Direct debits, annual renewals, and other recurring admin that still belongs on the tracker.
                        </AppText>
                      </div>
                      <div className="space-y-2">
                        {otherRecurringExpenses.map((expense) => renderRecurringExpenseCard(expense, { subscriptionView: false }))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </section>
        </BudgetAccordionSection>

        <BudgetAccordionSection
          open={openBudgetSection === 'bank-budget'}
          onToggle={() => toggleBudgetSection('bank-budget')}
          emoji="🏦"
          title="Budget from bank history"
          subtitle="Imported statements and connected-bank analysis, kept together inside Budget."
        >
          <StatementsTab hasPetProfiles={hasPetProfiles} />
        </BudgetAccordionSection>

        {monthlyExpenses.length > 0 ? (
          <BudgetAccordionSection
            open={openBudgetSection === 'recent-expenses'}
            onToggle={() => toggleBudgetSection('recent-expenses')}
            emoji="📌"
            title="Recent expenses"
            subtitle="Your latest visible spend, without scrolling through the whole Money page."
          >
            <section>
              <h2 className="text-[11px] font-medium text-[#888] uppercase tracking-[0.08em] mb-3">Recent expenses</h2>
              <div className="space-y-2">
                {monthlyExpenses.slice(-5).reverse().map((expense) => (
                  <div key={expense.id} className="flex justify-between items-center bg-card rounded-lg border border-border p-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-foreground">{expense.description || expense.category}</p>
                        {expense.isRecurring && expense.recurringPeriod && expense.recurringPeriod !== 'none' && (
                          <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                            {recurringPeriodLabel(expense.recurringPeriod)}
                          </span>
                        )}
                        {expense.visibility === 'household' && (
                          <span className="inline-flex rounded-full bg-teal-light px-2 py-0.5 text-[10px] font-medium text-teal">
                            Household
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{expense.category} · {format(new Date(expense.date), 'd MMM')}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {expense.receiptStoragePath ? (
                        <Button type="button" variant="outline" onClick={() => void openExpenseReceipt(expense)}>
                          Open receipt
                        </Button>
                      ) : null}
                      <span className="text-sm font-semibold text-foreground">£{expense.amount.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </BudgetAccordionSection>
        ) : null}

        {data.householdShare ? (
          <BudgetAccordionSection
            open={openBudgetSection === 'household-spend'}
            onToggle={() => toggleBudgetSection('household-spend')}
            emoji="🏠"
            title="Household spend trail"
            subtitle={`Shared expenses for you and ${householdPeerName}, kept in one place.`}
          >
            <section className="mt-0">
              <AppSectionHeader
                title="Household spend trail"
                subtitle={`Only expenses marked Household are visible here for both you and ${householdPeerName}.`}
              />
              {householdMonthlyExpenses.length > 0 ? (
                <div className="space-y-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="bg-card rounded-lg border border-border p-3">
                      <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Shared this month</p>
                      <p className="text-lg font-bold text-foreground mt-1">{formatSterlingAmount(householdMonthlySpend)}</p>
                    </div>
                    <div className="bg-card rounded-lg border border-border p-3">
                      <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Shared items</p>
                      <p className="text-lg font-bold text-foreground mt-1">{householdMonthlyExpenses.length}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {householdMonthlyExpenses.slice(-3).reverse().map((expense) => (
                      <div key={expense.id} className="flex justify-between items-center bg-card rounded-lg border border-border p-3">
                        <div>
                          <p className="text-sm font-medium text-foreground">{expense.description || expense.category}</p>
                          <p className="text-xs text-muted-foreground">{expense.category} · {format(new Date(expense.date), 'd MMM')}</p>
                        </div>
                        <span className="text-sm font-semibold text-foreground">{formatSterlingAmount(expense.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <AppInlineAlert title="No shared household spend saved yet." tone="info">
                  Mark an expense as Household when you want it visible in Money for both adults.
                </AppInlineAlert>
              )}
            </section>
          </BudgetAccordionSection>
        ) : null}
      </div>

      <Dialog
        open={paymentOpen}
        onOpenChange={(nextOpen) => {
          setPaymentOpen(nextOpen);
          if (!nextOpen) {
            setSelectedRecurringExpenseId(null);
            setPaymentAmount('');
          }
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Mark payment as paid</DialogTitle>
            <DialogDescription>
              Log the actual amount that left your account. This adds a one-off spend entry and moves the tracker to the next due date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-border bg-muted/30 px-3 py-3">
              <p className="text-sm font-medium text-foreground">
                {selectedRecurringExpense?.description || selectedRecurringExpense?.category || 'Recurring payment'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {selectedRecurringExpense?.nextDue ? getRecurringDueLabel(selectedRecurringExpense.nextDue) : 'Due date unavailable'}
              </p>
            </div>
            <Input
              placeholder="Amount paid (£)"
              type="number"
              value={paymentAmount}
              onChange={(event) => setPaymentAmount(event.target.value)}
            />
            <Button
              onClick={handleRecordPayment}
              disabled={paymentSaving || !paymentAmount || Number.isNaN(Number(paymentAmount))}
              className="w-full bg-primary text-primary-foreground"
            >
              {paymentSaving ? 'Saving...' : 'Log payment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function StatementsTab({ hasPetProfiles }: { hasPetProfiles: boolean }) {
  const { user, updateBudgetLimits, data, bulkAddExpenses } = useApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [imports, setImports] = useState<StatementImportRow[]>([]);
  const [importsLoading, setImportsLoading] = useState(true);
  const [previewTransactions, setPreviewTransactions] = useState<StatementTransaction[]>([]);
  const [previewSummary, setPreviewSummary] = useState<StatementImportSummary | null>(null);
  const [statementParsing, setStatementParsing] = useState(false);
  const [saveIntent, setSaveIntent] = useState<'save' | 'sync' | null>(null);
  const [syncingImportId, setSyncingImportId] = useState<string | null>(null);
  const [bankViewMode, setBankViewMode] = useState<BankViewMode>('mine');
  const [bankAccounts, setBankAccounts] = useState<BankAccountRow[]>([]);
  const [bankTransactions, setBankTransactions] = useState<BankTransactionRow[]>([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [householdBankAccounts, setHouseholdBankAccounts] = useState<HouseholdBankAccountRow[]>([]);
  const [householdBankTransactions, setHouseholdBankTransactions] = useState<HouseholdBankTransactionRow[]>([]);
  const [householdBankLoading, setHouseholdBankLoading] = useState(false);
  const [bankSyncing, setBankSyncing] = useState(false);
  const [showRecentBankTransactions, setShowRecentBankTransactions] = useState(false);
  const [integrationSettings, setIntegrationSettings] = useState<IntegrationSettingsRecord | null>(null);
  const [integrationSettingsLoading, setIntegrationSettingsLoading] = useState(false);
  const [autoBudgetSaving, setAutoBudgetSaving] = useState(false);
  const [homeProfile, setHomeProfile] = useState<HomeProfileMoneyContext | null>(null);
  const [homeVehicles, setHomeVehicles] = useState<SharedHomeVehicleRecord[]>([]);
  const [vehicleInsuranceRenewals, setVehicleInsuranceRenewals] = useState<SharedVehicleInsuranceRenewal[]>([]);
  const [storedHomeBills, setStoredHomeBills] = useState<SharedHomeBillRecord[]>([]);
  const [bankCategoryOverrides, setBankCategoryOverrides] = useState<BankFeedCategoryOverride[]>([]);
  const [bankCategoryRules, setBankCategoryRules] = useState<BankCategoryRule[]>([]);
  const [amazonOrders, setAmazonOrders] = useState<AmazonOrderRow[]>([]);
  const [amazonOrderItems, setAmazonOrderItems] = useState<AmazonOrderItemRow[]>([]);
  const [amazonCategoryRules, setAmazonCategoryRules] = useState<AmazonCategoryRuleRow[]>([]);
  const [amazonPasteText, setAmazonPasteText] = useState('');
  const [amazonSaving, setAmazonSaving] = useState(false);
  const [bankCorrectionTarget, setBankCorrectionTarget] = useState<BankCorrectionTarget | null>(null);
  const [bankCorrectionCategoryId, setBankCorrectionCategoryId] = useState<BankBudgetCategoryId>('other_discretionary');
  const [savingBankCorrectionScope, setSavingBankCorrectionScope] = useState<BankCategoryRuleScope | null>(null);
  const [budgetBreakdownView, setBudgetBreakdownView] = useState<'monthly' | 'annual'>('monthly');
  const [transactionCategoryFilterId, setTransactionCategoryFilterId] = useState<BankBudgetCategoryId | null>(null);
  const [applyingHomeBillKey, setApplyingHomeBillKey] = useState<SharedHomeBillKey | null>(null);
  const bankEnabled = isOpenBankingEnabled();
  const authState = searchParams.get('state');
  const autoBudgetApplyingRef = useRef(false);
  const reviewTransactionsAnchorRef = useRef<HTMLDivElement | null>(null);
  const correctionAnchorRef = useRef<HTMLDivElement | null>(null);
  const importPreviewAnchorRef = useRef<HTMLDivElement | null>(null);
  const pendingReviewScrollRef = useRef(false);
  const pendingCorrectionScrollRef = useRef(false);
  const householdPeerName = useMemo(
    () => getHouseholdSharePeerName(data.householdShare, user?.id || null) || data.profile.partnerName.trim() || 'your other adult',
    [data.householdShare, data.profile.partnerName, user?.id],
  );
  const recurringTrackerMerchantKeys = useMemo(
    () =>
      new Set(
        data.expenses
          .filter((expense) => expense.isRecurring && expense.recurringPeriod && expense.recurringPeriod !== 'none')
          .map((expense) => normaliseMoneyMerchant(expense.description || expense.category))
          .filter(Boolean),
      ),
    [data.expenses],
  );
  const moneyHouseholdBenchmarkInput = useMemo(
    () => ({
      partnerName: data.profile.partnerName || '',
      hasHouseholdShare: Boolean(data.householdShare),
      childrenCount: data.profile.children.filter((child) => child.name.trim().length > 0).length,
    }),
    [data.householdShare, data.profile.children, data.profile.partnerName],
  );

  const fetchImports = useCallback(async () => {
    if (authState?.startsWith(MOBILE_BANK_CALLBACK_STATE_PREFIX)) {
      window.location.replace(buildMobileBankCallbackUrl(searchParams));
      return;
    }

    if (!user) return;

    setImportsLoading(true);
    const { data: importRows, error } = await supabase
      .from('statement_imports' as any)
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(6);

    if (error) {
      console.error(error);
      setImports([]);
    } else {
      setImports((importRows || []) as StatementImportRow[]);
    }
    setImportsLoading(false);
  }, [authState, searchParams, user]);

  const fetchHomeMoneySetup = useCallback(async () => {
    if (!user) {
      setHomeProfile(null);
      setHomeVehicles([]);
      setVehicleInsuranceRenewals([]);
      return;
    }

    const [profileResult, vehicleResult, renewalResult] = await Promise.all([
      supabase
        .from('home_profiles')
        .select('address, tenure, monthly_rent, landlord_name, council_tax_band, mortgage_provider, ground_rent_amount, service_charge_amount, has_vehicle, vehicle_registration_number, vehicle_tax_due_date, vehicle_mot_due_date, utility_gas_provider, utility_electric_provider, utility_broadband_provider, utility_water_provider, utility_phone_provider')
        .eq('user_id', user.id)
        .maybeSingle(),
      (supabase as any)
        .from('home_vehicles')
        .select('id, registration_number, make, mot_due_date, mot_status, tax_due_date, tax_status, tax_annual_cost, tax_six_month_cost, tax_monthly_direct_debit_total, tax_monthly_direct_debit_installment, mot_estimated_cost, service_last_date, service_due_date, dvla_synced_at')
        .eq('user_id', user.id)
        .order('sort_order', { ascending: true }),
      (supabase as any)
        .from('renewals')
        .select('type, renewal_date, registration_number, vehicle_label, provider')
        .eq('user_id', user.id)
        .eq('type', 'car-insurance')
        .order('renewal_date', { ascending: true }),
    ]);

    if (profileResult.error) {
      console.error(profileResult.error);
      setHomeProfile(null);
    } else {
      setHomeProfile((profileResult.data as HomeProfileMoneyContext | null) || null);
    }

    if (vehicleResult.error) {
      console.error(vehicleResult.error);
      setHomeVehicles([]);
    } else {
      const storedVehicles = ((vehicleResult.data || []) as HomeVehicleRow[]).map((row) => ({
        id: row.id,
        registrationNumber: row.registration_number,
        make: row.make,
        motDueDate: row.mot_due_date,
        motStatus: row.mot_status,
        taxDueDate: row.tax_due_date,
        taxStatus: row.tax_status,
        taxAnnualCost: row.tax_annual_cost == null ? null : Number(row.tax_annual_cost),
        taxSixMonthCost: row.tax_six_month_cost == null ? null : Number(row.tax_six_month_cost),
        taxMonthlyDirectDebitTotal: row.tax_monthly_direct_debit_total == null ? null : Number(row.tax_monthly_direct_debit_total),
        taxMonthlyDirectDebitInstallment: row.tax_monthly_direct_debit_installment == null ? null : Number(row.tax_monthly_direct_debit_installment),
        motEstimatedCost: row.mot_estimated_cost == null ? null : Number(row.mot_estimated_cost),
        serviceLastDate: row.service_last_date,
        serviceDueDate: row.service_due_date,
        dvlaSyncedAt: row.dvla_synced_at,
      })) satisfies SharedHomeVehicleRecord[];

      setHomeVehicles(storedVehicles);

      void Promise.all(storedVehicles.map((vehicle) => repairStoredHomeVehicle(vehicle)))
        .then((repairedVehicles) => setHomeVehicles(repairedVehicles))
        .catch((error) => console.error(error));
    }

    if (renewalResult.error) {
      console.error(renewalResult.error);
      setVehicleInsuranceRenewals([]);
    } else {
      setVehicleInsuranceRenewals(
        ((renewalResult.data || []) as VehicleInsuranceRenewalRow[]).map((row) => ({
          type: row.type,
          renewalDate: row.renewal_date,
          registrationNumber: row.registration_number,
          vehicleLabel: row.vehicle_label,
          provider: row.provider,
        })),
      );
    }
  }, [user]);

  const fetchMoneySetup = useCallback(async () => {
    if (!user) {
      setStoredHomeBills([]);
      setBankCategoryOverrides([]);
      setBankCategoryRules([]);
      setAmazonOrders([]);
      setAmazonOrderItems([]);
      setAmazonCategoryRules([]);
      return;
    }
    const results = await Promise.all([
      supabase
        .from('home_bill_settings' as any)
        .select('bill_key, enabled, frequency, amount, due_day_of_week, due_day_of_month, annual_due_date, provider_name, provider_account')
        .eq('user_id', user.id),
      supabase
        .from('bank_category_overrides' as any)
        .select('merchant_key, merchant_label, category')
        .eq('user_id', user.id),
      supabase
        .from('bank_category_rules' as any)
        .select('id, scope, transaction_id, merchant_key, merchant_label, pattern_key, category_id')
        .eq('user_id', user.id),
      supabase
        .from('amazon_orders' as any)
        .select('id, order_number, order_date, order_total, currency, source, source_subject, source_sender, source_excerpt, subscribe_and_save, matched_bank_transaction_id, match_confidence, match_reason')
        .eq('user_id', user.id)
        .order('order_date', { ascending: false })
        .limit(24),
      supabase
        .from('amazon_order_items' as any)
        .select('id, amazon_order_id, item_title, item_brand_key, quantity, line_total, budget_category, category_confidence, needs_review, review_reason, is_subscribe_and_save')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false }),
      supabase
        .from('amazon_category_rules' as any)
        .select('id, scope, item_title, brand_key, keyword, budget_category')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false }),
    ]);

    const [billResult, overrideResult, ruleResult, amazonOrdersResult, amazonOrderItemsResult, amazonRuleResult] = results;

    if (billResult.error) {
      console.error(billResult.error);
      setStoredHomeBills([]);
    } else {
      setStoredHomeBills(
        ((billResult.data || []) as HomeBillRow[]).map((row) => ({
          billKey: row.bill_key,
          enabled: row.enabled,
          frequency: row.frequency,
          amount: row.amount == null ? null : Number(row.amount),
          dueDayOfWeek: row.due_day_of_week,
          dueDayOfMonth: row.due_day_of_month,
          annualDueDate: row.annual_due_date,
          providerName: row.provider_name,
          providerAccount: row.provider_account,
        })),
      );
    }

    if (overrideResult.error) {
      console.error(overrideResult.error);
      setBankCategoryOverrides([]);
    } else {
      setBankCategoryOverrides(
        ((overrideResult.data || []) as BankCategoryOverrideRow[]).map((row) => ({
          merchantKey: row.merchant_key,
          merchantLabel: row.merchant_label,
          category: row.category,
        })),
      );
    }

    if (ruleResult.error) {
      console.error(ruleResult.error);
      setBankCategoryRules([]);
    } else {
      setBankCategoryRules(
        ((ruleResult.data || []) as BankCategoryRuleRow[]).map((row) => ({
          id: row.id,
          scope: row.scope,
          transactionId: row.transaction_id,
          merchantKey: row.merchant_key,
          merchantLabel: row.merchant_label,
          patternKey: row.pattern_key,
          categoryId: row.category_id,
        })),
      );
    }

    if (amazonOrdersResult.error) {
      console.error(amazonOrdersResult.error);
      setAmazonOrders([]);
    } else {
      setAmazonOrders((amazonOrdersResult.data || []) as AmazonOrderRow[]);
    }

    if (amazonOrderItemsResult.error) {
      console.error(amazonOrderItemsResult.error);
      setAmazonOrderItems([]);
    } else {
      setAmazonOrderItems((amazonOrderItemsResult.data || []) as AmazonOrderItemRow[]);
    }

    if (amazonRuleResult.error) {
      console.error(amazonRuleResult.error);
      setAmazonCategoryRules([]);
    } else {
      setAmazonCategoryRules((amazonRuleResult.data || []) as AmazonCategoryRuleRow[]);
    }
  }, [user]);

  const fetchBankData = useCallback(async () => {
    if (!user || !bankEnabled) {
      setBankAccounts([]);
      setBankTransactions([]);
      return;
    }

    setBankLoading(true);
    try {
      const oneYearAgo = new Date(Date.now() - (365 * 24 * 60 * 60 * 1000)).toISOString().slice(0, 10);
      const [accountsResult, transactionsResult] = await Promise.all([
        supabase
          .from('bank_accounts' as any)
          .select('*')
          .eq('user_id', user.id)
          .order('last_synced', { ascending: false }),
        fetchStoredBankTransactions(user.id, { fromDate: oneYearAgo }),
      ]);

      if (accountsResult.error) {
        console.error(accountsResult.error);
        setBankAccounts([]);
      } else {
        setBankAccounts((accountsResult.data || []) as BankAccountRow[]);
      }

      setBankTransactions(
        ((transactionsResult || []) as BankTransactionRow[]).map((entry) => ({
          ...entry,
          amount: Number(entry.amount || 0),
        })),
      );
    } catch (error) {
      console.error(error);
      setBankAccounts([]);
      setBankTransactions([]);
    } finally {
      setBankLoading(false);
    }
  }, [bankEnabled, user]);

  const fetchHouseholdBankData = useCallback(async () => {
    if (!user || !bankEnabled || !data.householdShare) {
      setHouseholdBankAccounts([]);
      setHouseholdBankTransactions([]);
      return;
    }

    setHouseholdBankLoading(true);
    const oneYearAgo = new Date(Date.now() - (365 * 24 * 60 * 60 * 1000)).toISOString().slice(0, 10);
    const [accountsResult, transactionsResult] = await Promise.all([
      supabase.rpc('get_household_bank_accounts_summary'),
      supabase.rpc('get_household_bank_transactions_summary', { p_since_date: oneYearAgo }),
    ]);

    if (accountsResult.error) {
      console.error(accountsResult.error);
      setHouseholdBankAccounts([]);
    } else {
      setHouseholdBankAccounts((accountsResult.data || []) as HouseholdBankAccountRow[]);
    }

    if (transactionsResult.error) {
      console.error(transactionsResult.error);
      setHouseholdBankTransactions([]);
    } else {
      setHouseholdBankTransactions(
        ((transactionsResult.data || []) as HouseholdBankTransactionRow[]).map((entry) => ({
          ...entry,
          amount: Number(entry.amount || 0),
        })),
      );
    }

    setHouseholdBankLoading(false);
  }, [bankEnabled, data.householdShare, user]);

  useEffect(() => {
    void fetchImports();
  }, [fetchImports]);

  useEffect(() => {
    void fetchHomeMoneySetup();
  }, [fetchHomeMoneySetup]);

  useEffect(() => {
    void fetchMoneySetup();
  }, [fetchMoneySetup]);

  useEffect(() => {
    void fetchBankData();
  }, [fetchBankData]);

  useEffect(() => {
    void fetchHouseholdBankData();
  }, [fetchHouseholdBankData]);

  useEffect(() => {
    if (!data.householdShare && bankViewMode !== 'mine') {
      setBankViewMode('mine');
    }
  }, [bankViewMode, data.householdShare]);

  useEffect(() => {
    const handleRefresh = () => {
      void fetchHomeMoneySetup();
      void fetchMoneySetup();
    };

    window.addEventListener('adulting:data-changed', handleRefresh);
    return () => window.removeEventListener('adulting:data-changed', handleRefresh);
  }, [fetchHomeMoneySetup, fetchMoneySetup]);

  useEffect(() => {
    let cancelled = false;

    if (!user) {
      setIntegrationSettings(null);
      setIntegrationSettingsLoading(false);
      return;
    }

    setIntegrationSettingsLoading(true);
    void loadIntegrationSettings(user.id)
      .then((settings) => {
        if (!cancelled) {
          setIntegrationSettings(settings);
        }
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) {
          setIntegrationSettings(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIntegrationSettingsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const authError = searchParams.get('error');
    const authErrorDescription = searchParams.get('error_description');
    if (authError) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('error');
      nextParams.delete('error_description');
      nextParams.set('tab', 'budget');
      setSearchParams(nextParams, { replace: true });
      toast.error(authErrorDescription || 'Bank connection was cancelled or could not be completed.');
      return;
    }

    const authCode = searchParams.get('code');
    const legacyRef = searchParams.get('ref');

    if (legacyRef) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('ref');
      nextParams.set('tab', 'budget');
      setSearchParams(nextParams, { replace: true });
      toast.error('This bank connection used the older provider flow. Please reconnect your bank.');
      return;
    }

    if (!authCode && !authState) {
      if (searchParams.has('scope')) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('scope');
        nextParams.set('tab', 'budget');
        setSearchParams(nextParams, { replace: true });
      }
      return;
    }

    if (!user) return;

    const callbackUrl = typeof window !== 'undefined' ? window.location.href : undefined;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('code');
    nextParams.delete('state');
    nextParams.delete('scope');
    nextParams.set('tab', 'budget');
    setSearchParams(nextParams, { replace: true });

    if (!authCode || !authState) {
      toast.error('Bank connection did not return the details Adulting needed. Please try again.');
      return;
    }

    setBankSyncing(true);
    void completeBankConnection(
      authCode,
      authState,
      `${window.location.origin}/money?tab=budget`,
      user.id,
      callbackUrl,
    )
      .then(({ accounts, transactionCount }) => {
        const summary = buildFinanceLedgerBankConnectionSummary({
          accountCount: accounts.length,
          transactionCount,
          institutionNames: accounts.map((entry) => entry.institution_name),
        });
        toast.success(summary.body);
        void fetchBankData();
        void fetchHouseholdBankData();
      })
      .catch((error) => {
        console.error(error);
        toast.error(error instanceof Error ? error.message : 'Could not finish the bank connection.');
      })
      .finally(() => {
        setBankSyncing(false);
      });
  }, [fetchBankData, fetchHouseholdBankData, searchParams, setSearchParams, user]);

  useEffect(() => {
    if (!pendingReviewScrollRef.current || !showRecentBankTransactions) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      reviewTransactionsAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      pendingReviewScrollRef.current = false;
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [showRecentBankTransactions, transactionCategoryFilterId]);

  useEffect(() => {
    if (!pendingCorrectionScrollRef.current || !bankCorrectionTarget) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      correctionAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      pendingCorrectionScrollRef.current = false;
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [bankCorrectionTarget]);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    setStatementParsing(true);

    try {
      const parsed = isPdf
        ? parseStatementPdfText(await extractTextFromPdfFile(file), file.name)
        : parseStatementCsv(await file.text(), file.name);

      if (parsed.transactions.length === 0) {
        toast.error(
          isPdf
            ? 'That PDF did not contain any usable text-based transaction rows. Try a text-based export or CSV instead.'
            : 'That CSV did not contain any usable transaction rows.',
        );
        return;
      }

      setPreviewTransactions(parsed.transactions);
      setPreviewSummary(parsed.summary);
      toast.success(
        buildFinanceLedgerStatementImportReviewMessage(
          resolveSavedStatementImportLedgerSource(parsed.summary.source, parsed.summary.fileName),
          parsed.summary.transactionCount,
          parsed.summary.adapter,
        ),
      );
    } catch (error) {
      console.error(error);
      toast.error(
        isPdf
          ? 'Could not read that PDF statement. Try a text-based export or CSV instead.'
          : 'Could not read that statement file.',
      );
    } finally {
      setStatementParsing(false);
      event.target.value = '';
    }
  };

  const downloadStatementExport = (fileName: string, transactions: StatementTransaction[]) => {
    const safeName = fileName.replace(/\.csv$/i, '');
    downloadCsvFile(`${safeName}-categorised.csv`, buildCategorisedStatementCsv(transactions));
    if (user) {
      void trackProductEvent({
        userId: user.id,
        eventName: 'statement_export_downloaded',
        path: '/money',
        metadata: {
          fileName,
          transactionCount: transactions.length,
        },
      });
    }
    toast.success('Categorised CSV downloaded.');
  };

  const syncImportToBudgetHistory = async ({
    importId,
    transactions,
    periodLabel,
  }: {
    importId: string;
    transactions: StatementTransaction[];
    periodLabel: string | null;
  }) => {
    if (!user) return;

    const outgoingTransactions = transactions.filter((transaction) => transaction.direction === 'out');
    if (outgoingTransactions.length === 0) {
      toast.error('There are no outgoing transactions to sync.');
      return;
    }

    setSyncingImportId(importId);

    const insertedCount = await bulkAddExpenses(
      outgoingTransactions.map((transaction) => ({
        id: crypto.randomUUID(),
        amount: transaction.amount,
        category: transaction.category,
        date: transaction.date,
        description: transaction.description,
      }))
    );

    const skippedCount = outgoingTransactions.length - insertedCount;
    const syncedAt = new Date().toISOString();
    const { error } = await supabase
      .from('statement_imports' as any)
      .update({ expenses_synced_at: syncedAt })
      .eq('id', importId)
      .eq('user_id', user.id);

    if (error) {
      console.error(error);
      toast.error('The import saved, but the sync marker could not be updated.');
    }

    void trackProductEvent({
      userId: user.id,
      eventName: 'statement_expenses_synced',
      path: '/money',
      metadata: {
        importId,
        periodLabel,
        insertedCount,
        skippedCount,
      },
    });

    toast.success(
      insertedCount > 0
        ? `Synced ${insertedCount} spending item${insertedCount === 1 ? '' : 's'} into your budget history.`
        : 'Budget history already matched this statement.'
    );

    setSyncingImportId(null);
    await fetchImports();
  };

  const saveImport = async (syncToBudgetHistory = false) => {
    if (!user || !previewSummary || previewTransactions.length === 0) return;

    setSaveIntent(syncToBudgetHistory ? 'sync' : 'save');
    const { data: savedImport, error } = await supabase
      .from('statement_imports' as any)
      .insert({
        user_id: user.id,
        file_name: previewSummary.fileName,
        period_label: previewSummary.periodLabel,
        transactions: previewTransactions,
        summary: previewSummary,
      })
      .select('*')
      .single();

    if (error) {
      console.error(error);
      toast.error('Could not save that statement import.');
      setSaveIntent(null);
      return;
    }

    toast.success(syncToBudgetHistory ? 'Statement import saved. Syncing spend now…' : 'Statement import saved.');
    void trackProductEvent({
      userId: user.id,
      eventName: 'statement_import_saved',
      path: '/money',
      metadata: {
        periodLabel: previewSummary.periodLabel || null,
        transactionCount: previewSummary.transactionCount,
      },
    });

    if (syncToBudgetHistory && savedImport?.id) {
      await syncImportToBudgetHistory({
        importId: savedImport.id,
        transactions: previewTransactions,
        periodLabel: previewSummary.periodLabel || null,
      });
    }

    setPreviewTransactions([]);
    setPreviewSummary(null);
    setSaveIntent(null);
    await fetchImports();
  };

  const applyBudgetSuggestion = (summary: StatementImportSummary) => {
    const nextLimits = data.budgetLimits.map((limit) => ({
      category: limit.category,
      limit: (summary.suggestedBudget[limit.category as keyof typeof summary.suggestedBudget] || 0) > 0
        ? summary.suggestedBudget[limit.category as keyof typeof summary.suggestedBudget]
        : limit.limit,
    }));

    updateBudgetLimits(nextLimits);
    if (user) {
      void trackProductEvent({
        userId: user.id,
        eventName: 'statement_budget_applied',
        path: '/money',
        metadata: {
          categoriesUpdated: Object.values(summary.suggestedBudget).filter((value) => value > 0).length,
        },
      });
    }
    toast.success('Budget limits updated from your statement pattern.');
  };

  const previewOutgoings = previewTransactions.filter((transaction) => transaction.direction === 'out');
  const latestImport = imports[0];
  const previewImportSource = previewSummary
    ? resolveSavedStatementImportLedgerSource(previewSummary.source, previewSummary.fileName)
    : null;
  const previewImportAdapter = previewSummary?.adapter || null;
  const previewImportReview = previewSummary?.review || null;
  const previewInsights = previewSummary ? getStatementInsights(previewSummary, previewTransactions) : null;
  const mineBusinessBankAccounts = useMemo(
    () => bankAccounts.filter((account) => isFinanceBankAccountBusiness(account.account_role)),
    [bankAccounts],
  );
  const visibleMineBankAccounts = useMemo(
    () => bankAccounts.filter((account) => shouldIncludeFinanceBankAccountInMoneyView(account.account_role)),
    [bankAccounts],
  );
  const visibleMineBankAccountIds = useMemo(
    () => new Set(visibleMineBankAccounts.map((account) => account.id)),
    [visibleMineBankAccounts],
  );
  const mineBankAccounts = useMemo(
    () =>
      visibleMineBankAccounts.map((account) => ({
        id: account.id,
        owner_label: 'You',
        institution_name: account.institution_name,
        name: account.name,
        account_role: account.account_role == null ? null : account.account_role as FinanceBankAccountRole,
        iban: account.iban,
        currency: account.currency,
        balance: account.balance == null ? null : Number(account.balance),
        last_synced: account.last_synced,
      })) satisfies BankAccountSummary[],
    [visibleMineBankAccounts],
  );
  const mineBankTransactions = useMemo(
    () =>
      bankTransactions
        .filter((entry) => visibleMineBankAccountIds.has(entry.account_id))
        .map((entry) => ({
          id: entry.id,
          owner_label: 'You',
          account_id: entry.account_id,
          booking_date: entry.booking_date,
          amount: Number(entry.amount || 0),
          currency: entry.currency,
          description: entry.description,
          creditor_name: entry.creditor_name,
          debtor_name: entry.debtor_name,
        })) satisfies BankTransactionSummary[],
    [bankTransactions, visibleMineBankAccountIds],
  );
  const householdBankAccountSummary = useMemo(
    () =>
      householdBankAccounts.map((account) => ({
        id: account.account_id,
        owner_label: account.owner_label,
        institution_name: account.institution_name,
        name: account.account_name,
        account_role: account.account_role == null ? null : account.account_role as FinanceBankAccountRole,
        iban: account.iban,
        currency: account.currency,
        balance: account.balance == null ? null : Number(account.balance),
        last_synced: account.last_synced,
      })) satisfies BankAccountSummary[],
    [householdBankAccounts],
  );
  const householdBankTransactionSummary = useMemo(
    () =>
      householdBankTransactions.map((entry) => ({
        id: entry.transaction_id,
        owner_label: entry.owner_label,
        account_id: entry.account_id,
        booking_date: entry.booking_date,
        amount: Number(entry.amount || 0),
        currency: entry.currency,
        description: entry.description,
        creditor_name: entry.creditor_name,
        debtor_name: entry.debtor_name,
      })) satisfies BankTransactionSummary[],
    [householdBankTransactions],
  );
  const selectedBankAccounts = bankViewMode === 'household' ? householdBankAccountSummary : mineBankAccounts;
  const selectedBankTransactions = bankViewMode === 'household' ? householdBankTransactionSummary : mineBankTransactions;
  const selectedBankLoading = bankViewMode === 'household' ? householdBankLoading : bankLoading;
  const mineBusinessBankAccountNote = useMemo(() => {
    if (!mineBusinessBankAccounts.length) {
      return null;
    }

    return mineBusinessBankAccounts.length === 1
      ? '1 self-employed business account is being kept out of this personal money view. Change it in My Settings if it should count as personal or shared household money instead.'
      : `${mineBusinessBankAccounts.length} self-employed business accounts are being kept out of this personal money view. Change them in My Settings if any should count as personal or shared household money instead.`;
  }, [mineBusinessBankAccounts.length]);
  const hasOnlyBusinessBankAccountsInMineView =
    bankViewMode === 'mine' && selectedBankAccounts.length === 0 && mineBusinessBankAccounts.length > 0;
  const amazonOrderItemsByOrderId = useMemo(() => {
    const map = new Map<string, AmazonOrderItemRow[]>();
    amazonOrderItems.forEach((item) => {
      const current = map.get(item.amazon_order_id) || [];
      current.push(item);
      map.set(item.amazon_order_id, current);
    });
    return map;
  }, [amazonOrderItems]);
  const amazonOrderRecords = useMemo(
    () =>
      amazonOrders.map((order) => ({
        id: order.id,
        orderNumber: order.order_number,
        orderDate: order.order_date,
        orderTotal: Number(order.order_total || 0),
        currency: order.currency || 'GBP',
        source: (order.source === 'pasted_text' || order.source === 'historical_upload' ? order.source : 'forwarded_email') as AmazonOrderRecord['source'],
        subject: order.source_subject,
        senderEmail: order.source_sender,
        excerpt: order.source_excerpt,
        subscribeAndSave: order.subscribe_and_save,
        matchedBankTransactionId: order.matched_bank_transaction_id,
        matchConfidence: order.match_confidence,
        matchReason: order.match_reason,
        items: (amazonOrderItemsByOrderId.get(order.id) || []).map((item) => ({
          id: item.id,
          title: item.item_title,
          brandKey: item.item_brand_key,
          quantity: item.quantity,
          lineTotal: Number(item.line_total || 0),
          budgetCategory: item.budget_category as AmazonOrderRecord['items'][number]['budgetCategory'],
          confidence: item.category_confidence,
          needsReview: item.needs_review,
          reviewReason: item.review_reason,
          isSubscribeAndSave: item.is_subscribe_and_save,
        })),
      })) satisfies AmazonOrderRecord[],
    [amazonOrderItemsByOrderId, amazonOrders],
  );
  const amazonSummary = useMemo(() => buildAmazonOrderSummary(amazonOrderRecords), [amazonOrderRecords]);
  const bankFeedInputsWithAmazon = useMemo(
    () => mergeBankFeedInputsWithAmazonOrders(
      selectedBankTransactions.map((entry) => ({
        transactionId: entry.id,
        accountId: entry.account_id,
        bookingDate: entry.booking_date,
        amount: Number(entry.amount || 0),
        currency: entry.currency || 'GBP',
        description: entry.description,
        merchantName: entry.creditor_name || entry.debtor_name,
      })),
      amazonOrderRecords,
    ),
    [amazonOrderRecords, selectedBankTransactions],
  );
  const amazonReviewItems = useMemo<AmazonReviewItem[]>(
    () => amazonOrderRecords.flatMap((order) =>
      order.items
        .filter((item) => item.needsReview)
        .map((item) => ({
          orderNumber: order.orderNumber,
          orderId: order.id || null,
          itemId: item.id || null,
          title: item.title,
          brandKey: item.brandKey,
          lineTotal: item.lineTotal,
          reviewReason: item.reviewReason,
          isSubscribeAndSave: item.isSubscribeAndSave,
          currentCategory: item.budgetCategory,
        })),
    ),
    [amazonOrderRecords],
  );
  const amazonRuleModels = useMemo<AmazonCategoryRule[]>(
    () =>
      amazonCategoryRules.map((rule) => ({
        scope: rule.scope,
        itemTitle: rule.item_title,
        brandKey: rule.brand_key,
        keyword: rule.keyword,
        budgetCategory: rule.budget_category as AmazonCategoryRule['budgetCategory'],
      })),
    [amazonCategoryRules],
  );
  const [amazonCorrectionSelections, setAmazonCorrectionSelections] = useState<Record<string, BudgetCategory>>({});
  const getAmazonReviewItemKey = useCallback(
    (item: AmazonReviewItem) => item.itemId || `${item.orderNumber}:${item.title}`,
    [],
  );
  const selectedLatestBankSync = selectedBankAccounts[0]?.last_synced || null;
  const selectedBankFeedInputs = useMemo(
    () =>
      bankViewMode === 'household'
        ? selectedBankTransactions.map((entry) => ({
          transactionId: entry.id,
          accountId: entry.account_id,
          bookingDate: entry.booking_date,
          amount: Number(entry.amount || 0),
          currency: entry.currency || 'GBP',
          description: entry.description,
          merchantName: entry.creditor_name || entry.debtor_name,
        }))
        : bankFeedInputsWithAmazon,
    [bankFeedInputsWithAmazon, bankViewMode, selectedBankTransactions],
  );
  const mineBankFeedInputs = useMemo(
    () => mergeBankFeedInputsWithAmazonOrders(
      mineBankTransactions.map((entry) => ({
        transactionId: entry.id,
        accountId: entry.account_id,
        bookingDate: entry.booking_date,
        amount: Number(entry.amount || 0),
        currency: entry.currency || 'GBP',
        description: entry.description,
        merchantName: entry.creditor_name || entry.debtor_name,
      })),
      amazonOrderRecords,
    ),
    [amazonOrderRecords, mineBankTransactions],
  );
  const baseHomeBills = useMemo(
    () => mergeSharedHomeBillRecords(buildMoneyHomeBillDefaults(homeProfile), storedHomeBills),
    [homeProfile, storedHomeBills],
  );
  const mineHomeBillInferences = useMemo(
    () =>
      buildSharedHomeBillInferences(
        mineBankTransactions
          .filter((entry) => entry.amount < 0)
          .map((entry) => ({
            bookingDate: entry.booking_date,
            amount: Number(entry.amount || 0),
            description: entry.description,
            creditorName: entry.creditor_name,
          })),
        baseHomeBills,
      ),
    [baseHomeBills, mineBankTransactions],
  );
  const displayHomeBills = useMemo(
    () => applySharedHomeBillInferences(baseHomeBills, mineHomeBillInferences),
    [baseHomeBills, mineHomeBillInferences],
  );
  const normalizedVehicleRegistration = normalizeVehicleRegistration(homeProfile?.vehicle_registration_number || '');
  const vehicleCards = useMemo(() => {
    if (homeVehicles.length === 0) {
      if (!homeProfile?.has_vehicle || !normalizedVehicleRegistration) return [];
      return [{
        registrationNumber: normalizedVehicleRegistration,
        make: null,
        motDueDate: homeProfile.vehicle_mot_due_date || null,
        motStatus: null,
        taxDueDate: homeProfile.vehicle_tax_due_date || null,
        taxStatus: null,
        taxAnnualCost: null,
        taxSixMonthCost: null,
        taxMonthlyDirectDebitTotal: null,
        taxMonthlyDirectDebitInstallment: null,
        motEstimatedCost: null,
        serviceLastDate: null,
        serviceDueDate: null,
        dvlaSyncedAt: null,
      }] satisfies SharedHomeVehicleRecord[];
    }

    return homeVehicles;
  }, [homeProfile, homeVehicles, normalizedVehicleRegistration]);
  const billTransferCandidates = useMemo(
    () => buildSharedHomeBillTransferCandidates(baseHomeBills, mineHomeBillInferences),
    [baseHomeBills, mineHomeBillInferences],
  );
  const billMerchantKeys = useMemo(
    () =>
      new Set(
        [...mineHomeBillInferences.values()]
          .map((entry) => entry.merchantKey)
          .filter((entry): entry is string => Boolean(entry)),
      ),
    [mineHomeBillInferences],
  );
  const variableBankFeedInputs = useMemo(
    () =>
      mineBankFeedInputs.filter((entry) => {
        const merchantKey = normaliseMoneyMerchant(entry.merchantName || entry.description || '');
        return !merchantKey || !billMerchantKeys.has(merchantKey);
      }),
    [billMerchantKeys, mineBankFeedInputs],
  );
  const reviewCandidates = useMemo(
    () =>
      buildBankFeedMerchantReviewCandidates(mineBankFeedInputs, {
        categoryRules: bankCategoryRules,
        categoryOverrides: bankCategoryOverrides,
      })
        .filter((entry) => !billMerchantKeys.has(entry.merchantKey)),
    [bankCategoryOverrides, bankCategoryRules, billMerchantKeys, mineBankFeedInputs],
  );
  const budgetBuilderBillRows = useMemo(
    () =>
      displayHomeBills
        .filter((bill) => bill.enabled)
        .map((bill) => ({
          ...bill,
          label: getSharedHomeBillLabel(bill.billKey),
          monthlyAmount: getSharedHomeBillMonthlyAmount(bill),
        }))
        .sort((left, right) => (right.monthlyAmount || 0) - (left.monthlyAmount || 0)),
    [displayHomeBills],
  );
  const budgetBuilderBillMonthlyTotal = useMemo(
    () =>
      budgetBuilderBillRows.reduce((sum, bill) => sum + (bill.monthlyAmount || 0), 0),
    [budgetBuilderBillRows],
  );
  const bankAnalysis = useMemo(
    () =>
      buildBankFeedAnalysis(selectedBankFeedInputs, {
        categoryRules: bankViewMode === 'mine' ? bankCategoryRules : [],
        categoryOverrides: bankViewMode === 'mine' ? bankCategoryOverrides : [],
      }),
    [bankCategoryOverrides, bankCategoryRules, bankViewMode, selectedBankFeedInputs],
  );
  const bankBudgetSignals = useMemo(
    () =>
      buildBankFeedBudgetSignals(
        variableBankFeedInputs,
        data.budgetLimits,
        moneyHouseholdBenchmarkInput,
        {
          categoryRules: bankCategoryRules,
          categoryOverrides: bankCategoryOverrides,
        },
      ),
    [bankCategoryOverrides, bankCategoryRules, data.budgetLimits, moneyHouseholdBenchmarkInput, variableBankFeedInputs],
  );
  const bankFinanceViews = useMemo(
    () =>
      buildBankFinanceAnalysisViews(bankAnalysis, {
        ...moneyHouseholdBenchmarkInput,
        annualIncome: data.profile.annualIncome || null,
      }),
    [bankAnalysis, data.profile.annualIncome, moneyHouseholdBenchmarkInput],
  );
  const budgetBreakdown = bankAnalysis.budgetBreakdown;
  const petSpendSummary = useMemo(
    () =>
      buildPetSpendSummary({
        expenses: data.expenses,
        recurringOutgoings: bankAnalysis.recurringOutgoings,
      }),
    [bankAnalysis.recurringOutgoings, data.expenses],
  );
  const petBankRecurringOutgoings = useMemo(
    () => filterPetRecurringOutgoings(bankAnalysis.recurringOutgoings),
    [bankAnalysis.recurringOutgoings],
  );
  const financeReviewInbox = useMemo(
    () =>
      buildFinanceReviewInboxSummary({
        bankReviewCount: budgetBreakdown.reviewQueue.length,
        merchantReviewCount: bankViewMode === 'mine' ? reviewCandidates.length : 0,
        amazonReviewCount: bankViewMode === 'mine' ? amazonReviewItems.length : 0,
        statementImportNeedsReview: Boolean(previewImportReview),
        statementImportFallbackRecommended: previewImportReview?.state === 'fallback-recommended',
      }),
    [
      amazonReviewItems.length,
      bankViewMode,
      budgetBreakdown.reviewQueue.length,
      previewImportReview,
      reviewCandidates.length,
    ],
  );
  const bankRecurringTrackerItems = useMemo(
    () =>
      budgetBreakdown.recurringCommitments.filter(
        (item) => !recurringTrackerMerchantKeys.has(normaliseMoneyMerchant(item.merchant)),
      ),
    [budgetBreakdown.recurringCommitments, recurringTrackerMerchantKeys],
  );
  const recentNormalisedTransactions = useMemo(
    () =>
      [...bankAnalysis.normalisedTransactions]
        .sort((left, right) => right.bookingDate.localeCompare(left.bookingDate))
        .slice(0, 12),
    [bankAnalysis.normalisedTransactions],
  );
  const filteredNormalisedTransactions = useMemo(
    () =>
      transactionCategoryFilterId
        ? recentNormalisedTransactions.filter((entry) => entry.categoryId === transactionCategoryFilterId)
        : recentNormalisedTransactions,
    [recentNormalisedTransactions, transactionCategoryFilterId],
  );
  const hasTransactionCategoryFilter = Boolean(transactionCategoryFilterId);
  const handleReviewMatchingTransactions = useCallback((categoryId: BankBudgetCategoryId) => {
    pendingReviewScrollRef.current = true;
    setTransactionCategoryFilterId(categoryId);
    setShowRecentBankTransactions(true);
  }, []);
  const budgetBuilderCategoryRows = useMemo(
    () =>
      bankBudgetSignals.suggestedBudgetRows.filter(
        (entry) => entry.suggestedLimit > 0 || entry.currentMonthSpend > 0 || (entry.currentLimit || 0) > 0,
      ),
    [bankBudgetSignals.suggestedBudgetRows],
  );
  const connectedBankBudgetChanges = useMemo(
    () =>
      bankBudgetSignals.suggestedBudgetRows
        .filter((entry) => entry.suggestedLimit > 0)
        .filter((entry) => {
          if (entry.currentLimit == null) return true;
          return Math.abs(entry.currentLimit - entry.suggestedLimit) >= 1;
        }),
    [bankBudgetSignals.suggestedBudgetRows],
  );

  const latestBankSync = mineBankAccounts[0]?.last_synced || null;
  const autoBudgetEnabled = integrationSettings?.bank_budget_auto_refresh_enabled ?? false;

  const buildCorrectionTargetFromReviewItem = useCallback(
    (item: BankBudgetReviewItem): BankCorrectionTarget => ({
      key: `review-${item.transactionId || item.patternKey || item.merchantKey}-${item.bookingDate}`,
      transactionId: item.transactionId,
      merchantKey: item.merchantKey,
      merchantLabel: item.merchantLabel,
      patternKey: item.patternKey,
      rawLabel: item.rawLabel,
      bookingDate: item.bookingDate,
      amount: item.amount,
      currency: item.currency,
      suggestedCategoryId: item.suggestedCategoryId,
    }),
    [],
  );

  const buildCorrectionTargetFromReviewCandidate = useCallback(
    (candidate: (typeof reviewCandidates)[number]): BankCorrectionTarget => ({
      key: `candidate-${candidate.merchantKey}`,
      transactionId: null,
      merchantKey: candidate.merchantKey,
      merchantLabel: candidate.merchantLabel,
      patternKey: '',
      rawLabel: candidate.merchantLabel,
      bookingDate: candidate.latestBookingDate,
      amount: candidate.averageAmount,
      currency: bankAnalysis.primaryCurrency,
      suggestedCategoryId: mapBudgetCategoryToBankBreakdownCategoryId(candidate.suggestedCategory),
    }),
    [bankAnalysis.primaryCurrency],
  );

  const buildCorrectionTargetFromTransaction = useCallback(
    (item: BankNormalisedTransaction): BankCorrectionTarget => ({
      key: `transaction-${item.transactionId || item.patternKey || item.merchantKey}-${item.bookingDate}`,
      transactionId: item.transactionId,
      merchantKey: item.merchantKey,
      merchantLabel: item.merchantLabel,
      patternKey: item.patternKey,
      rawLabel: item.rawLabel,
      bookingDate: item.bookingDate,
      amount: item.absoluteAmount,
      currency: item.currency,
      suggestedCategoryId: item.categoryId,
    }),
    [],
  );
  const breakdownMetricLabel = budgetBreakdownView === 'monthly' ? 'Current month' : 'Annual total';
  const getBreakdownRowAmount = useCallback(
    (row: (typeof budgetBreakdown.rows)[number]) =>
      budgetBreakdownView === 'monthly' ? row.currentMonthAmount : row.annualTotal,
    [budgetBreakdownView],
  );
  const getBreakdownRowMeta = useCallback(
    (row: (typeof budgetBreakdown.rows)[number]) =>
      budgetBreakdownView === 'monthly'
        ? `Rolling 12-month average ${formatSterlingAmount(row.rollingMonthlyAverage, bankAnalysis.primaryCurrency)}`
        : `Monthly equivalent ${formatSterlingAmount(row.annualisedMonthlyEquivalent, bankAnalysis.primaryCurrency)} · ${row.annualCount} transaction${row.annualCount === 1 ? '' : 's'}`,
    [bankAnalysis.primaryCurrency, budgetBreakdownView],
  );

  const persistIntegrationSettings = useCallback(
    async (patch: Partial<IntegrationSettingsRecord>) => {
      if (!user) return null;
      const saved = await saveIntegrationSettings(user.id, patch);
      setIntegrationSettings(saved);
      return saved;
    },
    [user],
  );

  const applyConnectedBankBudgetSuggestion = useCallback(async (mode: 'manual' | 'automatic' = 'manual') => {
    if (!connectedBankBudgetChanges.length) return;

    await updateBudgetLimits(mergeSuggestedBudgetLimits(data.budgetLimits, connectedBankBudgetChanges));
    if (latestBankSync) {
      await persistIntegrationSettings({
        bank_budget_auto_last_applied_sync_at: latestBankSync,
      });
    }
    if (user) {
      void trackProductEvent({
        userId: user.id,
        eventName: 'connected_bank_budget_applied',
        path: '/money',
        metadata: {
          categoriesUpdated: connectedBankBudgetChanges.length,
          monthsCovered: bankAnalysis.monthsCovered,
          mode,
        },
      });
    }
    if (mode === 'manual') {
      toast.success('Budget limits updated from your connected bank history.');
    }
  }, [
    bankAnalysis.monthsCovered,
    connectedBankBudgetChanges,
    data.budgetLimits,
    latestBankSync,
    persistIntegrationSettings,
    updateBudgetLimits,
    user,
  ]);

  const openBankCorrection = useCallback((target: BankCorrectionTarget) => {
    pendingCorrectionScrollRef.current = true;
    setBankCorrectionTarget(target);
    setBankCorrectionCategoryId(target.suggestedCategoryId || 'other_discretionary');
  }, []);

  const scrollToImportPreview = useCallback(() => {
    importPreviewAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const closeBankCorrection = useCallback(() => {
    setBankCorrectionTarget(null);
    setSavingBankCorrectionScope(null);
  }, []);

  const saveBankCategoryRule = useCallback(async (
    scope: BankCategoryRuleScope,
    target: BankCorrectionTarget,
    categoryId: BankBudgetCategoryId,
  ) => {
    if (!user) return false;

    const client = supabase;
    setSavingBankCorrectionScope(scope);

    let matchQuery = client
      .from('bank_category_rules' as any)
      .select('id')
      .eq('user_id', user.id)
      .eq('scope', scope);

    if (scope === 'transaction') {
      if (!target.transactionId) {
        toast.error('Adulting cannot save a one-off rule for this item because there is no transaction id.');
        setSavingBankCorrectionScope(null);
        return false;
      }
      matchQuery = matchQuery.eq('transaction_id', target.transactionId);
    } else if (scope === 'merchant') {
      matchQuery = matchQuery.eq('merchant_key', target.merchantKey);
    } else {
      matchQuery = matchQuery.eq('pattern_key', target.patternKey);
    }

    const { data: existingRule, error: existingRuleError } = await matchQuery.maybeSingle();
    if (existingRuleError) {
      console.error(existingRuleError);
      toast.error('Adulting could not check your saved bank rules right now.');
      setSavingBankCorrectionScope(null);
      return false;
    }

    const payload = {
      user_id: user.id,
      scope,
      transaction_id: scope === 'transaction' ? target.transactionId : null,
      merchant_key: scope === 'merchant' ? target.merchantKey : null,
      merchant_label: scope === 'merchant' ? target.merchantLabel : null,
      pattern_key: scope === 'pattern' ? target.patternKey : null,
      category_id: categoryId,
    };

    const { error: saveRuleError } = existingRule?.id
      ? await client
        .from('bank_category_rules' as any)
        .update(payload)
        .eq('id', existingRule.id)
        .eq('user_id', user.id)
      : await client
        .from('bank_category_rules' as any)
        .insert(payload);

    if (saveRuleError) {
      console.error(saveRuleError);
      toast.error('Adulting could not save that bank rule right now.');
      setSavingBankCorrectionScope(null);
      return false;
    }

    if (scope === 'merchant') {
      const legacyCategory = mapBreakdownCategoryIdToLegacyCategory(categoryId);
      const { error: overrideError } = await client
        .from('bank_category_overrides' as any)
        .upsert(
          [{
            user_id: user.id,
            merchant_key: target.merchantKey,
            merchant_label: target.merchantLabel,
            category: legacyCategory,
          }],
          { onConflict: 'user_id,merchant_key' },
        );

      if (overrideError) {
        console.error(overrideError);
        toast.error('Adulting saved the new rule, but could not mirror the legacy merchant override.');
        setSavingBankCorrectionScope(null);
        await fetchMoneySetup();
        return false;
      }
    }

    await fetchMoneySetup();
    setSavingBankCorrectionScope(null);
    return true;
  }, [fetchMoneySetup, user]);

  const handleSaveBankCorrection = useCallback(async (scope: BankCategoryRuleScope) => {
    if (!bankCorrectionTarget) return;
    const saved = await saveBankCategoryRule(scope, bankCorrectionTarget, bankCorrectionCategoryId);
    if (!saved) return;

    const scopeLabel =
      scope === 'transaction'
        ? 'this transaction only'
        : scope === 'merchant'
          ? 'this merchant going forward'
          : 'this exact pattern going forward';
    toast.success(`Adulting will now use ${formatBankBudgetCategoryLabel(bankCorrectionCategoryId)} for ${scopeLabel}.`);
    closeBankCorrection();
  }, [bankCorrectionCategoryId, bankCorrectionTarget, closeBankCorrection, saveBankCategoryRule]);

  const handleAmazonImportPaste = useCallback(async () => {
    if (!user || !amazonPasteText.trim()) return;

    setAmazonSaving(true);
    try {
      const parsed = parseAmazonOrderText(amazonPasteText, { source: 'pasted_text' });
      if (!parsed) {
        toast.error('That text did not look like an Amazon order email or order summary.');
        return;
      }

      const orderRecord = buildAmazonOrderRecord(parsed, amazonRuleModels, {
        excerpt: amazonPasteText.slice(0, 1500),
        source: 'pasted_text',
      });
      const match = reconcileAmazonOrdersToBankTransactions([orderRecord], bankTransactions)[0] || {
        bankTransactionId: null,
        matchConfidence: 'low' as const,
        matchReason: null,
      };

      const { data: savedOrder, error: orderError } = await supabase
        .from('amazon_orders' as any)
        .upsert(
          [{
            user_id: user.id,
            order_number: orderRecord.orderNumber,
            order_date: orderRecord.orderDate,
            order_total: orderRecord.orderTotal,
            currency: orderRecord.currency,
            source: orderRecord.source,
            source_subject: orderRecord.subject,
            source_sender: orderRecord.senderEmail,
            source_excerpt: orderRecord.excerpt,
            subscribe_and_save: orderRecord.subscribeAndSave,
            matched_bank_transaction_id: match.bankTransactionId,
            match_confidence: match.matchConfidence,
            match_reason: match.matchReason,
          }],
          { onConflict: 'user_id,order_number' },
        )
        .select('id')
        .single();

      if (orderError || !savedOrder?.id) {
        throw new Error(orderError?.message || 'Could not save the Amazon order.');
      }

      const { error: deleteItemsError } = await supabase
        .from('amazon_order_items' as any)
        .delete()
        .eq('amazon_order_id', savedOrder.id);

      if (deleteItemsError) {
        throw new Error(deleteItemsError.message);
      }

      const itemRows = orderRecord.items.map((item) => ({
        user_id: user.id,
        amazon_order_id: savedOrder.id,
        item_title: item.title,
        item_brand_key: item.brandKey,
        quantity: item.quantity,
        line_total: item.lineTotal,
        budget_category: item.budgetCategory,
        category_confidence: item.confidence,
        needs_review: item.needsReview,
        review_reason: item.needsReview ? 'low-confidence' : item.reviewReason,
        is_subscribe_and_save: item.isSubscribeAndSave,
      }));

      const { error: itemError } = await supabase.from('amazon_order_items' as any).insert(itemRows);
      if (itemError) {
        throw new Error(itemError.message);
      }

      setAmazonPasteText('');
      await fetchMoneySetup();
      toast.success(`Saved Amazon order ${orderRecord.orderNumber}.`);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Could not save that Amazon order.');
    } finally {
      setAmazonSaving(false);
    }
  }, [amazonPasteText, amazonRuleModels, bankTransactions, fetchMoneySetup, user]);

  const handleAmazonItemOnlyCorrection = useCallback(async (item: AmazonReviewItem) => {
    if (!user) return;

    const itemKey = getAmazonReviewItemKey(item);
    const category = amazonCorrectionSelections[itemKey] || item.currentCategory || AMAZON_CATEGORY_TARGETS[0].budgetCategory;

    setAmazonSaving(true);
    try {
      if (!item.itemId) {
        throw new Error('Adulting could not find the saved Amazon item to update.');
      }

      const { error: itemError } = await supabase
        .from('amazon_order_items' as any)
        .update({
          budget_category: category,
          category_confidence: 'high',
          needs_review: false,
          review_reason: null,
        })
        .eq('id', item.itemId)
        .eq('user_id', user.id);

      if (itemError) {
        throw new Error(itemError.message);
      }

      await fetchMoneySetup();
      toast.success(`${item.title} is now set to ${category}.`);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Could not save that Amazon item.');
    } finally {
      setAmazonSaving(false);
    }
  }, [amazonCorrectionSelections, fetchMoneySetup, getAmazonReviewItemKey, user]);

  const handleAmazonCorrection = useCallback(async (
    item: AmazonReviewItem,
    scope: AmazonCategoryRuleScope,
  ) => {
    if (!user) return;

    const itemKey = getAmazonReviewItemKey(item);
    const category = amazonCorrectionSelections[itemKey] || item.currentCategory || AMAZON_CATEGORY_TARGETS[0].budgetCategory;
    const ruleValue =
      scope === 'item'
        ? item.title
        : scope === 'brand'
          ? item.brandKey || item.title
          : item.title;

    setAmazonSaving(true);
    try {
      const ruleInput = toAmazonCategoryRuleInput(category as AmazonCategoryRule['budgetCategory'], scope, ruleValue);
      const targetColumn =
        scope === 'item'
          ? 'item_title'
          : scope === 'brand'
            ? 'brand_key'
            : 'keyword';

      const { data: existingRule, error: existingRuleError } = await supabase
        .from('amazon_category_rules' as any)
        .select('id')
        .eq('user_id', user.id)
        .eq('scope', scope)
        .eq(targetColumn, ruleValue)
        .maybeSingle();

      if (existingRuleError) {
        throw new Error(existingRuleError.message);
      }

      const ruleMutation = existingRule?.id
        ? await supabase
          .from('amazon_category_rules' as any)
          .update(ruleInput)
          .eq('id', existingRule.id)
          .eq('user_id', user.id)
        : await supabase
          .from('amazon_category_rules' as any)
          .insert([{ user_id: user.id, ...ruleInput }]);

      if (ruleMutation.error) {
        throw new Error(ruleMutation.error.message);
      }

      if (item.itemId) {
        const { error: itemError } = await supabase
          .from('amazon_order_items' as any)
          .update({
            budget_category: category,
            category_confidence: 'high',
            needs_review: false,
            review_reason: null,
          })
          .eq('id', item.itemId)
          .eq('user_id', user.id);

        if (itemError) {
          throw new Error(itemError.message);
        }
      }

      await fetchMoneySetup();
      toast.success(`${item.title} will now sit under ${category}.`);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Could not save that Amazon correction.');
    } finally {
      setAmazonSaving(false);
    }
  }, [amazonCorrectionSelections, fetchMoneySetup, getAmazonReviewItemKey, user]);

  const handleApplyBillTransfer = useCallback(async (billKey: SharedHomeBillKey) => {
    if (!user) return;

    const candidate = billTransferCandidates.find((entry) => entry.billKey === billKey);
    if (!candidate) return;

    setApplyingHomeBillKey(billKey);
    const bill = candidate.suggestedBill;
    const { error } = await supabase
      .from('home_bill_settings' as any)
      .upsert(
        [{
          user_id: user.id,
          bill_key: bill.billKey,
          enabled: bill.enabled,
          frequency: bill.frequency,
          amount: bill.amount,
          due_day_of_week: bill.frequency === 'weekly' ? bill.dueDayOfWeek : null,
          due_day_of_month: bill.frequency === 'monthly' ? bill.dueDayOfMonth : null,
          annual_due_date: bill.frequency === 'annual' ? bill.annualDueDate : null,
          provider_name: bill.providerName?.trim() || null,
          provider_account: bill.providerAccount?.trim() || null,
        }],
        { onConflict: 'user_id,bill_key' },
      );

    if (error) {
      console.error(error);
      toast.error('Could not send that bill into Home right now.');
      setApplyingHomeBillKey(null);
      return;
    }

    await fetchMoneySetup();
    window.dispatchEvent(new CustomEvent('adulting:data-changed'));
    setApplyingHomeBillKey(null);
    toast.success(`${candidate.label} is now saved in Home bills.`);
  }, [billTransferCandidates, fetchMoneySetup, user]);

  const handleToggleAutoBudgetRefresh = useCallback(async () => {
    if (!user || autoBudgetSaving) return;
    const nextEnabled = !autoBudgetEnabled;
    setAutoBudgetSaving(true);
    try {
      await persistIntegrationSettings({
        bank_budget_auto_refresh_enabled: nextEnabled,
      });
      toast.success(
        nextEnabled
          ? 'Automatic connected-bank budget refresh is on.'
          : 'Automatic connected-bank budget refresh is off.',
      );
    } catch (error) {
      console.error(error);
      toast.error('Could not update automatic connected-bank budget refresh right now.');
    } finally {
      setAutoBudgetSaving(false);
    }
  }, [autoBudgetEnabled, autoBudgetSaving, persistIntegrationSettings, user]);

  useEffect(() => {
    if (
      !integrationSettings ||
      integrationSettingsLoading ||
      bankLoading ||
      autoBudgetApplyingRef.current ||
      !shouldAutoRefreshConnectedBankBudget({
        enabled: autoBudgetEnabled,
        latestBankSyncAt: latestBankSync,
        lastAppliedSyncAt: integrationSettings.bank_budget_auto_last_applied_sync_at,
        suggestedBudgetRowCount: connectedBankBudgetChanges.length,
      })
    ) {
      return;
    }

    autoBudgetApplyingRef.current = true;
    void applyConnectedBankBudgetSuggestion('automatic')
      .catch((error) => {
        console.error(error);
        toast.error('Adulting could not refresh your budget automatically from the latest bank sync.');
      })
      .finally(() => {
        autoBudgetApplyingRef.current = false;
      });
  }, [
    applyConnectedBankBudgetSuggestion,
    autoBudgetEnabled,
    bankLoading,
    connectedBankBudgetChanges.length,
    integrationSettings,
    integrationSettingsLoading,
    latestBankSync,
  ]);

  const previewBudgetBenchmark = useMemo(
    () =>
      previewInsights
        ? buildMoneyBudgetBenchmarkRows(previewInsights.suggestedBudget, moneyHouseholdBenchmarkInput)
        : { benchmark: null, rows: [] },
    [moneyHouseholdBenchmarkInput, previewInsights],
  );

  return (
    <div className="space-y-4">
      <AppCard>
        <div className="flex items-start gap-3">
          <ReceiptText size={18} className="text-primary mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <AppSectionHeader
              title="Statement upload"
              subtitle="Upload a PDF or CSV export from your bank to get automatic category suggestions, a spending snapshot, and a better starting budget."
            />
          </div>
        </div>
        <label className="mt-4 block">
          <span className="sr-only">Upload statement PDF or CSV</span>
          <FileInput
            type="file"
            accept=".csv,text/csv,.pdf,application/pdf"
            onChange={handleFileChange}
            disabled={statementParsing}
            className="block w-full"
          />
        </label>
        {statementParsing ? (
          <p className="text-[11px] text-muted-foreground mt-2">Reading your statement now…</p>
        ) : null}
        <p className="text-[11px] text-muted-foreground mt-2">
          Beta note: PDF import works best for text-based bank exports. Scanned or image-only PDFs may still need CSV. Categorisation is pattern-based, so review low-confidence items before relying on the categories.
        </p>
      </AppCard>

      <AppCard>
        <div className="flex items-start gap-3">
          <WalletCards size={18} className="text-primary mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <AppSectionHeader
              title="Connected-bank budget"
              subtitle="Bank connections are managed in My Settings. This section stays focused on imported accounts, transactions, and budget analysis once history is ready."
            />
          </div>
        </div>

        <AppButtonRow className="mt-4">
          <AppButton size="sm" tone="outline" onPress={() => navigate('/profile')}>
            Open My Settings
          </AppButton>
        </AppButtonRow>

        {bankSyncing ? (
          <p className="text-xs text-muted-foreground mt-3">Finishing your bank sync…</p>
        ) : null}

        {data.householdShare ? (
          <div className="mt-4 rounded-xl border border-border bg-background px-4 py-3">
            <AppSectionHeader
              title="Bank view"
              subtitle="Switch between your bank feed and the linked household summary without changing what Adulting writes back."
            />
            <AppButtonRow className="mt-3">
              <AppButton
                size="sm"
                tone={bankViewMode === 'mine' ? 'primary' : 'outline'}
                onPress={() => setBankViewMode('mine')}
              >
                Mine
              </AppButton>
              <AppButton
                size="sm"
                tone={bankViewMode === 'household' ? 'primary' : 'outline'}
                onPress={() => setBankViewMode('household')}
              >
                Household
              </AppButton>
            </AppButtonRow>
            {bankViewMode === 'household' ? (
              <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
                Household view is read-only and combines imported bank history for you and {householdPeerName}. Automatic budget updates and manual budget changes still use only your own connected accounts.
              </p>
            ) : null}
          </div>
        ) : null}

        {bankEnabled ? (
          selectedBankLoading ? (
            <AppText className="mt-3" fontSize="$sm" color="$colorMuted">Loading connected accounts…</AppText>
          ) : selectedBankAccounts.length === 0 ? (
            <div className="mt-4 space-y-4">
              <AppText fontSize="$sm" color="$colorMuted">
                {bankViewMode === 'household'
                  ? `No linked household bank accounts are visible yet. Once both adults have connected accounts, Adulting will combine them here for a read-only household summary.`
                  : hasOnlyBusinessBankAccountsInMineView
                    ? 'Your connected accounts are currently marked as self-employed business, so Adulting is keeping them out of this personal money view. Change the role in My Settings if one should count as personal or shared household money instead.'
                    : 'No bank accounts connected in My Settings yet. Add one there when you want a live money summary instead of CSV only.'}
              </AppText>
              {bankViewMode === 'mine' ? (
                <AppButtonRow>
                  <AppButton size="sm" tone="outline" onPress={() => navigate('/profile')}>
                    Open My Settings
                  </AppButton>
                </AppButtonRow>
              ) : null}
              <div className="rounded-xl border border-border bg-background px-4 py-3">
                <AppSectionHeader
                  title="Budget"
                  subtitle="A calm household-budget view built from connected-bank history, with essentials, debt, savings, discretionary spend, and disposable income kept separate."
                />
                {hasOnlyBusinessBankAccountsInMineView ? (
                  <AppInlineAlert title="No personal bank history to summarise yet." tone="info">
                    Adulting is keeping your self-employed business accounts out of this personal money view. Change the account role in My Settings if one should count as personal or shared household money instead.
                  </AppInlineAlert>
                ) : (
                  <AppInlineAlert title="No bank history to summarise yet." tone="info">
                    {bankViewMode === 'household'
                      ? 'Adulting will build the shared household budget view here once both adults have imported bank transactions.'
                      : 'Open My Settings to connect a bank account, or import a reviewed statement here, and Adulting will build the monthly and annual budget view here.'}
                  </AppInlineAlert>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-border bg-background px-4 py-3">
                  <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Accounts linked</p>
                  <p className="text-lg font-bold text-foreground mt-1">{selectedBankAccounts.length}</p>
                </div>
                <div className="rounded-xl border border-border bg-background px-4 py-3">
                  <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Last sync</p>
                  <p className="text-sm font-semibold text-foreground mt-1">
                    {selectedLatestBankSync ? format(new Date(selectedLatestBankSync), 'd MMM yyyy') : 'Not yet'}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                {selectedBankAccounts.map((account) => (
                  <div key={`${account.owner_label}-${account.id}`} className="rounded-xl border border-border bg-background px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {account.name || account.institution_name || 'Connected account'}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {[
                            bankViewMode === 'household'
                              ? account.owner_label
                              : account.account_role === 'household'
                                ? getFinanceBankAccountRoleLabel(account.account_role)
                                : null,
                            account.iban || account.id,
                            account.last_synced ? `synced ${format(new Date(account.last_synced), 'd MMM')}` : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      </div>
                      <span className="text-sm font-semibold text-foreground">
                        {account.balance != null ? formatSterlingAmount(Number(account.balance), account.currency || 'GBP') : 'Balance unavailable'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {bankViewMode === 'mine' ? (
                <AppButtonRow>
                  <AppButton size="sm" tone="outline" onPress={() => navigate('/profile')}>
                    Manage bank connection in My Settings
                  </AppButton>
                </AppButtonRow>
              ) : null}
              {bankViewMode === 'mine' && mineBusinessBankAccountNote ? (
                <AppInlineAlert title="Business accounts kept separate" tone="info">
                  {mineBusinessBankAccountNote}
                </AppInlineAlert>
              ) : null}

              <div className="rounded-xl border border-border bg-background px-4 py-3">
                <AppSectionHeader
                  title={bankViewMode === 'household' ? 'Household money summary' : 'Money summary from connected accounts'}
                  subtitle={bankViewMode === 'household'
                    ? `Combined across you and ${householdPeerName}. Adulting still keeps the raw transaction list hidden by default.`
                    : 'Adulting keeps the raw transaction list hidden by default. Open it only if you want to review the imported entries yourself.'}
                />

                {bankAnalysis.transactionCount > 0 ? (
                  <div className="mt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-xl border border-border bg-card px-4 py-3">
                        <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Months covered</p>
                        <p className="text-lg font-bold text-foreground mt-1">{bankAnalysis.monthsCovered || 0}</p>
                      </div>
                      <div className="rounded-xl border border-border bg-card px-4 py-3">
                        <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Net movement</p>
                        <p className="text-lg font-bold text-foreground mt-1">
                          {formatSterlingAmount(bankAnalysis.netTotal, bankAnalysis.primaryCurrency)}
                        </p>
                      </div>
                      <div className="rounded-xl border border-border bg-card px-4 py-3">
                        <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Money in</p>
                        <p className="text-base font-semibold text-foreground mt-1">
                          {formatSterlingAmount(bankAnalysis.incomingTotal, bankAnalysis.primaryCurrency)}
                        </p>
                      </div>
                      <div className="rounded-xl border border-border bg-card px-4 py-3">
                        <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Money out</p>
                        <p className="text-base font-semibold text-foreground mt-1">
                          {formatSterlingAmount(bankAnalysis.outgoingTotal, bankAnalysis.primaryCurrency)}
                        </p>
                      </div>
                    </div>

                    {bankAnalysis.likelyPartialYear ? (
                      <AppInlineAlert title="Adulting does not have a full year of bank history yet." tone="info">
                        Adulting currently has {bankAnalysis.monthsCovered} month{bankAnalysis.monthsCovered === 1 ? '' : 's'} of imported bank history. Newer bank connections now ask for up to 12 months where the provider allows it.
                      </AppInlineAlert>
                    ) : null}

                    <div className="rounded-xl border border-border bg-card px-4 py-3">
                      <AppSectionHeader
                        title="Budget"
                        subtitle="A calm household-budget view built from connected-bank history, with essentials, debt, savings, discretionary spend, and disposable income kept separate."
                      />
                      <AppButtonRow className="mt-3">
                        <AppButton
                          size="sm"
                          tone={budgetBreakdownView === 'monthly' ? 'primary' : 'outline'}
                          onPress={() => setBudgetBreakdownView('monthly')}
                        >
                          Monthly
                        </AppButton>
                        <AppButton
                          size="sm"
                          tone={budgetBreakdownView === 'annual' ? 'primary' : 'outline'}
                          onPress={() => setBudgetBreakdownView('annual')}
                        >
                          Annual
                        </AppButton>
                      </AppButtonRow>
                      <div className="mt-4 grid grid-cols-2 gap-3">
                        {[
                          { label: 'Income', value: budgetBreakdown.disposableIncome.income },
                          { label: 'Essential household costs', value: budgetBreakdown.disposableIncome.essentialHouseholdCosts },
                          { label: 'Debt', value: budgetBreakdown.disposableIncome.committedDebtRepayments },
                          { label: 'Savings', value: budgetBreakdown.disposableIncome.savingsContributions },
                          { label: 'Discretionary', value: budgetBreakdown.disposableIncome.discretionarySpend },
                          { label: 'Disposable income', value: budgetBreakdown.disposableIncome.disposableIncome },
                          { label: 'Left after essentials', value: budgetBreakdown.disposableIncome.leftoverAfterEssentialsOnly },
                          { label: 'Left after essentials + savings', value: budgetBreakdown.disposableIncome.leftoverAfterEssentialsAndSavings },
                        ].map((item) => (
                          <div key={item.label} className="rounded-xl border border-border bg-background px-4 py-3">
                            <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">{item.label}</p>
                            <p className="text-base font-semibold text-foreground mt-1">
                              {formatSterlingAmount(
                                budgetBreakdownView === 'monthly' ? item.value.monthlyActual : item.value.annualTotal,
                                bankAnalysis.primaryCurrency,
                              )}
                            </p>
                            <p className="text-[11px] text-muted-foreground mt-1">
                              {budgetBreakdownView === 'monthly'
                                ? `Rolling 12-month average ${formatSterlingAmount(item.value.rollingMonthlyAverage, bankAnalysis.primaryCurrency)}`
                                : `Monthly equivalent ${formatSterlingAmount(item.value.annualisedMonthlyEquivalent, bankAnalysis.primaryCurrency)}`}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-xl border border-border bg-card px-4 py-3">
                      <AppSectionHeader
                        title="Creditor-ready view"
                        subtitle="The same budget, reframed around priority costs and a protected savings buffer before you talk repayment offers."
                      />
                      <div className="mt-4 grid grid-cols-2 gap-3">
                        {[
                          { label: 'Priority costs', value: bankFinanceViews.creditorView.priorityCosts.rollingMonthlyAverage },
                          { label: 'Protected savings buffer', value: bankFinanceViews.creditorView.protectedSavingsBuffer.rollingMonthlyAverage },
                          { label: 'Current debt repayments', value: bankFinanceViews.creditorView.currentDebtRepayments.rollingMonthlyAverage },
                          { label: 'Priority-cost balance', value: bankFinanceViews.creditorView.priorityCostBalance.rollingMonthlyAverage },
                          { label: 'Available for creditors', value: bankFinanceViews.creditorView.availableIncomeForCreditors.rollingMonthlyAverage },
                        ].map((item) => (
                          <div key={item.label} className="rounded-xl border border-border bg-background px-4 py-3">
                            <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">{item.label}</p>
                            <p className="text-base font-semibold text-foreground mt-1">
                              {formatSterlingAmount(item.value, bankAnalysis.primaryCurrency)}
                            </p>
                            <p className="text-[11px] text-muted-foreground mt-1">Rolling monthly view</p>
                          </div>
                        ))}
                      </div>
                      <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
                        Household shape: {bankFinanceViews.creditorView.householdLabel}. {bankFinanceViews.creditorView.guidance}
                      </p>
                      <AppButtonRow className="mt-3">
                        <AppButton
                          size="sm"
                          tone="outline"
                          onPress={() => void copyMoneyAnalysisSummary(
                            'Creditor summary',
                            buildBankCreditorSummaryText(bankFinanceViews.creditorView, bankAnalysis.primaryCurrency),
                          )}
                        >
                          Copy creditor summary
                        </AppButton>
                      </AppButtonRow>
                    </div>

                    <div className="rounded-xl border border-border bg-card px-4 py-3">
                      <AppSectionHeader
                        title="Mortgage prep view"
                        subtitle="A lighter commitments-led snapshot from the same data. Useful for gathering figures, not for predicting a lender decision."
                      />
                      <div className="mt-4 grid grid-cols-2 gap-3">
                        <div className="rounded-xl border border-border bg-background px-4 py-3">
                          <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Annual income on profile</p>
                          <p className="text-base font-semibold text-foreground mt-1">
                            {bankFinanceViews.mortgagePrepView.annualIncomeOnProfile == null
                              ? 'Not set'
                              : formatSterlingAmount(bankFinanceViews.mortgagePrepView.annualIncomeOnProfile, bankAnalysis.primaryCurrency)}
                          </p>
                        </div>
                        {[
                          { label: 'Net monthly income from bank', value: bankFinanceViews.mortgagePrepView.netMonthlyIncomeFromBank.rollingMonthlyAverage },
                          { label: 'Committed monthly outgoings', value: bankFinanceViews.mortgagePrepView.committedMonthlyOutgoings.rollingMonthlyAverage },
                          { label: 'Other core living costs', value: bankFinanceViews.mortgagePrepView.otherCoreLivingCosts.rollingMonthlyAverage },
                          { label: 'Headroom after committed', value: bankFinanceViews.mortgagePrepView.headroomAfterCommitted.rollingMonthlyAverage },
                          { label: 'Headroom after core living costs', value: bankFinanceViews.mortgagePrepView.headroomAfterCoreLivingCosts.rollingMonthlyAverage },
                        ].map((item) => (
                          <div key={item.label} className="rounded-xl border border-border bg-background px-4 py-3">
                            <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">{item.label}</p>
                            <p className="text-base font-semibold text-foreground mt-1">
                              {formatSterlingAmount(item.value, bankAnalysis.primaryCurrency)}
                            </p>
                            <p className="text-[11px] text-muted-foreground mt-1">Rolling monthly view</p>
                          </div>
                        ))}
                      </div>
                      <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
                        Household shape: {bankFinanceViews.mortgagePrepView.householdLabel}. {bankFinanceViews.mortgagePrepView.guidance}
                      </p>
                      <AppButtonRow className="mt-3">
                        <AppButton
                          size="sm"
                          tone="outline"
                          onPress={() => void copyMoneyAnalysisSummary(
                            'Mortgage prep summary',
                            buildBankMortgagePrepSummaryText(bankFinanceViews.mortgagePrepView, bankAnalysis.primaryCurrency),
                          )}
                        >
                          Copy mortgage prep summary
                        </AppButton>
                      </AppButtonRow>
                    </div>

                    {budgetBreakdown.rows.length > 0 ? (
                      <div className="rounded-xl border border-border bg-card px-4 py-3">
                        <AppSectionHeader
                          title="Category table"
                          subtitle={`${breakdownMetricLabel} with the right annual context kept visible for one-off and irregular costs.`}
                        />
                        <div className="mt-3 space-y-2">
                          {budgetBreakdown.rows.map((row) => (
                            <div key={row.categoryId} className="rounded-lg border border-border px-3 py-3">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-foreground">
                                    {row.parentLabel}: {row.categoryLabel}
                                  </p>
                                  <p className="text-[11px] text-muted-foreground mt-1">
                                    {getBreakdownRowMeta(row)}
                                  </p>
                                </div>
                                <span className="text-sm font-semibold text-foreground">
                                  {formatSterlingAmount(getBreakdownRowAmount(row), bankAnalysis.primaryCurrency)}
                                </span>
                              </div>
                              <AppButtonRow className="mt-3">
                                <AppButton
                                  size="sm"
                                  tone="outline"
                                  onPress={() => handleReviewMatchingTransactions(row.categoryId)}
                                >
                                  Review matching transactions
                                </AppButton>
                              </AppButtonRow>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {budgetBreakdown.recurringCommitments.length > 0 ? (
                      <div className="rounded-xl border border-border bg-card px-4 py-3">
                        <AppSectionHeader
                          title="Recurring commitments"
                          subtitle="Cadence-aware commitments pulled out from ordinary spend so monthly pressure is easier to understand."
                        />
                        <div className="mt-3 space-y-2">
                          {budgetBreakdown.recurringCommitments.map((item) => (
                            <div key={`${item.merchant}-${item.categoryId}`} className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">{item.merchant}</p>
                                <p className="text-[11px] text-muted-foreground">
                                  {item.categoryLabel} · {item.cadence} · average {formatSterlingAmount(item.averageAmount, bankAnalysis.primaryCurrency)}
                                </p>
                              </div>
                              <span className="text-sm font-semibold text-foreground">
                                {budgetBreakdownView === 'monthly'
                                  ? formatSterlingAmount(item.monthlyEquivalent, bankAnalysis.primaryCurrency)
                                  : formatSterlingAmount(item.totalAmount, bankAnalysis.primaryCurrency)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {financeReviewInbox ? (
                      <div className="rounded-xl border border-border bg-card px-4 py-3">
                        <AppSectionHeader
                          title={financeReviewInbox.title}
                          subtitle={financeReviewInbox.subtitle}
                        />
                        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                          {financeReviewInbox.sections.map((section) => (
                            <div key={section.key} className="rounded-xl border border-border bg-background px-4 py-3">
                              <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">{section.title}</p>
                              <p className="text-lg font-bold text-foreground mt-1">{section.count}</p>
                              <p className="text-[11px] leading-relaxed text-muted-foreground mt-2">{section.summary}</p>
                            </div>
                          ))}
                        </div>
                        {previewImportReview ? (
                          <div className="mt-4 rounded-lg border border-border bg-background px-3 py-3">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground">{previewImportReview.title}</p>
                                <p className="text-[11px] leading-relaxed text-muted-foreground mt-1">{previewImportReview.body}</p>
                              </div>
                              <AppButton size="sm" tone="outline" onPress={scrollToImportPreview}>
                                Open statement preview
                              </AppButton>
                            </div>
                          </div>
                        ) : null}
                        {budgetBreakdown.reviewQueue.length > 0 ? (
                          <div className="mt-4 space-y-3">
                            <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Uncertain bank items</p>
                            {budgetBreakdown.reviewQueue.slice(0, 6).map((item) => (
                              <div key={`review-${item.transactionId || item.patternKey || item.merchantKey}-${item.bookingDate}`} className="rounded-lg border border-border px-3 py-3">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-foreground">{item.merchantLabel}</p>
                                    <p className="text-[11px] text-muted-foreground mt-1">
                                      {describeBankBudgetReviewReason(item.reason)}
                                    </p>
                                    <p className="text-[11px] text-muted-foreground mt-1">
                                      {format(new Date(item.bookingDate), 'd MMM yyyy')} · {formatSterlingAmount(item.amount, item.currency || bankAnalysis.primaryCurrency)} · {item.suggestedCategoryLabel || 'Needs review'}
                                    </p>
                                  </div>
                                  <AppButton
                                    size="sm"
                                    tone="outline"
                                    onPress={() => openBankCorrection(buildCorrectionTargetFromReviewItem(item))}
                                  >
                                    Correct category
                                  </AppButton>
                                </div>
                              </div>
                            ))}
                            {budgetBreakdown.reviewQueue.length > 6 ? (
                              <p className="text-[11px] text-muted-foreground">
                                {budgetBreakdown.reviewQueue.length - 6} more bank item{budgetBreakdown.reviewQueue.length - 6 === 1 ? '' : 's'} still sit in this inbox.
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                        {bankViewMode === 'mine' && reviewCandidates.length > 0 ? (
                          <div className="mt-4 space-y-3">
                            <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Merchants to confirm</p>
                            {reviewCandidates.slice(0, 5).map((candidate) => (
                              <div key={candidate.merchantKey} className="rounded-lg border border-border px-3 py-3">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-foreground">{candidate.merchantLabel}</p>
                                    <p className="text-[11px] text-muted-foreground mt-1">
                                      {describeBankFeedMerchantReviewReason(candidate.reason)}
                                    </p>
                                    <p className="text-[11px] text-muted-foreground mt-1">
                                      {candidate.transactionCount} outgoing transaction{candidate.transactionCount === 1 ? '' : 's'} · average {formatSterlingAmount(candidate.averageAmount, bankAnalysis.primaryCurrency)} · latest {format(new Date(candidate.latestBookingDate), 'd MMM yyyy')}
                                    </p>
                                  </div>
                                  <AppButton
                                    size="sm"
                                    tone="outline"
                                    onPress={() => openBankCorrection(buildCorrectionTargetFromReviewCandidate(candidate))}
                                  >
                                    Confirm category
                                  </AppButton>
                                </div>
                              </div>
                            ))}
                            {reviewCandidates.length > 5 ? (
                              <p className="text-[11px] text-muted-foreground">
                                {reviewCandidates.length - 5} more merchant{reviewCandidates.length - 5 === 1 ? '' : 's'} still need a saved category.
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                        {bankViewMode === 'mine' && amazonReviewItems.length > 0 ? (
                          <div className="mt-4 space-y-3">
                            <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Amazon items to place</p>
                            {amazonReviewItems.slice(0, 5).map((item) => {
                              const itemKey = getAmazonReviewItemKey(item);
                              const selectedCategory = amazonCorrectionSelections[itemKey] || item.currentCategory || AMAZON_CATEGORY_TARGETS[0].budgetCategory;
                              return (
                                <div key={`${item.orderNumber}-${item.itemId || item.title}`} className="rounded-lg border border-border px-3 py-3">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="text-sm font-medium text-foreground">{item.title}</p>
                                      <p className="text-[11px] text-muted-foreground mt-1">
                                        {item.orderNumber} · {formatSterlingAmount(item.lineTotal, bankAnalysis.primaryCurrency)}
                                        {item.isSubscribeAndSave ? ' · Subscribe & Save' : ''}
                                      </p>
                                      <p className="text-[11px] text-muted-foreground mt-1">
                                        {item.reviewReason === 'low-confidence' ? 'Adulting does not have enough detail to trust this yet.' : 'Needs a human check.'}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="mt-3 space-y-2">
                                    <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Category</p>
                                    <AppButtonRow className="flex-wrap">
                                      {AMAZON_CATEGORY_TARGETS.map((target) => (
                                        <AppButton
                                          key={`${itemKey}-${target.label}`}
                                          size="sm"
                                          tone={selectedCategory === target.budgetCategory ? 'primary' : 'outline'}
                                          onPress={() => setAmazonCorrectionSelections((current) => ({
                                            ...current,
                                            [itemKey]: target.budgetCategory,
                                          }))}
                                        >
                                          {target.label}
                                        </AppButton>
                                      ))}
                                    </AppButtonRow>
                                    <AppButtonRow className="flex-wrap">
                                      <AppButton size="sm" tone="outline" onPress={() => void handleAmazonItemOnlyCorrection(item)} disabled={amazonSaving}>
                                        This item only
                                      </AppButton>
                                      <AppButton size="sm" tone="outline" onPress={() => void handleAmazonCorrection(item, 'item')} disabled={amazonSaving}>
                                        Always this title
                                      </AppButton>
                                      <AppButton size="sm" tone="outline" onPress={() => void handleAmazonCorrection(item, 'brand')} disabled={amazonSaving || !item.brandKey}>
                                        Usually this brand
                                      </AppButton>
                                      <AppButton size="sm" tone="primary" onPress={() => void handleAmazonCorrection(item, 'keyword')} disabled={amazonSaving}>
                                        Usually this keyword
                                      </AppButton>
                                    </AppButtonRow>
                                  </div>
                                </div>
                              );
                            })}
                            {amazonReviewItems.length > 5 ? (
                              <p className="text-[11px] text-muted-foreground">
                                {amazonReviewItems.length - 5} more Amazon item{amazonReviewItems.length - 5 === 1 ? '' : 's'} still need a human check.
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    {bankAnalysis.spendingByCategory.length > 0 ? (
                      <div className="rounded-xl border border-border bg-card px-4 py-3">
                        <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground mb-3">Where your money goes</p>
                        <div className="space-y-2">
                          {bankAnalysis.spendingByCategory.slice(0, 5).map((item) => (
                            <div key={item.category} className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground">{item.category}</p>
                                <p className="text-[11px] text-muted-foreground">
                                  {item.transactionCount} outgoing transaction{item.transactionCount === 1 ? '' : 's'}
                                </p>
                              </div>
                              <span className="text-sm font-semibold text-foreground">
                                {formatSterlingAmount(item.totalAmount, bankAnalysis.primaryCurrency)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {bankAnalysis.incomeBySource.length > 0 ? (
                      <div className="rounded-xl border border-border bg-card px-4 py-3">
                        <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground mb-3">Where your money comes from</p>
                        <div className="space-y-2">
                          {bankAnalysis.incomeBySource.map((item) => (
                            <div key={item.source} className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">{item.source}</p>
                                <p className="text-[11px] text-muted-foreground">
                                  {item.transactionCount} incoming payment{item.transactionCount === 1 ? '' : 's'}
                                </p>
                              </div>
                              <span className="text-sm font-semibold text-foreground">
                                {formatSterlingAmount(item.totalAmount, bankAnalysis.primaryCurrency)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {bankAnalysis.recurringIncomes.length > 0 ? (
                      <div className="rounded-xl border border-border bg-card px-4 py-3">
                        <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground mb-3">Likely recurring income</p>
                        <div className="space-y-2">
                          {bankAnalysis.recurringIncomes.map((item) => (
                            <div key={item.source} className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">{item.source}</p>
                                <p className="text-[11px] text-muted-foreground">
                                  {item.categoryLabel} · {getRecurringCadenceLabel(item.cadence)} · average {formatSterlingAmount(item.averageAmount, bankAnalysis.primaryCurrency)}
                                </p>
                              </div>
                              <span className="text-sm font-semibold text-foreground">
                                {formatSterlingAmount(item.totalAmount, bankAnalysis.primaryCurrency)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {bankAnalysis.recurringOutgoings.length > 0 ? (
                      <div className="rounded-xl border border-border bg-card px-4 py-3">
                        <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground mb-3">Likely recurring outgoings</p>
                        <div className="space-y-2">
                          {bankAnalysis.recurringOutgoings.map((item) => (
                            <div key={item.merchant} className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">{item.merchant}</p>
                                <p className="text-[11px] text-muted-foreground">
                                  {item.category} · {item.count} payments · average {formatSterlingAmount(item.averageAmount, bankAnalysis.primaryCurrency)}
                                </p>
                              </div>
                              <span className="text-sm font-semibold text-foreground">
                                {formatSterlingAmount(item.totalAmount, bankAnalysis.primaryCurrency)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {hasPetProfiles ? (
                      <div className="rounded-xl border border-border bg-card px-4 py-3">
                        <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground mb-3">Pet spend</p>
                        <p className="text-sm text-muted-foreground">
                          Keep pet costs visible here while paperwork stays in Documents and the wider pet view stays in My Pets.
                        </p>
                        <div className="grid grid-cols-1 gap-2 mt-3 md:grid-cols-3">
                          <div className="rounded-lg border border-border px-3 py-2">
                            <p className="text-[10px] text-muted-foreground">This month</p>
                            <p className="text-sm font-semibold text-foreground">{formatSterlingAmount(petSpendSummary.currentMonthTotal, bankAnalysis.primaryCurrency)}</p>
                          </div>
                          <div className="rounded-lg border border-border px-3 py-2">
                            <p className="text-[10px] text-muted-foreground">Saved recurring</p>
                            <p className="text-sm font-semibold text-foreground">{petSpendSummary.manualRecurringCount}</p>
                          </div>
                          <div className="rounded-lg border border-border px-3 py-2">
                            <p className="text-[10px] text-muted-foreground">Bank patterns</p>
                            <p className="text-sm font-semibold text-foreground">{petSpendSummary.connectedRecurringCount}</p>
                          </div>
                        </div>
                        {petSpendSummary.hasAnySpend ? (
                          <div className="mt-3 space-y-2">
                            {petSpendSummary.latestExpenseDescription ? (
                              <div className="rounded-lg border border-border px-3 py-2">
                                <p className="text-sm font-medium text-foreground">{petSpendSummary.latestExpenseDescription}</p>
                                <p className="text-[11px] text-muted-foreground">
                                  Latest logged pet spend{petSpendSummary.latestExpenseDate ? ` · ${format(new Date(petSpendSummary.latestExpenseDate), 'd MMM yyyy')}` : ''}
                                  {typeof petSpendSummary.latestExpenseAmount === 'number' ? ` · ${formatSterlingAmount(petSpendSummary.latestExpenseAmount, bankAnalysis.primaryCurrency)}` : ''}
                                </p>
                              </div>
                            ) : null}
                            {petBankRecurringOutgoings.slice(0, 3).map((item) => (
                              <div key={`pet-${item.merchant}`} className="rounded-lg border border-border px-3 py-2">
                                <p className="text-sm font-medium text-foreground">{item.merchant}</p>
                                <p className="text-[11px] text-muted-foreground">
                                  {item.count} payment{item.count === 1 ? '' : 's'} · average {formatSterlingAmount(item.averageAmount, bankAnalysis.primaryCurrency)}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-[11px] text-muted-foreground mt-3">
                            No pet spend is visible yet. Once pet costs are logged or recognised, Adulting will keep them visible here.
                          </p>
                        )}
                        <AppButtonRow className="mt-3">
                          <AppButton tone="outline" size="sm" onPress={() => navigate('/pets')}>
                            Open My Pets
                          </AppButton>
                          <AppButton tone="outline" size="sm" onPress={() => navigate('/documents')}>
                            Open Documents
                          </AppButton>
                        </AppButtonRow>
                      </div>
                    ) : null}

                    {bankAnalysis.annualRenewalCandidates.length > 0 ? (
                      <div className="rounded-xl border border-border bg-card px-4 py-3">
                        <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground mb-3">Likely annual renewals</p>
                        <div className="space-y-2">
                          {bankAnalysis.annualRenewalCandidates.map((item) => (
                            <div key={`${item.merchant}-${item.latestBookingDate}`} className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">{item.merchant}</p>
                                <p className="text-[11px] text-muted-foreground">
                                  {item.category} · {item.reason}
                                </p>
                              </div>
                              <span className="text-sm font-semibold text-foreground">
                                {formatSterlingAmount(item.annualAmount, bankAnalysis.primaryCurrency)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {bankAnalysis.overspendSignals.length > 0 ? (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                        <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-amber-900 mb-3">Category pressure this month</p>
                        <div className="space-y-2">
                          {bankAnalysis.overspendSignals.map((item) => (
                            <div key={item.category} className="rounded-lg border border-amber-200 bg-white/70 px-3 py-2">
                              <p className="text-sm font-medium text-foreground">{item.category}</p>
                              <p className="text-[11px] text-amber-900 mt-1">{item.message}</p>
                              <p className="text-[11px] text-muted-foreground mt-1">
                                {formatSterlingAmount(item.currentAmount, bankAnalysis.primaryCurrency)} so far vs about {formatSterlingAmount(item.averageAmount, bankAnalysis.primaryCurrency)} in a usual month.
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {bankAnalysis.yearOnYear ? (
                      <div className="rounded-xl border border-border bg-card px-4 py-3">
                        <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground mb-3">Year-on-year snapshot</p>
                        <p className="text-sm text-foreground">
                          {bankAnalysis.yearOnYear.currentMonthKey} is {bankAnalysis.yearOnYear.differenceAmount >= 0 ? 'running higher' : 'running lower'} than {bankAnalysis.yearOnYear.previousYearMonthKey} by{' '}
                          <span className="font-semibold">
                            {formatSterlingAmount(Math.abs(bankAnalysis.yearOnYear.differenceAmount), bankAnalysis.primaryCurrency)}
                          </span>
                          .
                        </p>
                        <div className="mt-3 space-y-2">
                          {bankAnalysis.yearOnYear.categoryChanges.map((item) => (
                            <div key={item.category} className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground">{item.category}</p>
                                <p className="text-[11px] text-muted-foreground">
                                  {bankAnalysis.yearOnYear.currentMonthKey}: {formatSterlingAmount(item.currentAmount, bankAnalysis.primaryCurrency)} · {bankAnalysis.yearOnYear.previousYearMonthKey}: {formatSterlingAmount(item.previousAmount, bankAnalysis.primaryCurrency)}
                                </p>
                              </div>
                              <span className={`text-sm font-semibold ${item.differenceAmount >= 0 ? 'text-amber-700' : 'text-success'}`}>
                                {item.differenceAmount >= 0 ? '+' : '-'}
                                {formatSterlingAmount(Math.abs(item.differenceAmount), bankAnalysis.primaryCurrency)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {bankAnalysis.unusualSpendFlags.length > 0 ? (
                      <div className="rounded-xl border border-border bg-card px-4 py-3">
                        <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground mb-3">Unusual spend flags</p>
                        <div className="space-y-2">
                          {bankAnalysis.unusualSpendFlags.map((item) => (
                            <div key={`${item.merchant}-${item.bookingDate}-${item.amount}`} className="rounded-lg border border-border px-3 py-2">
                              <p className="text-sm font-medium text-foreground">{item.merchant}</p>
                              <p className="text-[11px] text-muted-foreground mt-1">
                                {item.message} Logged on {format(new Date(item.bookingDate), 'd MMM yyyy')}.
                              </p>
                              <p className="text-[11px] text-muted-foreground mt-1">
                                {formatSterlingAmount(item.amount, bankAnalysis.primaryCurrency)} vs a more typical {formatSterlingAmount(item.typicalAmount, bankAnalysis.primaryCurrency)}.
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {bankAnalysis.broadMerchantNames.length > 0 ? (
                      <AppInlineAlert title="Some merchants still stay broad without deeper order data." tone="info">
                        Adulting can spot those merchants, but names like {bankAnalysis.broadMerchantNames.join(', ')} may still stay under broader categories until deeper provider ingestion is added.
                      </AppInlineAlert>
                    ) : null}

                    {bankViewMode === 'mine' && bankBudgetSignals.overBudgetCategories.length > 0 ? (
                      <AppInlineAlert title="Connected accounts already show budget pressure this month." tone="warning">
                        {bankBudgetSignals.overBudgetCategories.map((item) => (
                          <span key={item.category} className="block">
                            {item.category} is {formatSterlingAmount(item.overBy, bankAnalysis.primaryCurrency)} over the current limit from connected-account spending this month.
                          </span>
                        ))}
                      </AppInlineAlert>
                    ) : null}

                    {bankBudgetSignals.unusualSpendFlags.length > 0 ? (
                      <div className="rounded-xl border border-border bg-card px-4 py-3">
                        <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground mb-3">Unusual spend to review</p>
                        <ul className="space-y-2 text-sm text-foreground">
                          {bankBudgetSignals.unusualSpendFlags.map((item) => (
                            <li key={item} className="leading-relaxed">{item}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}

                    {bankViewMode === 'mine' ? (
                      <>
                        <div className="rounded-xl border border-border bg-card px-4 py-3">
                          <AppSectionHeader
                            title="Budget builder from connected accounts"
                            subtitle="Adulting separates likely regular bills from flexible spending, so the monthly budget only uses the rest of your bank pattern."
                          />
                          <div className="mt-4 grid grid-cols-2 gap-3">
                            <div className="rounded-xl border border-border bg-background px-4 py-3">
                              <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Regular bills tracked</p>
                              <p className="text-lg font-bold text-foreground mt-1">{budgetBuilderBillRows.length}</p>
                            </div>
                            <div className="rounded-xl border border-border bg-background px-4 py-3">
                              <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Bills per month</p>
                              <p className="text-lg font-bold text-foreground mt-1">
                                {formatSterlingAmount(budgetBuilderBillMonthlyTotal, bankAnalysis.primaryCurrency)}
                              </p>
                            </div>
                          </div>
                          {budgetBuilderBillRows.length > 0 ? (
                            <div className="mt-4 space-y-2">
                              {budgetBuilderBillRows.slice(0, 5).map((bill) => (
                                <div key={bill.billKey} className="flex items-center justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-foreground">{bill.label}</p>
                                    <p className="text-[11px] text-muted-foreground">
                                      {bill.providerName ? `${bill.providerName} · ` : ''}
                                      {formatSharedHomeBillSchedule(bill)}
                                    </p>
                                  </div>
                                  <span className="text-sm font-semibold text-foreground">
                                    {formatSharedHomeBillAmount(bill.amount, bill.frequency) || 'Amount not set'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
                              Adulting has not confirmed any regular bills from your connected history yet.
                            </p>
                          )}
                          {bankBudgetSignals.benchmark ? (
                            <p className="mt-4 text-[11px] leading-relaxed text-muted-foreground">
                              Flexible-spending comparison uses {bankBudgetSignals.benchmark.householdLabel.toLowerCase()} from {bankBudgetSignals.benchmark.sourceLabel}.
                              {bankBudgetSignals.benchmark.note ? ` ${bankBudgetSignals.benchmark.note}` : ''}
                            </p>
                          ) : null}
                          {budgetBuilderCategoryRows.length > 0 ? (
                            <div className="mt-3 space-y-2">
                              {budgetBuilderCategoryRows.slice(0, 5).map((item) => (
                                <div key={item.category} className="flex items-center justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-foreground">{item.category}</p>
                                    <p className="text-[11px] text-muted-foreground">
                                      Current {item.currentLimit == null ? 'not set' : formatSterlingAmount(item.currentLimit, bankAnalysis.primaryCurrency)} · flexible month {formatSterlingAmount(item.monthlyAverage, bankAnalysis.primaryCurrency)}
                                    </p>
                                    {item.benchmarkMonthly ? (
                                      <p className="text-[11px] text-muted-foreground">
                                        ONS benchmark {formatSterlingAmount(item.benchmarkMonthly, bankAnalysis.primaryCurrency)}
                                      </p>
                                    ) : null}
                                  </div>
                                  <span className="text-sm font-semibold text-foreground">
                                    {formatSterlingAmount(item.suggestedLimit, bankAnalysis.primaryCurrency)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : null}
                          <AppButtonRow className="mt-4">
                            <AppButton
                              size="sm"
                              tone="outline"
                              onPress={() => void applyConnectedBankBudgetSuggestion('manual')}
                              disabled={!connectedBankBudgetChanges.length}
                            >
                              {connectedBankBudgetChanges.length ? 'Update budget from connected accounts' : 'Budget already matches bank pattern'}
                            </AppButton>
                          </AppButtonRow>
                        </div>

                        {billTransferCandidates.length > 0 ? (
                          <div className="rounded-xl border border-border bg-card px-4 py-3">
                            <AppSectionHeader
                              title="Bills ready for Home"
                              subtitle="These repeating payments look like household bills. Save them into Home so the bill list carries the amount and provider."
                            />
                            {!homeProfile?.address ? (
                              <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
                                Save your home details in Home as well, otherwise the Bills section will still stay behind its empty-state setup prompt.
                              </p>
                            ) : null}
                            <div className="mt-3 space-y-3">
                              {billTransferCandidates.map((candidate) => (
                                <div key={candidate.billKey} className="rounded-lg border border-border px-3 py-3">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="text-sm font-medium text-foreground">{candidate.label}</p>
                                      <p className="text-[11px] text-muted-foreground mt-1">{candidate.summary}</p>
                                      <p className="text-[11px] text-muted-foreground mt-1">
                                        Current {formatSharedHomeBillAmount(candidate.currentBill.amount, candidate.currentBill.frequency) || 'not saved'} · suggested {formatSharedHomeBillAmount(candidate.suggestedBill.amount, candidate.suggestedBill.frequency) || 'amount not set'}
                                      </p>
                                      {candidate.suggestedBill.providerName ? (
                                        <p className="text-[11px] text-muted-foreground mt-1">
                                          Provider: {candidate.suggestedBill.providerName}
                                        </p>
                                      ) : null}
                                    </div>
                                    <AppButton
                                      size="sm"
                                      tone="outline"
                                      onPress={() => void handleApplyBillTransfer(candidate.billKey)}
                                      disabled={applyingHomeBillKey === candidate.billKey}
                                    >
                                      {applyingHomeBillKey === candidate.billKey ? 'Saving…' : 'Send to Home'}
                                    </AppButton>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        <div className="rounded-xl border border-border bg-card px-4 py-3">
                          <AppSectionHeader
                            title="Amazon orders"
                            subtitle="Paste the order email or pasted order text here. Adulting will split mixed baskets into budget categories and remember your corrections for next time."
                          />
                          <div className="mt-4 grid grid-cols-2 gap-3">
                            <div className="rounded-xl border border-border bg-background px-4 py-3">
                              <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Orders</p>
                              <p className="text-lg font-bold text-foreground mt-1">{amazonOrders.length}</p>
                            </div>
                            <div className="rounded-xl border border-border bg-background px-4 py-3">
                              <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Spend tracked</p>
                              <p className="text-lg font-bold text-foreground mt-1">{formatSterlingAmount(amazonSummary.total, bankAnalysis.primaryCurrency)}</p>
                            </div>
                            <div className="rounded-xl border border-border bg-background px-4 py-3">
                              <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Matched to bank</p>
                              <p className="text-lg font-bold text-foreground mt-1">{amazonSummary.matchedCount}</p>
                            </div>
                            <div className="rounded-xl border border-border bg-background px-4 py-3">
                              <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Needs review</p>
                              <p className="text-lg font-bold text-foreground mt-1">{amazonSummary.reviewCount}</p>
                            </div>
                          </div>
                          {amazonSummary.subscribeAndSaveCount > 0 ? (
                            <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
                              Subscribe & Save detected in {amazonSummary.subscribeAndSaveCount} order{amazonSummary.subscribeAndSaveCount === 1 ? '' : 's'}.
                            </p>
                          ) : null}
                          <Textarea
                            className="mt-4 min-h-[140px]"
                            placeholder="Paste the Amazon confirmation email, dispatch email, or order summary text here"
                            value={amazonPasteText}
                            onChange={(event) => setAmazonPasteText(event.target.value)}
                          />
                          <AppButtonRow className="mt-3">
                            <AppButton
                              size="sm"
                              tone="primary"
                              onPress={() => void handleAmazonImportPaste()}
                              disabled={amazonSaving || !amazonPasteText.trim()}
                            >
                              {amazonSaving ? 'Saving…' : 'Import Amazon order'}
                            </AppButton>
                          </AppButtonRow>
                          {amazonOrderRecords.length > 0 ? (
                            <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
                              Latest Amazon order: {amazonOrderRecords[0].orderNumber} · {format(new Date(amazonOrderRecords[0].orderDate), 'd MMM yyyy')} · {formatSterlingAmount(amazonOrderRecords[0].orderTotal, amazonOrderRecords[0].currency)}
                            </p>
                          ) : null}
                          {amazonReviewItems.length > 0 ? (
                            <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
                              {amazonReviewItems.length} low-confidence Amazon item{amazonReviewItems.length === 1 ? '' : 's'} now sit in the finance review inbox above so the import panel stays calmer.
                            </p>
                          ) : null}
                        </div>

                        <div className="rounded-xl border border-border bg-card px-4 py-3">
                          <AppSectionHeader
                            title="Automatic budget updates"
                            subtitle="Let Adulting quietly refresh the flexible-spending budget after a newer connected-bank sync lands."
                          />
                          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                            {integrationSettingsLoading
                              ? 'Checking your saved automatic-budget setting now.'
                              : autoBudgetEnabled
                                ? integrationSettings?.bank_budget_auto_last_applied_sync_at
                                  ? `On. Adulting last refreshed your budget from the bank sync recorded on ${format(new Date(integrationSettings.bank_budget_auto_last_applied_sync_at), 'd MMM yyyy, HH:mm')}.`
                                  : 'On. Adulting will apply the next connected-bank budget refresh automatically when a newer sync lands.'
                                : 'Off. Adulting will only change your budget when you press the manual update button above.'}
                          </p>
                          {latestBankSync ? (
                            <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
                              Latest connected-bank sync: {format(new Date(latestBankSync), 'd MMM yyyy, HH:mm')}.
                            </p>
                          ) : null}
                          <AppButtonRow className="mt-4">
                            <AppButton
                              size="sm"
                              tone={autoBudgetEnabled ? 'outline' : 'primary'}
                              onPress={() => void handleToggleAutoBudgetRefresh()}
                              disabled={integrationSettingsLoading || autoBudgetSaving}
                            >
                              {autoBudgetSaving
                                ? 'Saving…'
                                : autoBudgetEnabled
                                  ? 'Turn automatic updates off'
                                  : 'Turn automatic updates on'}
                            </AppButton>
                          </AppButtonRow>
                        </div>
                      </>
                    ) : (
                      <AppInlineAlert title="Household bank view is read-only." tone="info">
                        Adulting combines imported bank history for both adults here, but budget auto-refresh, category confirmation, and Home bill updates still use only your personal connected-bank feed.
                      </AppInlineAlert>
                    )}

                    <div className="flex flex-col gap-3 rounded-xl border border-border bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="space-y-1">
                        <p className="text-xs leading-relaxed text-muted-foreground">
                          Recent imported transactions stay hidden by default so Money feels useful without feeling intrusive.
                        </p>
                        {hasTransactionCategoryFilter ? (
                          <p className="text-[11px] leading-relaxed text-muted-foreground">
                            Filtering to {formatBankBudgetCategoryLabel(transactionCategoryFilterId!)}.
                          </p>
                        ) : null}
                      </div>
                      <AppButtonRow className="sm:justify-end">
                        {hasTransactionCategoryFilter ? (
                          <AppButton
                            size="sm"
                            tone="outline"
                            onPress={() => setTransactionCategoryFilterId(null)}
                          >
                            Clear filter
                          </AppButton>
                        ) : null}
                        <AppButton
                          size="sm"
                          tone="outline"
                          onPress={() => setShowRecentBankTransactions((current) => !current)}
                        >
                          {showRecentBankTransactions ? 'Hide recent transactions' : 'Show recent transactions'}
                        </AppButton>
                      </AppButtonRow>
                    </div>

                    {bankCorrectionTarget ? (
                      <div ref={correctionAnchorRef} className="rounded-xl border border-border bg-card px-4 py-3">
                        <AppSectionHeader
                          title="Correct bank category"
                          subtitle="Choose the household budget category that fits best, then tell Adulting whether to remember it once, by merchant, or by the exact pattern."
                        />
                        <div className="mt-3 rounded-lg border border-border px-3 py-3">
                          <p className="text-sm font-medium text-foreground">{bankCorrectionTarget.merchantLabel}</p>
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            {format(new Date(bankCorrectionTarget.bookingDate), 'd MMM yyyy')} · {formatSterlingAmount(bankCorrectionTarget.amount, bankCorrectionTarget.currency || bankAnalysis.primaryCurrency)}
                          </p>
                          <p className="mt-1 text-[11px] text-muted-foreground truncate">
                            {bankCorrectionTarget.rawLabel}
                          </p>
                        </div>
                        <div className="mt-3">
                          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground mb-2">Budget category</p>
                          <Select
                            value={bankCorrectionCategoryId}
                            onValueChange={(value) => setBankCorrectionCategoryId(value as BankBudgetCategoryId)}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {BANK_BREAKDOWN_CATEGORY_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <AppButtonRow className="mt-4 flex-wrap">
                          {bankCorrectionTarget.transactionId ? (
                            <AppButton
                              size="sm"
                              tone="outline"
                              onPress={() => void handleSaveBankCorrection('transaction')}
                              disabled={savingBankCorrectionScope !== null}
                            >
                              {savingBankCorrectionScope === 'transaction' ? 'Saving…' : 'This one only'}
                            </AppButton>
                          ) : null}
                          <AppButton
                            size="sm"
                            tone="outline"
                            onPress={() => void handleSaveBankCorrection('merchant')}
                            disabled={savingBankCorrectionScope !== null}
                          >
                            {savingBankCorrectionScope === 'merchant' ? 'Saving…' : 'This merchant going forward'}
                          </AppButton>
                          {bankCorrectionTarget.patternKey ? (
                            <AppButton
                              size="sm"
                              tone="primary"
                              onPress={() => void handleSaveBankCorrection('pattern')}
                              disabled={savingBankCorrectionScope !== null}
                            >
                              {savingBankCorrectionScope === 'pattern' ? 'Saving…' : 'This exact pattern going forward'}
                            </AppButton>
                          ) : null}
                          <AppButton
                            size="sm"
                            tone="outline"
                            onPress={closeBankCorrection}
                            disabled={savingBankCorrectionScope !== null}
                          >
                            Cancel
                          </AppButton>
                        </AppButtonRow>
                      </div>
                    ) : null}

                    {showRecentBankTransactions && filteredNormalisedTransactions.length > 0 ? (
                      <div ref={reviewTransactionsAnchorRef} className="rounded-xl border border-border bg-card px-4 py-3">
                        <AppSectionHeader
                          title="Recent bank transactions"
                          subtitle={hasTransactionCategoryFilter
                            ? 'Filtered to the category you selected above.'
                            : 'Shown only because you asked to see them.'}
                        />
                        <div className="space-y-2">
                          {filteredNormalisedTransactions.map((transaction) => (
                            <div key={`${transaction.transactionId || transaction.patternKey}-${transaction.bookingDate}-${transaction.amount}`} className="rounded-lg border border-border px-3 py-3">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-foreground truncate">{transaction.merchantLabel}</p>
                                  <p className="mt-1 text-[11px] text-muted-foreground">
                                    {[
                                      format(new Date(transaction.bookingDate), 'd MMM yyyy'),
                                      bankViewMode === 'household'
                                        ? selectedBankTransactions.find((entry) => entry.id === transaction.transactionId)?.owner_label || null
                                        : null,
                                      transaction.categoryLabel || 'Needs review',
                                    ].filter(Boolean).join(' · ')}
                                  </p>
                                  <p className="mt-1 text-[11px] text-muted-foreground">
                                    {transaction.needsReview && transaction.reviewReason
                                      ? describeBankBudgetReviewReason(transaction.reviewReason)
                                      : describeNormalisationKind(transaction.normalisationKind)}
                                  </p>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                  <span className={`text-sm font-semibold ${transaction.amount < 0 ? 'text-destructive' : 'text-success'}`}>
                                    {transaction.amount < 0 ? '-' : '+'}
                                    {formatSterlingAmount(Math.abs(transaction.amount), transaction.currency || bankAnalysis.primaryCurrency)}
                                  </span>
                                  {bankViewMode === 'mine' ? (
                                    <AppButton
                                      size="sm"
                                      tone="outline"
                                      onPress={() => openBankCorrection(buildCorrectionTargetFromTransaction(transaction))}
                                    >
                                      Correct
                                    </AppButton>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : showRecentBankTransactions ? (
                      <div ref={reviewTransactionsAnchorRef}>
                        <AppInlineAlert title="No matching bank transactions yet." tone="info">
                          {hasTransactionCategoryFilter
                            ? 'Adulting could not find recent imported transactions for that category in the current view.'
                            : bankViewMode === 'household'
                              ? 'Transactions will appear here after both adults have imported bank history.'
                              : 'Transactions will appear here after your connected-bank history arrives.'}
                        </AppInlineAlert>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="mt-4 space-y-4">
                    <div className="rounded-xl border border-border bg-card px-4 py-3">
                      <AppSectionHeader
                        title="Budget"
                        subtitle="A calm household-budget view built from connected-bank history, with essentials, debt, savings, discretionary spend, and disposable income kept separate."
                      />
                      <AppInlineAlert title="No bank history to summarise yet." tone="info">
                        {bankViewMode === 'household'
                          ? `Adulting will build the shared household budget view here once both adults have imported bank transactions.`
                          : 'Open My Settings to connect a bank account, or import a reviewed statement here, and Adulting will build the monthly and annual budget view here.'}
                      </AppInlineAlert>
                    </div>
                    <AppText fontSize="$sm" color="$colorMuted">
                      {bankViewMode === 'household'
                        ? `Adulting will build the shared household money summary here once both adults have imported bank transactions.`
                        : 'Adulting will build your money summary here once imported bank transactions arrive.'}
                    </AppText>
                  </div>
                )}
              </div>
            </div>
          )
        ) : null}
      </AppCard>

      {previewSummary && previewInsights && (
        <div ref={importPreviewAnchorRef}>
          <AppCard>
          <div className="flex items-center gap-3">
            <WalletCards size={18} className="text-primary" />
            <div className="flex-1">
              <AppText fontSize="$md" fontWeight="700">{previewSummary.fileName}</AppText>
              <AppText fontSize="$sm" color="$colorMuted">
                {previewImportAdapter?.label || previewImportSource?.shortLabel || 'Imported statement'} · {previewSummary.periodLabel || 'Imported statement'} · {previewSummary.transactionCount} transactions
              </AppText>
            </div>
          </div>

          {previewImportReview ? (
            <div className="rounded-xl border border-border bg-background px-4 py-3">
              <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground mb-2">
                {previewImportReview.state === 'fallback-recommended' ? 'Safer import path' : 'Import review'}
              </p>
              <p className="text-sm font-semibold text-foreground">{previewImportReview.title}</p>
              <p className="text-[11px] leading-relaxed text-muted-foreground mt-2">{previewImportReview.body}</p>
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-border bg-background px-4 py-3">
              <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Outgoings</p>
              <p className="text-lg font-bold text-foreground mt-1">£{previewSummary.outgoingsTotal.toFixed(0)}</p>
            </div>
            <div className="rounded-xl border border-border bg-background px-4 py-3">
              <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Incoming</p>
              <p className="text-lg font-bold text-success mt-1">£{previewSummary.incomingTotal.toFixed(0)}</p>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-background px-4 py-3">
            <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground mb-2">Suggested budget</p>
            <div className="grid grid-cols-2 gap-2">
              {DEFAULT_BUDGET_CATEGORIES.map((category) => (
                <div key={category} className="rounded-lg border border-border px-3 py-2">
                  <p className="text-[10px] text-muted-foreground">{category}</p>
                  <p className="text-sm font-semibold text-foreground">£{previewInsights.suggestedBudget[category]}</p>
                </div>
              ))}
            </div>
          </div>

          {previewBudgetBenchmark.rows.length > 0 ? (
            <div className="rounded-xl border border-border bg-background px-4 py-3">
              <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground mb-2">UK household comparison</p>
              <p className="text-[11px] leading-relaxed text-muted-foreground mb-3">
                Comparing this statement budget builder against {previewBudgetBenchmark.benchmark?.householdLabel.toLowerCase()} from {previewBudgetBenchmark.benchmark?.sourceLabel}.
                {previewBudgetBenchmark.benchmark?.note ? ` ${previewBudgetBenchmark.benchmark.note}` : ''}
              </p>
              <div className="space-y-2">
                {previewBudgetBenchmark.rows.map((item) => (
                  <div key={item.category} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">{item.category}</p>
                      <p className="text-[11px] text-muted-foreground">
                        Suggested {formatSterlingAmount(item.suggestedLimit)} · ONS benchmark {formatSterlingAmount(item.benchmarkMonthly)}
                      </p>
                    </div>
                    <span className={`text-sm font-semibold ${item.differenceAmount >= 0 ? 'text-amber-700' : 'text-success'}`}>
                      {item.differenceAmount >= 0 ? '+' : '-'}
                      {formatSterlingAmount(Math.abs(item.differenceAmount))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="rounded-xl border border-border bg-background px-4 py-3">
            <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground mb-2">Confidence check</p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg border border-border px-3 py-2">
                <p className="text-[10px] text-muted-foreground">High</p>
                <p className="text-sm font-semibold text-foreground">{previewInsights.confidenceCounts.high}</p>
              </div>
              <div className="rounded-lg border border-border px-3 py-2">
                <p className="text-[10px] text-muted-foreground">Medium</p>
                <p className="text-sm font-semibold text-foreground">{previewInsights.confidenceCounts.medium}</p>
              </div>
              <div className="rounded-lg border border-border px-3 py-2">
                <p className="text-[10px] text-muted-foreground">Low</p>
                <p className="text-sm font-semibold text-foreground">{previewInsights.confidenceCounts.low}</p>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">
              Low-confidence rows are usually one-off retailers, transfers, or vague bank references.
            </p>
          </div>

          {previewInsights.recurringOutgoings.length > 0 && (
            <div className="rounded-xl border border-border bg-background px-4 py-3">
              <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground mb-2">Recurring patterns spotted</p>
              <div className="space-y-2">
                {previewInsights.recurringOutgoings.slice(0, 4).map((item) => (
                  <div key={`${item.merchant}-${item.category}`} className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{item.merchant}</p>
                      <p className="text-[11px] text-muted-foreground">{item.category} · {item.count} payments</p>
                    </div>
                    <span className="text-sm font-semibold text-foreground">~£{item.averageAmount.toFixed(0)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-border bg-background px-4 py-3">
            <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground mb-2">Preview</p>
            <div className="space-y-2">
              {previewOutgoings.slice(0, 6).map((transaction, index) => (
                <div key={`${transaction.date}-${transaction.description}-${index}`} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{transaction.description}</p>
                    <p className="text-[11px] text-muted-foreground">{transaction.category} · {transaction.confidence} confidence</p>
                  </div>
                  <span className="text-sm font-semibold text-foreground">£{transaction.amount.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>

          <AppButtonRow>
            <AppButton
              tone="outline"
              size="sm"
              onPress={() => applyBudgetSuggestion(previewSummary)}
            >
              Apply suggested budget
            </AppButton>
            <AppButton
              tone="outline"
              size="sm"
              onPress={() => downloadStatementExport(previewSummary.fileName, previewTransactions)}
            >
              <Download size={14} />
              Download categorised CSV
            </AppButton>
            <AppButton
              size="sm"
              onPress={() => saveImport()}
              disabled={saveIntent !== null}
            >
              {saveIntent === 'save' ? 'Saving…' : 'Save statement import'}
            </AppButton>
            <AppButton
              tone="secondary"
              size="sm"
              onPress={() => saveImport(true)}
              disabled={saveIntent !== null}
            >
              {saveIntent === 'sync' ? 'Saving and syncing…' : 'Save and sync spend'}
            </AppButton>
          </AppButtonRow>
          </AppCard>
        </div>
      )}

      <AppCard>
        <AppSectionHeader
          title="Saved imports"
          subtitle="Keep the latest statement passes here so Adulting can work from real spending patterns."
        />
        <div className="mt-4 space-y-3">
          {importsLoading ? (
            <AppText fontSize="$sm" color="$colorMuted">Loading imports…</AppText>
          ) : imports.length === 0 ? (
            <AppEmptyState
              title="No statement imports saved yet"
              body="Upload a statement above and Adulting will keep the reviewed import here for later budget work."
            />
          ) : (
            imports.map((entry) => (
              <AppCard key={entry.id} backgroundColor="$background">
                {(() => {
                  const insights = getStatementInsights(entry.summary, entry.transactions);
                  const importSource = resolveSavedStatementImportLedgerSource(entry.summary.source, entry.file_name);
                  return (
                    <>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{entry.file_name}</p>
                    <p className="text-[11px] text-muted-foreground">{importSource.shortLabel} · {entry.period_label || 'Imported statement'} · {format(new Date(entry.created_at), 'd MMM yyyy')}</p>
                  </div>
                  <AppButtonRow>
                    <AppButton
                      tone="outline"
                      size="sm"
                      onPress={() => applyBudgetSuggestion(entry.summary)}
                    >
                      Apply budget
                    </AppButton>
                    <AppButton
                      tone="outline"
                      size="sm"
                      onPress={() => downloadStatementExport(entry.file_name, entry.transactions)}
                    >
                      Export CSV
                    </AppButton>
                  </AppButtonRow>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-muted-foreground">
                  <div>Outgoings: <span className="font-semibold text-foreground">£{entry.summary.outgoingsTotal.toFixed(0)}</span></div>
                  <div>Transactions: <span className="font-semibold text-foreground">{entry.summary.transactionCount}</span></div>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className="text-[11px] text-muted-foreground">
                    Confidence: {insights.confidenceCounts.high} high · {insights.confidenceCounts.medium} medium · {insights.confidenceCounts.low} low
                  </span>
                  {entry.expenses_synced_at ? (
                    <span className="rounded-full bg-success/10 px-2.5 py-1 text-[10px] font-medium text-success">
                      Synced to budget on {format(new Date(entry.expenses_synced_at), 'd MMM')}
                    </span>
                  ) : (
                    <AppButton
                      tone="outline"
                      size="sm"
                      onPress={() => syncImportToBudgetHistory({ importId: entry.id, transactions: entry.transactions, periodLabel: entry.period_label })}
                      disabled={syncingImportId === entry.id}
                    >
                      {syncingImportId === entry.id ? 'Syncing…' : 'Sync spend to budget'}
                    </AppButton>
                  )}
                </div>
                {insights.recurringOutgoings.length > 0 && (
                  <p className="text-[11px] text-muted-foreground mt-3">
                    Recurring: {insights.recurringOutgoings.slice(0, 2).map((item) => `${item.merchant} (${item.count})`).join(' · ')}
                  </p>
                )}
                    </>
                  );
                })()}
              </AppCard>
            ))
          )}
        </div>
        {latestImport && (
          <AppText className="mt-3" fontSize="$xs" color="$colorMuted">
            Latest import: {latestImport.file_name}. Adulting keeps the latest reviewed statement visible here so the budget flow and import history stay in one shared shell.
          </AppText>
        )}
      </AppCard>
    </div>
  );
}

function DebtsTab({
  launchDebtShortcut = false,
  onLaunchHandled,
}: {
  launchDebtShortcut?: boolean;
  onLaunchHandled?: () => void;
}) {
  const { user, data } = useApp();
  const [debts, setDebts] = useState<DebtRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [pendingShortcutClear, setPendingShortcutClear] = useState(false);
  const [creditorName, setCreditorName] = useState('');
  const [debtType, setDebtType] = useState('Credit card');
  const [balance, setBalance] = useState('');
  const [apr, setApr] = useState('');
  const [minimumPayment, setMinimumPayment] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<DebtRow['payment_status']>('current');
  const [notes, setNotes] = useState('');
  const [extraPayment, setExtraPayment] = useState('100');
  const [transferApr, setTransferApr] = useState('0');
  const [transferFeePercent, setTransferFeePercent] = useState('3');
  const [transferMonths, setTransferMonths] = useState('12');
  const [openSections, setOpenSections] = useState({
    picture: false,
    modelling: false,
    support: false,
    scripts: false,
  });

  useEffect(() => {
    if (!launchDebtShortcut) {
      return;
    }

    setOpen(true);
    setPendingShortcutClear(true);
  }, [launchDebtShortcut]);

  useEffect(() => {
    if (!pendingShortcutClear || open) {
      return;
    }

    onLaunchHandled?.();
    setPendingShortcutClear(false);
  }, [onLaunchHandled, open, pendingShortcutClear]);

  const fetchDebts = async () => {
    if (!user) return;
    setLoading(true);
    const { data: debtRows, error } = await supabase
      .from('debts' as any)
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error(error);
      setDebts([]);
    } else {
      setDebts((debtRows || []) as DebtRow[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    void fetchDebts();
  }, [user]);

  const totalBalance = debts.reduce((sum, debt) => sum + debt.balance, 0);
  const totalMinimums = debts.reduce((sum, debt) => sum + (debt.minimum_payment || 0), 0);
  const priorityDebt = [...debts].sort((left, right) => {
    const leftScore = (left.payment_status === 'missed' ? 1000 : left.payment_status === 'struggling' ? 500 : 0) + (left.apr || 0);
    const rightScore = (right.payment_status === 'missed' ? 1000 : right.payment_status === 'struggling' ? 500 : 0) + (right.apr || 0);
    return rightScore - leftScore;
  })[0] || null;
  const firstName = data.profile.firstName || '[Your name]';
  const annualIncome = data.profile.annualIncome || 0;

  const handleSaveDebt = async () => {
    if (!user || !creditorName.trim() || !balance) return;

    const { error } = await supabase.from('debts' as any).insert({
      user_id: user.id,
      creditor_name: creditorName.trim(),
      debt_type: debtType,
      balance: Number(balance),
      apr: apr ? Number(apr) : null,
      minimum_payment: minimumPayment ? Number(minimumPayment) : null,
      payment_status: paymentStatus,
      notes: notes.trim() || null,
    });

    if (error) {
      console.error(error);
      toast.error('Could not save that debt.');
      return;
    }

    void trackProductEvent({
      userId: user.id,
      eventName: 'debt_added',
      path: '/money',
      metadata: {
        debtType,
        paymentStatus,
      },
    });
    toast.success('Debt added.');
    notifyDebtDataChanged();
    setCreditorName('');
    setDebtType('Credit card');
    setBalance('');
    setApr('');
    setMinimumPayment('');
    setPaymentStatus('current');
    setNotes('');
    setOpen(false);
    await fetchDebts();
  };

  const debtActionPlan = useMemo(() => {
    if (!priorityDebt) return null;

    if (priorityDebt.payment_status === 'missed') {
      return {
        title: 'Stop the spiral today',
        summary: `Your highest-pressure account right now is ${priorityDebt.creditor_name}. Move fast on this before more fees, collections pressure, or panic avoidance build.`,
        steps: [
          'Contact the creditor today and ask for breathing room while you sort an affordable plan.',
          'Use a free debt-advice service now instead of waiting for the next letter or missed payment.',
          'Prioritise essentials first: housing, council tax, energy, food, and travel for work or care.',
        ],
      };
    }

    if (priorityDebt.payment_status === 'struggling') {
      return {
        title: 'Act before the next miss',
        summary: `You are already under pressure with ${priorityDebt.creditor_name}. The best move now is early contact while you still have more room to negotiate.`,
        steps: [
          'Ask for a temporary reduced payment or a short-term arrangement before the account tips into arrears.',
          'Use the statement tools to see what is realistically available after essentials.',
          'Prepare a one-line explanation of what has changed so you are not writing from scratch under stress.',
        ],
      };
    }

    return {
      title: 'Pressure-test the current plan',
      summary: `Your debts are marked as current, but the total balance is still £${totalBalance.toFixed(0)}. Use this section before the load becomes reactive again.`,
      steps: [
        'Check whether the highest-APR account is slowing overall progress.',
        'Model what even a small extra payment does before committing to it.',
        'Keep a ready-made script nearby so you can ask for help early if circumstances change.',
      ],
    };
  }, [priorityDebt, totalBalance]);

  const scripts = useMemo(() => {
    const creditor = priorityDebt?.creditor_name || '[Creditor]';
    return buildMoneyDebtScripts(firstName, creditor);
  }, [firstName, priorityDebt]);

  const strategyComparison = useMemo(() => compareDebtStrategies(
    debts.map((debt) => ({
      id: debt.id,
      balance: debt.balance,
      apr: debt.apr,
      minimumPayment: debt.minimum_payment,
    })),
    Number(extraPayment) || 0,
  ), [debts, extraPayment]);

  const strategySummary = useMemo(() => {
    if (debts.length === 0) return null;

    if (strategyComparison.winner === 'tie') {
      return 'With your current numbers, both strategies clear in about the same time. Choose the one you are more likely to keep following.';
    }

    const fasterStrategy = strategyComparison.winner === 'avalanche'
      ? strategyComparison.avalanche
      : strategyComparison.snowball;

    const savingsText = strategyComparison.interestDelta > 0
      ? ` and saves about £${strategyComparison.interestDelta.toFixed(0)} in interest`
      : '';

    const timingText = strategyComparison.monthsDelta > 0
      ? `${strategyComparison.winner === 'avalanche' ? 'Avalanche' : 'Snowball'} clears your debts about ${strategyComparison.monthsDelta} month${strategyComparison.monthsDelta === 1 ? '' : 's'} sooner`
      : `${strategyComparison.winner === 'avalanche' ? 'Avalanche' : 'Snowball'} keeps the overall cost lower`;

    if (fasterStrategy.monthsToClear === null) {
      return `${strategyComparison.winner === 'avalanche' ? 'Avalanche' : 'Snowball'} is the healthier shape here${savingsText}, but your current payment level is still not enough to clear everything.`;
    }

    return `${timingText}${savingsText}. This is maths, not regulated advice.`;
  }, [debts.length, strategyComparison]);

  const debtPressure = useMemo(() => assessDebtPressure(
    debts.map((debt) => ({
      debtType: debt.debt_type,
      balance: debt.balance,
      apr: debt.apr,
      minimumPayment: debt.minimum_payment,
      paymentStatus: debt.payment_status,
    })),
    annualIncome,
  ), [annualIncome, debts]);

  const rateComparisons = useMemo(() => debts
    .map((debt) => {
      const comparison = getDebtRateComparison(debt.debt_type, debt.apr);
      if (!comparison) return null;
      return { debt, comparison };
    })
    .filter((entry): entry is { debt: DebtRow; comparison: NonNullable<ReturnType<typeof getDebtRateComparison>> } => entry !== null),
  [debts]);

  const transferCandidate = useMemo(() => {
    return [...debts]
      .filter((debt) => debt.balance > 0 && (debt.debt_type.toLowerCase().includes('card') || debt.debt_type.toLowerCase().includes('credit')))
      .sort((left, right) => (right.apr || 0) - (left.apr || 0))[0] || null;
  }, [debts]);

  const transferEstimate = useMemo(() => {
    if (!transferCandidate) return null;
    return calculateBalanceTransferEstimate(
      transferCandidate.balance,
      transferCandidate.apr,
      Number(transferApr) || 0,
      Number(transferFeePercent) || 0,
      Number(transferMonths) || 0,
    );
  }, [transferApr, transferCandidate, transferFeePercent, transferMonths]);

  const transferSummary = useMemo(() => {
    if (!transferCandidate || !transferEstimate) return null;

    const months = Math.max(Number(transferMonths) || 0, 0);
    const feePercent = Number(transferFeePercent) || 0;
    const promoRate = Number(transferApr) || 0;

    if (transferEstimate.estimatedSaving > 0) {
      return `Rough carry-cost maths says moving ${transferCandidate.creditor_name} to ${promoRate}% for ${months} month${months === 1 ? '' : 's'} with a ${feePercent}% fee could cost about £${transferEstimate.estimatedSaving.toFixed(0)} less than leaving it at ${transferCandidate.apr}% APR. This ignores repayments and any revert rate after the offer ends.`;
    }

    if (transferEstimate.estimatedSaving < 0) {
      return `On this rough carry-cost check, the fee plus promo rate would cost about £${Math.abs(transferEstimate.estimatedSaving).toFixed(0)} more than leaving ${transferCandidate.creditor_name} where it is. Check the full fee, promo window, and post-offer rate before assuming a transfer helps.`;
    }

    return `On this rough carry-cost check, the transfer comes out about flat with leaving ${transferCandidate.creditor_name} where it is. The fee, promo window, and post-offer rate become the deciding details.`;
  }, [transferApr, transferCandidate, transferEstimate, transferFeePercent, transferMonths]);

  const copyMoneyAnalysisSummary = async (title: string, body: string) => {
    await navigator.clipboard.writeText(body);
    toast.success(`${title} copied.`);
  };

  const copyScript = async (title: string, body: string) => {
    await navigator.clipboard.writeText(body);
    if (user) {
      void trackProductEvent({
        userId: user.id,
        eventName: 'debt_script_copied',
        path: '/money',
        metadata: {
          title,
          creditor: priorityDebt?.creditor_name || null,
        },
      });
    }
    toast.success('Creditor script copied.');
  };

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  return (
    <div className="space-y-4">
      <SectionAccordionCard
        emoji="📉"
        title="Debt picture"
        subtitle="Start with the live balances and the immediate pressure signal."
        open={openSections.picture}
        onToggle={() => toggleSection('picture')}
      >
        <div className="space-y-4">
          <AppCard>
            <AppSectionHeader
              title="Debt overview"
              subtitle="Track the balances, see where interest bites, and keep support scripts close."
              action={(
                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogTrigger asChild>
                    <Button className="h-auto rounded-full px-4 py-1.5 text-[11px] font-medium">
                      <Plus size={14} /> Add debt
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-sm">
                    <DialogHeader>
                      <DialogTitle>Add debt</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                      <Input placeholder="Creditor name" value={creditorName} onChange={(event) => setCreditorName(event.target.value)} />
                      <Input placeholder="Debt type" value={debtType} onChange={(event) => setDebtType(event.target.value)} />
                      <Input placeholder="Balance (£)" value={balance} onChange={(event) => setBalance(event.target.value)} type="number" />
                      <Input placeholder="APR (optional)" value={apr} onChange={(event) => setApr(event.target.value)} type="number" />
                      <Input placeholder="Minimum payment (£)" value={minimumPayment} onChange={(event) => setMinimumPayment(event.target.value)} type="number" />
                      <Select value={paymentStatus} onValueChange={(value) => setPaymentStatus(value as DebtRow['payment_status'])}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="current">Current</SelectItem>
                          <SelectItem value="struggling">Struggling</SelectItem>
                          <SelectItem value="missed">Missed</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input placeholder="Notes (optional)" value={notes} onChange={(event) => setNotes(event.target.value)} />
                      <Button onClick={handleSaveDebt} className="w-full bg-primary text-primary-foreground">Save debt</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            />

            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="rounded-xl border border-border bg-background px-4 py-3">
                <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Total debt</p>
                <p className="text-lg font-bold text-foreground mt-1">£{totalBalance.toFixed(0)}</p>
              </div>
              <div className="rounded-xl border border-border bg-background px-4 py-3">
                <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Minimums</p>
                <p className="text-lg font-bold text-foreground mt-1">£{totalMinimums.toFixed(0)}/mo</p>
              </div>
            </div>
          </AppCard>

          {debtActionPlan ? (
            <AppInlineAlert title={debtActionPlan.title} tone="warning">
              {debtActionPlan.summary}
              <ul className="mt-3 list-disc pl-5 space-y-1.5 text-xs text-muted-foreground">
                {debtActionPlan.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ul>
            </AppInlineAlert>
          ) : null}
        </div>
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="🧮"
        title="Cost and strategy"
        subtitle="Use the same saved debts to see interest, payoff shape, and rough transfer maths."
        open={openSections.modelling}
        onToggle={() => toggleSection('modelling')}
      >
        <div className="space-y-4">
          <AppCard>
            <AppSectionHeader
              title="Debt-cost modelling"
              subtitle="See what interest is doing and what extra payment would actually change."
              action={(
                <Input
                  value={extraPayment}
                  onChange={(event) => setExtraPayment(event.target.value)}
                  type="number"
                  className="w-28"
                  placeholder="Extra £"
                />
              )}
            />

            <div className="mt-4 space-y-3">
              {loading ? (
                <AppText fontSize="$sm" color="$colorMuted">Loading debts…</AppText>
              ) : debts.length === 0 ? (
                <AppEmptyState
                  title="No debts added yet"
                  body="Add the first debt above and Adulting will start showing monthly cost and payoff shape here."
                />
              ) : (
                debts.map((debt) => {
                  const model = calculateDebtModel(
                    debt.balance,
                    debt.apr,
                    debt.minimum_payment || 0,
                    Number(extraPayment) || 0,
                  );

                  return (
                    <AppCard key={debt.id} backgroundColor="$background">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground">{debt.creditor_name}</p>
                          <p className="text-[11px] text-muted-foreground">{debt.debt_type} · {debt.payment_status}</p>
                        </div>
                        <span className="text-sm font-semibold text-foreground">£{debt.balance.toFixed(0)}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-3 text-[11px]">
                        <div className="rounded-lg border border-border px-3 py-2">
                          <p className="text-muted-foreground">APR</p>
                          <p className="font-semibold text-foreground">{debt.apr ? `${debt.apr}%` : '—'}</p>
                        </div>
                        <div className="rounded-lg border border-border px-3 py-2">
                          <p className="text-muted-foreground">Interest</p>
                          <p className="font-semibold text-foreground">£{model.monthlyInterest.toFixed(0)}/mo</p>
                        </div>
                        <div className="rounded-lg border border-border px-3 py-2">
                          <p className="text-muted-foreground">Clear by</p>
                          <p className="font-semibold text-foreground">{model.estimatedMonthsToClear ? `${model.estimatedMonthsToClear}m` : 'Not reducing'}</p>
                        </div>
                      </div>
                      {debt.notes ? <p className="text-xs text-muted-foreground mt-3">{debt.notes}</p> : null}
                    </AppCard>
                  );
                })
              )}
            </div>
          </AppCard>

          {debts.length > 0 ? (
            <AppCard>
              <AppSectionHeader
                title="Repayment strategy comparison"
                subtitle="Compare avalanche and snowball using your saved debts plus the same monthly debt budget."
              />

              <div className="grid grid-cols-1 gap-3 mt-4 md:grid-cols-2">
                {[
                  {
                    key: 'avalanche',
                    title: 'Avalanche',
                    subtitle: 'Pay the highest APR first',
                    result: strategyComparison.avalanche,
                  },
                  {
                    key: 'snowball',
                    title: 'Snowball',
                    subtitle: 'Pay the smallest balance first',
                    result: strategyComparison.snowball,
                  },
                ].map((strategy) => (
                  <div
                    key={strategy.key}
                    className={`rounded-xl border px-4 py-3 ${
                      strategyComparison.winner === strategy.key
                        ? 'border-primary bg-primary/5'
                        : 'border-border bg-background'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">{strategy.title}</p>
                        <p className="text-[11px] text-muted-foreground mt-1">{strategy.subtitle}</p>
                      </div>
                      {strategyComparison.winner === strategy.key ? (
                        <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-medium text-primary">
                          Better fit on cost
                        </span>
                      ) : null}
                    </div>

                    <div className="grid grid-cols-3 gap-2 mt-3 text-[11px]">
                      <div className="rounded-lg border border-border px-3 py-2">
                        <p className="text-muted-foreground">Debt budget</p>
                        <p className="font-semibold text-foreground">£{strategy.result.monthlyBudget.toFixed(0)}/mo</p>
                      </div>
                      <div className="rounded-lg border border-border px-3 py-2">
                        <p className="text-muted-foreground">Interest</p>
                        <p className="font-semibold text-foreground">£{strategy.result.totalInterestPaid.toFixed(0)}</p>
                      </div>
                      <div className="rounded-lg border border-border px-3 py-2">
                        <p className="text-muted-foreground">Clear time</p>
                        <p className="font-semibold text-foreground">
                          {strategy.result.monthsToClear === null ? 'Not clearing' : `${strategy.result.monthsToClear}m`}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {strategySummary ? (
                <AppCard backgroundColor="$background" className="mt-4">
                  <AppText fontSize="$sm" color="$colorMuted">{strategySummary}</AppText>
                </AppCard>
              ) : null}
            </AppCard>
          ) : null}

          {debts.length > 0 ? (
            <AppCard>
              <AppSectionHeader
                title="Rate check and options"
                subtitle="Latest published Bank of England averages in this build are Jan 2026 figures: credit cards 21.75% APR, personal loans 9.03%, and overdrafts 22.03%. Adulting shows comparison maths and option prompts, not regulated product advice."
              />

              <div className="mt-4 space-y-3">
                {rateComparisons.length === 0 ? (
                  <AppEmptyState
                    title="No benchmark comparison yet"
                    body="Add APRs to a credit card, overdraft, or loan to compare them with the latest Bank of England averages."
                  />
                ) : (
                  rateComparisons.map(({ debt, comparison }) => {
                    const statusMeta = getDebtRateStatusMeta(comparison.status);
                    const differenceLabel = Math.abs(comparison.differenceApr).toFixed(1);

                    return (
                      <AppCard key={debt.id} backgroundColor="$background">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-foreground">{debt.creditor_name}</p>
                            <p className="text-[11px] text-muted-foreground mt-1">
                              {comparison.benchmarkLabel} · {comparison.periodLabel}
                            </p>
                          </div>
                          <span className={`rounded-full px-2.5 py-1 text-[10px] font-medium ${statusMeta.classes}`}>
                            {statusMeta.label}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                          You are paying {comparison.currentApr.toFixed(1)}% APR against a {comparison.benchmarkApr.toFixed(2)}% average, which is {differenceLabel} percentage point{differenceLabel === '1.0' ? '' : 's'} {comparison.differenceApr >= 0 ? 'above' : 'below'} the current benchmark.
                        </p>
                      </AppCard>
                    );
                  })
                )}
              </div>

              <AppCard backgroundColor="$background" className="mt-4">
                <AppSectionHeader
                  title="Balance transfer and consolidation explainer"
                  subtitle="Use this for rough maths only. It does not account for repayments, eligibility, or the revert rate after a promo ends."
                />

                {transferCandidate ? (
                  <>
                    <p className="text-xs text-muted-foreground leading-relaxed mt-3">
                      Adulting is using {transferCandidate.creditor_name} as the current card example because it has the highest saved card APR in this account.
                    </p>
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      <Input value={transferApr} onChange={(event) => setTransferApr(event.target.value)} type="number" placeholder="Promo APR" />
                      <Input value={transferFeePercent} onChange={(event) => setTransferFeePercent(event.target.value)} type="number" placeholder="Fee %" />
                      <Input value={transferMonths} onChange={(event) => setTransferMonths(event.target.value)} type="number" placeholder="Months" />
                    </div>

                    {transferEstimate ? (
                      <div className="grid grid-cols-2 gap-2 mt-3 text-[11px] md:grid-cols-4">
                        <div className="rounded-lg border border-border px-3 py-2">
                          <p className="text-muted-foreground">Current carry cost</p>
                          <p className="font-semibold text-foreground">£{transferEstimate.currentCarryCost.toFixed(0)}</p>
                        </div>
                        <div className="rounded-lg border border-border px-3 py-2">
                          <p className="text-muted-foreground">Transfer fee</p>
                          <p className="font-semibold text-foreground">£{transferEstimate.transferFeeCost.toFixed(0)}</p>
                        </div>
                        <div className="rounded-lg border border-border px-3 py-2">
                          <p className="text-muted-foreground">Promo interest</p>
                          <p className="font-semibold text-foreground">£{transferEstimate.promoCarryCost.toFixed(0)}</p>
                        </div>
                        <div className="rounded-lg border border-border px-3 py-2">
                          <p className="text-muted-foreground">Rough saving</p>
                          <p className="font-semibold text-foreground">
                            {transferEstimate.estimatedSaving >= 0 ? '+' : '-'}£{Math.abs(transferEstimate.estimatedSaving).toFixed(0)}
                          </p>
                        </div>
                      </div>
                    ) : null}

                    {transferSummary ? (
                      <p className="text-xs text-muted-foreground leading-relaxed mt-3">{transferSummary}</p>
                    ) : null}
                  </>
                ) : (
                  <AppText className="mt-3" fontSize="$sm" color="$colorMuted">Add a credit card with an APR to run rough balance-transfer maths here.</AppText>
                )}

                <ul className="mt-4 list-disc pl-5 space-y-1 text-xs text-muted-foreground">
                  <li>0% usually still comes with a transfer fee, and the rate after the promo window matters if the balance is still there.</li>
                  <li>Consolidation only helps if the full repayable cost falls, not just because the monthly payment looks easier.</li>
                  <li>Longer terms or secured borrowing can make debt more expensive or riskier, so use regulated help before taking that route.</li>
                </ul>
              </AppCard>
            </AppCard>
          ) : null}
        </div>
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="🛟"
        title="Support and options"
        subtitle="Keep the regulated help and first moves visible before pressure becomes reactive."
        open={openSections.support}
        onToggle={() => toggleSection('support')}
      >
        <div className="space-y-4">
          <AppInlineAlert title={debtPressure?.title || 'If money is tipping into crisis'} tone="warning">
            {debtPressure?.summary || 'Use proper debt advice early. Adulting can help you prepare, but crisis support should go to trained services.'}
            {debtPressure ? (
              <ul className="mt-4 list-disc pl-5 space-y-1.5 text-xs text-muted-foreground">
                {debtPressure.reasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            ) : null}
            <div className="grid grid-cols-1 gap-2 text-xs text-muted-foreground md:grid-cols-2">
              <a href="https://www.stepchange.org/" target="_blank" rel="noreferrer" className="rounded-xl border border-border bg-background px-4 py-3 hover:bg-muted/40">
                <span className="font-medium text-foreground">StepChange</span>
                <span className="block mt-1">Free regulated debt advice and repayment-plan support.</span>
              </a>
              <a href="https://nationaldebtline.org/" target="_blank" rel="noreferrer" className="rounded-xl border border-border bg-background px-4 py-3 hover:bg-muted/40">
                <span className="font-medium text-foreground">National Debtline</span>
                <span className="block mt-1">Free debt advice. Phone: 0808 808 4000.</span>
              </a>
              <a href="https://www.moneyhelper.org.uk/en/money-troubles/dealing-with-debt" target="_blank" rel="noreferrer" className="rounded-xl border border-border bg-background px-4 py-3 hover:bg-muted/40">
                <span className="font-medium text-foreground">MoneyHelper</span>
                <span className="block mt-1">Government-backed debt guidance and option explainers.</span>
              </a>
              <a href="https://www.citizensadvice.org.uk/debt-and-money/" target="_blank" rel="noreferrer" className="rounded-xl border border-border bg-background px-4 py-3 hover:bg-muted/40">
                <span className="font-medium text-foreground">Citizens Advice</span>
                <span className="block mt-1">Debt rights, breathing-space basics, and local support routes.</span>
              </a>
            </div>
          </AppInlineAlert>
        </div>
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="✉️"
        title="Scripts and letters"
        subtitle="Open the ready-written creditor wording only when you need it."
        open={openSections.scripts}
        onToggle={() => toggleSection('scripts')}
      >
        <AppCard>
          <AppSectionHeader
            title="Creditor scripts and letters"
            subtitle="Use these as a starting point when you need breathing room, a reduced plan, or a calmer written trail."
          />
          <div className="mt-4 space-y-3">
            {scripts.map((script) => (
              <AppCard key={script.title} backgroundColor="$background">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">{script.title}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">{script.description}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => copyScript(script.title, script.body)} className="rounded-full text-[11px]">
                    <Copy size={12} />
                    Copy
                  </Button>
                </div>
                <pre className="mt-3 whitespace-pre-wrap text-xs text-muted-foreground leading-relaxed font-sans">{script.body}</pre>
              </AppCard>
            ))}
          </div>
        </AppCard>
      </SectionAccordionCard>
    </div>
  );
}

function BenefitsTab() {
  const { data, addLifeEvent } = useApp();
  const [results, setResults] = useState<ExtendedBenefitResult[] | null>(null);
  const [lastInput, setLastInput] = useState<BenefitsInput | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [lifeEventRescan, setLifeEventRescan] = useState<{
    event: string;
    summary: string;
    changedFields: string[];
  } | null>(null);
  const defaultEmploymentStatus = getDefaultBenefitsEmploymentStatus(data.profile);
  const [openSections, setOpenSections] = useState({
    scan: false,
    scripts: false,
    support: false,
  });

  const runCheck = (input: BenefitsInput) => {
    setLastInput(input);
    const benefits = calculateBenefits(input);
    setResults(benefits);
    setShowAll(false);
    setLifeEventRescan(null);
  };

  const handleLifeEvent = (event: string) => {
    addLifeEvent(event as never);
    if (lastInput) {
      const rescan = applyBenefitsLifeEvent(lastInput, event as BenefitsLifeEvent);
      setLastInput(rescan.nextInput);
      setResults(calculateBenefits(rescan.nextInput));
      setShowAll(false);
      setLifeEventRescan({
        event,
        summary: rescan.summary,
        changedFields: rescan.changedFields,
      });
      toast.success(`Benefits re-scanned for ${event}.`);
    } else {
      setLifeEventRescan({
        event,
        summary: 'Life event saved. Run the benefits check to re-scan with your latest details.',
        changedFields: [],
      });
      toast.success(`${event} saved.`);
    }
  };

  const eligibleResults = results?.filter((result) => result.status === 'eligible') || [];
  const checkResults = results?.filter((result) => result.status === 'check') || [];
  const notEligibleResults = results?.filter((result) => result.status === 'not-eligible') || [];
  const totalUnclaimed = eligibleResults.reduce((sum, result) => sum + result.annualValue, 0);
  const totalPossible = checkResults.reduce((sum, result) => sum + result.annualValue, 0);
  const firstName = data.profile.firstName || '[Your name]';

  const supportScripts = useMemo(
    () => buildMoneyBenefitsScripts(firstName),
    [firstName],
  );

  const financialRightsCards = useMemo(() => {
    const employmentSummary =
      lastInput?.employmentStatus === 'self-employed'
        ? 'Keep self-employed income drops, Maternity Allowance, and HMRC-linked money rights visible together.'
        : lastInput?.employmentStatus === 'not-working'
          ? 'Use this when income has stopped, work has ended, or a change of circumstances should trigger a re-check.'
          : lastInput?.employmentStatus === 'mixed'
            ? 'Use this when employed pay and self-employed income overlap and the situation is getting messy.'
            : 'Use this for redundancy pay, statutory pay, deductions, and income changes linked to work.'

    const cards = [
      {
        title: 'Benefit appeal rights',
        summary: 'Keep the dispute path clear when a decision looks wrong or a change has not been recorded properly.',
        bullets: [
          'mandatory reconsideration and appeal deadlines',
          'what evidence helps before you send anything',
          'how to report a change of circumstances cleanly',
        ],
        href: 'https://www.citizensadvice.org.uk/benefits/',
        linkLabel: 'Open benefit appeal help',
        external: true,
      },
      {
        title: 'Debt rights and breathing space',
        summary: 'If benefit problems are turning into arrears, move early and keep regulated help close.',
        bullets: [
          'breathing space and affordable-arrangement basics',
          'bailiff versus debt-collector rules',
          'where to go when pressure is building around essentials',
        ],
        href: 'https://www.stepchange.org/',
        linkLabel: 'Open debt rights help',
        external: true,
      },
      {
        title: 'Consumer and essential-bill rights',
        summary: 'Use this when bills, suppliers, or unfair charges are starting to push the household off balance.',
        bullets: [
          'energy, water, and council-bill affordability support',
          'consumer complaint routes and unfair-charge basics',
          'how to ask for hardship support before arrears spiral',
        ],
        href: 'https://www.citizensadvice.org.uk/consumer/',
        linkLabel: 'Open consumer rights help',
        external: true,
      },
      {
        title: 'Employment financial rights',
        summary: employmentSummary,
        bullets: [
          'redundancy pay and notice-pay basics',
          'statutory sick, maternity, paternity, and parental pay',
          'where to move next inside Adulting when work income changes',
        ],
        href: '/work?tab=rights',
        linkLabel: 'Open work rights',
        external: false,
      },
    ];

    if (lastInput?.hasHealthCondition || lastInput?.providesCare) {
      cards.push({
        title: 'Disability and carer support',
        summary: 'Keep disability and caring support visible instead of assuming it sits somewhere else in the system.',
        bullets: [
          'PIP, DLA, Attendance Allowance, and related support',
          'Carer’s Allowance, Carer’s Credit, and NI protection',
          'local-authority carer support and NHS cost help',
        ],
        href: 'https://www.citizensadvice.org.uk/benefits/sick-or-disabled-people-and-carers/',
        linkLabel: 'Open disability and carer help',
        external: true,
      });
    }

    return cards;
  }, [lastInput]);

  const copySupportScript = async (title: string, body: string) => {
    await navigator.clipboard.writeText(body);
    toast.success(`${title} copied.`);
  };

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  return (
    <div className="space-y-4">
      <SectionAccordionCard
        emoji="🧮"
        title={lastInput ? 'Benefits re-check' : 'Benefits scan'}
        subtitle="Run or revisit the scan after a life change, then keep the rescan triggers close by."
        open={openSections.scan}
        onToggle={() => toggleSection('scan')}
      >
        <div className="space-y-4">
          <AppText fontSize="$sm" color="$colorMuted">
            Find out what you could be entitled to. It only takes a minute.
          </AppText>

          {!results ? (
            <BenefitsForm
              defaultPostcode={data.profile.postcode}
              defaultIncome={data.profile.annualIncome}
              defaultChildAges={data.profile.children.map((child) => child.age)}
              defaultEmploymentStatus={defaultEmploymentStatus}
              onSubmit={runCheck}
            />
          ) : (
            <>
              {totalUnclaimed > 0 ? (
                <div className="bg-plum-light rounded-xl border border-primary/20 p-5 text-center">
                  <p className="text-xs text-muted-foreground mb-1">You could be missing out on</p>
                  <p className="text-3xl font-bold text-primary">£{totalUnclaimed.toLocaleString()}/year</p>
                  {totalPossible > 0 ? (
                    <p className="text-xs text-muted-foreground mt-1">
                      Plus up to £{totalPossible.toLocaleString()}/year worth checking
                    </p>
                  ) : null}
                </div>
              ) : null}

              {eligibleResults.length > 0 ? (
                <section className="space-y-3">
                  <h2 className="text-xs font-semibold text-success uppercase tracking-wide">
                    Likely eligible ({eligibleResults.length})
                  </h2>
                  <div className="space-y-3">
                    {eligibleResults.map((benefit) => <BenefitCard key={benefit.name} benefit={benefit} />)}
                  </div>
                </section>
              ) : null}

              {checkResults.length > 0 ? (
                <section className="space-y-3">
                  <h2 className="text-xs font-semibold text-accent uppercase tracking-wide">
                    Worth checking ({checkResults.length})
                  </h2>
                  <div className="space-y-3">
                    {checkResults.map((benefit) => <BenefitCard key={benefit.name} benefit={benefit} />)}
                  </div>
                </section>
              ) : null}

              {notEligibleResults.length > 0 ? (
                <section className="space-y-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAll(!showAll)}
                    className="h-auto px-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
                  >
                    Unlikely ({notEligibleResults.length})
                    <AppDisclosureGlyph open={showAll} />
                  </Button>
                  {showAll ? (
                    <div className="space-y-3">
                      {notEligibleResults.map((benefit) => <BenefitCard key={benefit.name} benefit={benefit} />)}
                    </div>
                  ) : null}
                </section>
              ) : null}

              <AppButton tone="outline" onPress={() => setResults(null)} width="100%">
                Update my details
              </AppButton>
            </>
          )}

          {lifeEventRescan ? (
            <AppCard>
              <AppSectionHeader
                title={`Benefits re-scan updated for ${lifeEventRescan.event}`}
                subtitle={lifeEventRescan.summary}
              />
              {lifeEventRescan.changedFields.length > 0 ? (
                <ul className="mt-3 space-y-1 text-xs text-muted-foreground list-disc pl-4">
                  {lifeEventRescan.changedFields.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </AppCard>
          ) : null}

          <AppCard>
            <AppSectionHeader
              title="Re-check triggers"
              subtitle="Save the life changes that should send you back through the scan."
            />
            <div className="mt-4">
              <LifeEventsChips activeEvents={data.lifeEvents} onTap={handleLifeEvent} />
            </div>
          </AppCard>
        </div>
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="✉️"
        title="Benefits scripts"
        subtitle="Open the calmer wording only when you need to send something."
        open={openSections.scripts}
        onToggle={() => toggleSection('scripts')}
      >
        {results ? (
          <AppCard>
            <AppSectionHeader
              title="Benefits scripts"
              subtitle="Use these when anxiety is high and you need a calm opening line rather than a perfect script from memory."
            />
            <div className="mt-4 space-y-3">
              {supportScripts.map((script) => (
                <AppCard key={script.title} backgroundColor="$background">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">{script.title}</p>
                      <p className="text-[11px] text-muted-foreground mt-1">{script.description}</p>
                    </div>
                    <AppButton tone="outline" size="sm" onPress={() => void copySupportScript(script.title, script.body)}>
                      <Copy size={12} />
                      Copy
                    </AppButton>
                  </div>
                  <pre className="mt-3 whitespace-pre-wrap text-xs text-muted-foreground leading-relaxed font-sans">{script.body}</pre>
                </AppCard>
              ))}
            </div>
          </AppCard>
        ) : (
          <AppCard>
            <AppSectionHeader
              title="Benefits scripts"
              subtitle="Run the scan first and the relevant scripts will be easier to keep in one place."
            />
          </AppCard>
        )}
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="⚖️"
        title="Rights and support"
        subtitle="Keep the backup routes visible without mixing them into the scan results."
        open={openSections.support}
        onToggle={() => toggleSection('support')}
      >
        {results ? (
          <AppCard>
            <AppSectionHeader
              title="Financial rights and support"
              subtitle="Adulting can organise the next step, but rights-heavy situations still need the right backup and the right route."
            />
            <div className="mt-4 space-y-3">
              {financialRightsCards.map((card) => (
                <AppCard key={card.title} backgroundColor="$background">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">{card.title}</p>
                      <p className="text-[11px] text-muted-foreground mt-1">{card.summary}</p>
                    </div>
                    <a
                      href={card.href}
                      target={card.external ? '_blank' : undefined}
                      rel={card.external ? 'noopener noreferrer' : undefined}
                      className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-2 text-[11px] font-medium text-foreground whitespace-nowrap active:scale-[0.98]"
                    >
                      {card.linkLabel}
                      <ExternalLink size={12} />
                    </a>
                  </div>
                  <ul className="mt-3 list-disc pl-5 space-y-1 text-[11px] text-muted-foreground">
                    {card.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                </AppCard>
              ))}
            </div>
          </AppCard>
        ) : (
          <AppCard>
            <AppSectionHeader
              title="Financial rights and support"
              subtitle="Run the scan first, then the rights and support routes will line up with the context you saved."
            />
          </AppCard>
        )}
      </SectionAccordionCard>
    </div>
  );
}

function SurvivalTab({ activation }: { activation: SurvivalActivationAssessment | null }) {
  const navigate = useNavigate();
  const { user, data } = useApp();
  const firstName = data.profile.firstName || '[Your name]';
  const savedPostcode = useMemo(
    () => data.profile.postcode.trim().toUpperCase().replace(/\s+/g, ' '),
    [data.profile.postcode],
  );
  const [localSupportLookup, setLocalSupportLookup] = useState<LocalSupportLookupResult | null>(null);
  const [loadingLocalSupportLookup, setLoadingLocalSupportLookup] = useState(false);
  const [localSupportLookupError, setLocalSupportLookupError] = useState<string | null>(null);
  const [openSections, setOpenSections] = useState({
    firstMoves: false,
    local: false,
    council: false,
    support: false,
  });

  useEffect(() => {
    let isActive = true;

    if (!savedPostcode) {
      setLocalSupportLookup(null);
      setLocalSupportLookupError(null);
      setLoadingLocalSupportLookup(false);
      return undefined;
    }

    setLoadingLocalSupportLookup(true);
    setLocalSupportLookupError(null);

    void (async () => {
      const { data: result, error } = await supabase.functions.invoke('money-local-support-lookup', {
        body: { postcode: savedPostcode },
      });

      if (!isActive) return;

      if (error) {
        setLocalSupportLookup(null);
        setLocalSupportLookupError(error.message || 'Could not look up local support right now.');
        setLoadingLocalSupportLookup(false);
        return;
      }

      if ((result as any)?.error) {
        setLocalSupportLookup(null);
        setLocalSupportLookupError(String((result as any).error));
        setLoadingLocalSupportLookup(false);
        return;
      }

      setLocalSupportLookup((result as LocalSupportLookupResult) || null);
      setLoadingLocalSupportLookup(false);
    })();

    return () => {
      isActive = false;
    };
  }, [savedPostcode]);

  const copySavedPostcode = async () => {
    if (!savedPostcode) return;

    await navigator.clipboard.writeText(savedPostcode);
    toast.success('Postcode copied.');
  };

  const directSupportActions = [
    {
      title: 'Turn2us grants and benefits tools',
      summary: 'Use this first when cash is tight and you need to check for grants, emergency funds, or a fuller benefits picture.',
      bullets: [
        'search grants and support funds',
        'double-check benefits and missing support',
        'use this before assuming there is nothing else available',
      ],
      actions: [
        {
          label: 'Open Grants Search',
          href: 'https://grants-search.turn2us.org.uk/',
        },
        {
          label: 'Open Benefits Calculator',
          href: 'https://benefits-calculator.turn2us.org.uk/',
        },
      ],
    },
    {
      title: 'Trussell Trust emergency food',
      summary: savedPostcode
        ? `Use this if food is becoming the immediate problem. Adulting has saved postcode ${savedPostcode} so you can move through the food-bank search faster.`
        : 'Use this if food is becoming the immediate problem and you need to find a local food bank or understand the voucher route.',
      bullets: [
        'find your nearest food bank',
        'check how food-bank referrals and vouchers work',
        savedPostcode
          ? `use saved postcode ${savedPostcode} when the Trussell search asks where you are`
          : 'use this when the next shop is the real emergency',
      ],
      actions: [
        {
          label: 'Find a Food Bank',
          href: localSupportLookup?.foodBankUrl || 'https://www.trussell.org.uk/emergency-food/find-a-foodbank',
        },
        {
          label: 'How Emergency Food Works',
          href: 'https://www.trusselltrust.org/get-help/emergency-food/',
        },
      ],
    },
    {
      title: 'Universal Credit emergency help',
      summary: 'Use this when a one-off shock cost has landed or your Universal Credit has been reduced and you need to check the official support routes.',
      bullets: [
        'Budgeting Advance for unexpected one-off costs',
        'advance or hardship routes if payments are reduced',
        'official UC journal, helpline, and jobcentre application routes',
      ],
      actions: [
        {
          label: 'Budgeting Advance Guide',
          href: 'https://www.gov.uk/universal-credit-advance-hardship-payment/unexpected-costs',
        },
        {
          label: 'Advance or Hardship Overview',
          href: 'https://www.gov.uk/universal-credit-advance-hardship-payment',
        },
      ],
    },
    {
      title: 'Council hardship and local crisis support',
      summary: localSupportLookup
        ? `Adulting matched ${localSupportLookup.authorityName} from postcode ${savedPostcode}. Use this when council tax, local welfare support, deposits, or crisis help may depend on your council rather than a national scheme.`
        : 'Use this when council tax, local welfare support, deposits, or other crisis help may depend on your council rather than a national scheme.',
      bullets: [
        localSupportLookup
          ? `open ${localSupportLookup.authorityName} first instead of starting from a generic search`
          : 'find the right council site fast',
        'ask about council tax hardship, local welfare, or crisis support',
        'use this before arrears grow or a deadline is missed',
      ],
      actions: [
        ...(localSupportLookup ? [{
          label: `Open ${localSupportLookup.authorityName}`,
          href: localSupportLookup.councilUrl,
        }] : []),
        {
          label: localSupportLookup ? 'GOV.UK council fallback' : 'Find Your Local Council',
          href: localSupportLookup?.councilSearchUrl || 'https://www.gov.uk/find-local-council',
        },
      ],
    },
  ];

  const councilHardshipRoutes = [
    {
      title: 'Council Tax Reduction / support',
      summary: localSupportLookup
        ? `Ask ${localSupportLookup.authorityName} about Council Tax Reduction if you are on a low income or getting benefits.`
        : 'Ask your council about Council Tax Reduction if you are on a low income or getting benefits.',
      bullets: [
        'can apply whether you rent, own, work, or are unemployed',
        'each council runs its own scheme and the award can vary',
        'use your saved postcode or council match to get to the right form faster',
      ],
      actions: [
        {
          label: 'Council Tax Reduction guide',
          href: 'https://www.gov.uk/apply-council-tax-reduction',
        },
        ...(localSupportLookup ? [{
          label: `Open ${localSupportLookup.authorityName}`,
          href: localSupportLookup.councilUrl,
        }] : []),
      ],
    },
    {
      title: 'Cost of living and local welfare help',
      summary: localSupportLookup
        ? `${localSupportLookup.authorityName} may run hardship, food, energy, or essentials support in addition to national benefits.`
        : 'Your council may run hardship, food, energy, or essentials support in addition to national benefits.',
      bullets: [
        'can include help with food, energy or water bills, and essential items',
        'some councils also run school-holiday food voucher support',
        'you do not always need to be on benefits to qualify',
      ],
      actions: [
        {
          label: 'Local cost of living help',
          href: 'https://www.gov.uk/cost-living-help-local-council',
        },
        ...(localSupportLookup ? [{
          label: `Open ${localSupportLookup.authorityName}`,
          href: localSupportLookup.councilUrl,
        }] : []),
      ],
    },
    {
      title: 'Rent shortfall or move-cost help',
      summary: 'Use this if rent is the pressure point and Housing Benefit or Universal Credit housing support is not covering enough.',
      bullets: [
        'Discretionary Housing Payments can help with rent shortfalls',
        'may also help with rent deposits or rent in advance if you need to move',
        'cannot cover council tax, so keep council tax support as a separate ask',
      ],
      actions: [
        {
          label: 'Discretionary Housing Payment',
          href: 'https://www.gov.uk/government/publications/claiming-discretionary-housing-payments/claiming-discretionary-housing-payments',
        },
        ...(localSupportLookup ? [{
          label: `Open ${localSupportLookup.authorityName}`,
          href: localSupportLookup.councilUrl,
        }] : []),
      ],
    },
    {
      title: 'Homelessness or eviction prevention',
      summary: localSupportLookup
        ? `If you may lose your home soon, ask ${localSupportLookup.authorityName} for homelessness prevention help early rather than waiting for the move-out date.`
        : 'If you may lose your home soon, ask your council for homelessness prevention help early rather than waiting for the move-out date.',
      bullets: [
        'in England, the council must help if you are homeless or could become homeless within 8 weeks',
        'support can include advice, emergency housing, or help to stay where you are',
        'use this early if eviction, relationship breakdown, abuse, or unsafe housing is involved',
      ],
      actions: [
        {
          label: 'Homelessness help guide',
          href: 'https://www.gov.uk/if-youre-homeless-at-risk-of-homelessness',
        },
        ...(localSupportLookup ? [{
          label: `Open ${localSupportLookup.authorityName}`,
          href: localSupportLookup.councilUrl,
        }] : []),
      ],
    },
  ];

  const councilHardshipScript = `Hello,

I am getting in touch because I am under financial pressure and I need to understand what support may be available from the council before things get worse.

Please let me know how to check or apply for:
- Council Tax Reduction or council tax support
- local welfare or cost of living support
- Discretionary Housing Payment if rent support may be relevant
- homelessness prevention help if my housing situation becomes unsafe

If there is a different team or form for any of these, please point me to the right route.

Thank you,
${firstName}`;

  const survivalPacks = useMemo(
    () => buildMoneySurvivalPacks(firstName),
    [firstName],
  );

  const copySurvivalScript = async (title: string, body: string) => {
    await navigator.clipboard.writeText(body);
    if (user) {
      void trackProductEvent({
        userId: user.id,
        eventName: 'survival_script_copied',
        path: '/money',
        metadata: {
          title,
        },
      });
    }
    toast.success('Survival script copied.');
  };

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  return (
    <div className="space-y-4">
      <SectionAccordionCard
        emoji="🚨"
        title="First moves"
        subtitle="Keep the immediate support routes visible before you need to think too hard."
        open={openSections.firstMoves}
        onToggle={() => toggleSection('firstMoves')}
      >
        <div className="space-y-4">
          {activation ? (
            <div className={`rounded-xl border px-4 py-4 ${getSurvivalActivationMeta(activation.level).classes}`}>
              <div className="flex items-start gap-3">
                <AlertTriangle size={18} className="mt-0.5 shrink-0 text-foreground" />
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-medium ${getSurvivalActivationMeta(activation.level).badgeClasses}`}>
                      {getSurvivalActivationMeta(activation.level).badgeLabel}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                      {activation.trigger === 'both'
                        ? 'Budget + debt signals'
                        : activation.trigger === 'budget'
                          ? 'Budget signal'
                          : 'Debt signal'}
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-foreground mt-2">{activation.title}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed mt-1">{activation.summary}</p>
                </div>
              </div>

              {activation.reasons.length > 0 ? (
                <ul className="mt-3 list-disc pl-5 space-y-1 text-xs text-muted-foreground">
                  {activation.reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          <AppCard>
            <AppSectionHeader
              title="Survival mode"
              subtitle={
                activation
                  ? 'Money signals are already nudging you here. Use this section to stabilise the next few steps fast.'
                  : 'Help when money runs tight and you need the next practical step fast.'
              }
            />
            <AppButtonRow className="mb-4">
              <a
                href="tel:08088084000"
                className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-2 text-[11px] font-medium text-primary-foreground active:scale-[0.98]"
              >
                Call National Debtline
              </a>
              <a
                href="https://nationaldebtline.org/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-2 text-[11px] font-medium text-foreground active:scale-[0.98]"
              >
                Open National Debtline
                <ExternalLink size={12} />
              </a>
            </AppButtonRow>
            <AppText fontSize="$sm" color="$colorMuted">
              These are direct support actions using official GOV.UK routes or trusted UK support services, so you do not have to remember where to start when the pressure is already high.
            </AppText>
          </AppCard>

          <AppCard>
            <AppSectionHeader title="When to use this section" />
            <AppText className="mt-2" fontSize="$sm" color="$colorMuted">
              Use Survival when bills are already slipping, you are juggling essentials, or the stress is pushing you into avoidance. The goal is not perfection. The goal is stabilising the next few steps.
            </AppText>
          </AppCard>
        </div>
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="📍"
        title="Local support"
        subtitle="Use the saved postcode route so local help is easier to find when the pressure is already high."
        open={openSections.local}
        onToggle={() => toggleSection('local')}
      >
        {savedPostcode ? (
          <AppCard>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <AppText fontSize="$md" fontWeight="700">Local support routed from your postcode</AppText>
                <AppText className="mt-1" fontSize="$sm" color="$colorMuted">
                  Adulting is using saved postcode <span className="font-medium text-foreground">{savedPostcode}</span> to shorten council and food-support routes.
                </AppText>
              </div>
              <AppButton type="button" tone="outline" size="sm" onPress={copySavedPostcode}>
                Copy postcode
                <Copy size={12} />
              </AppButton>
            </div>

            <AppCard backgroundColor="$background" className="mt-4">
              <AppText fontSize="$sm" fontWeight="600">
                {loadingLocalSupportLookup
                  ? 'Matching your council'
                  : localSupportLookup
                    ? `${localSupportLookup.authorityName} matched`
                    : 'Council lookup fallback'}
              </AppText>
              <AppText className="mt-1" fontSize="$sm" color="$colorMuted">
                {loadingLocalSupportLookup
                  ? 'Adulting is checking the GOV.UK postcode lookup so the council-support route starts closer to the right place.'
                  : localSupportLookup
                    ? `Use ${localSupportLookup.authorityName} first for council tax hardship, local welfare support, deposits, or crisis help.`
                    : localSupportLookupError || 'Adulting could not match the council right now, so use the postcode copy action and the GOV.UK fallback below.'}
              </AppText>
              <AppButtonRow className="mt-3">
                {localSupportLookup ? (
                  <a
                    href={localSupportLookup.councilUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-2 text-[11px] font-medium text-foreground active:scale-[0.98]"
                  >
                    Open {localSupportLookup.authorityName}
                    <ExternalLink size={12} />
                  </a>
                ) : null}
                <a
                  href={localSupportLookup?.foodBankUrl || 'https://www.trussell.org.uk/emergency-food/find-a-foodbank'}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-2 text-[11px] font-medium text-foreground active:scale-[0.98]"
                >
                  Find food bank with postcode
                  <ExternalLink size={12} />
                </a>
              </AppButtonRow>
            </AppCard>
          </AppCard>
        ) : (
          <AppCard>
            <AppSectionHeader
              title="Add a postcode for local support routes"
              subtitle="Adulting can only narrow council and food-support routes if your postcode is saved first."
            />
            <AppButton type="button" tone="outline" size="sm" className="mt-4" onPress={() => navigate('/profile')}>
              Add postcode in profile
            </AppButton>
          </AppCard>
        )}
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="🏛️"
        title="Council routes"
        subtitle="Keep the main council-side routes together when housing or council tax is the pressure point."
        open={openSections.council}
        onToggle={() => toggleSection('council')}
      >
        <AppCard>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <AppText fontSize="$md" fontWeight="700">What to ask your council about</AppText>
              <AppText className="mt-1" fontSize="$sm" color="$colorMuted">
                These are the main council-side routes worth checking when the pressure is housing, council tax, or basic essentials rather than only general budgeting.
              </AppText>
            </div>
            <AppButton type="button" tone="outline" size="sm" onPress={() => copySurvivalScript('Council hardship request', councilHardshipScript)}>
              Copy council script
              <Copy size={12} />
            </AppButton>
          </div>

          <div className="grid gap-3 md:grid-cols-2 mt-4">
            {councilHardshipRoutes.map((route) => (
              <AppCard key={route.title} backgroundColor="$background">
                <p className="text-sm font-medium text-foreground">{route.title}</p>
                <p className="text-xs text-muted-foreground leading-relaxed mt-1">{route.summary}</p>
                <ul className="mt-3 list-disc pl-5 space-y-1.5 text-xs text-muted-foreground">
                  {route.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
                <div className="mt-4 flex flex-wrap gap-2">
                  {route.actions.map((action) => (
                    <a
                      key={`${route.title}-${action.href}`}
                      href={action.href}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-2 text-[11px] font-medium text-foreground active:scale-[0.98]"
                    >
                      {action.label}
                      <ExternalLink size={12} />
                    </a>
                  ))}
                </div>
              </AppCard>
            ))}
          </div>
        </AppCard>
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="🧾"
        title="Direct support and scripts"
        subtitle="Keep the external help and the ready-written scripts together when you need them."
        open={openSections.support}
        onToggle={() => toggleSection('support')}
      >
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            {directSupportActions.map((resource) => (
              <AppCard key={resource.title}>
                <p className="text-sm font-semibold text-foreground">{resource.title}</p>
                <p className="text-xs text-muted-foreground leading-relaxed mt-1">{resource.summary}</p>
                <ul className="mt-4 list-disc pl-5 space-y-1.5 text-xs text-muted-foreground">
                  {resource.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
                <div className="mt-4 flex flex-wrap gap-2">
                  {resource.actions.map((action) => (
                    <a
                      key={action.href}
                      href={action.href}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-2 text-[11px] font-medium text-foreground active:scale-[0.98]"
                    >
                      {action.label}
                      <ExternalLink size={12} />
                    </a>
                  ))}
                </div>
              </AppCard>
            ))}
          </div>

          <div className="space-y-3">
            {survivalPacks.map((pack) => (
              <AppCard key={pack.title}>
                <p className="text-sm font-semibold text-foreground">{pack.title}</p>
                <p className="text-xs text-muted-foreground leading-relaxed mt-1">{pack.summary}</p>
                <ul className="mt-4 list-disc pl-5 space-y-1.5 text-xs text-muted-foreground">
                  {pack.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ul>
                <AppCard backgroundColor="$background" className="mt-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-foreground">{pack.scriptTitle}</p>
                    <AppButton tone="outline" size="sm" onPress={() => copySurvivalScript(pack.scriptTitle, pack.scriptBody)}>
                      <Copy size={12} />
                      Copy
                    </AppButton>
                  </div>
                  <pre className="mt-3 whitespace-pre-wrap text-xs text-muted-foreground leading-relaxed font-sans">{pack.scriptBody}</pre>
                </AppCard>
              </AppCard>
            ))}
          </div>
        </div>
      </SectionAccordionCard>
    </div>
  );
}
